import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import Papa from "papaparse";
import "./MBChatComponent.css";
import { trackBuilderEvent } from "../../../helpers/builderAnalytics";

import {
  ref as storageRef,
  deleteObject,
  uploadString,
} from "firebase/storage";
import { collection, deleteDoc, doc, getDocs, onSnapshot, setDoc } from "firebase/firestore";
import { onValue, ref as databaseRef, serverTimestamp as databaseServerTimestamp, set as setDatabaseValue } from "firebase/database";
import { auth, storage, database, firestore } from "../../../helpers/firebaseConfig"; // <-- ensure database is RTDB instance
import {
  cloudRunFilterLayerByPolygons,
  cloudRunBuildOsmNetwork,
  cloudRunRunParcels,
  geocodeAddress,
} from "../../../helpers/AI_functions"; // <-- your AIFunctions file
import {
  LAYER_TYPES,
  LAYER_ID_KEYS,
  REQUIRED_LINK_FIELDS,
  parseLineStringToPath,
  parsePolygonGeoJsonFeatures,
  parseHazardGeoJsonSnapshot,
  sanitizePolygonGeoJsonUpload,
  pathToLineString,
  findHeader,
  sqDist,
  pathLengthMeters,
  getLinkStartNodeId,
  getLinkEndNodeId,
  withLinkNodeIds,
  closestPointOnPath,
  splitPathAtProjection,
  makeLinkCsvRow,
  makeNodeCsvRow,
  isDraftLikeRow,
  parseLayerDoc,
  defaultLayers,
  duplicatedFileNameFromId,
  contentTypeForFileName,
  normalizeCreateLayerType,
  isCreateLayerTypeSupported,
  createLayerFileExtensionForType,
  validateCreateLayerFileName,
  validateCreateLayerHeaders,
  noValidRowsMessageForLayerType,
  sortCustomConfigVars,
  toNumericDestinationIds,
  normalizeContraflowEntries,
  normalizeBlockedRoadEntries,
  serializeBlockedRoadEntries,
  mergeCustomConfigVars,
  idsEqual,
} from "../../../helpers/modelBuilder/mbChatPureUtils";
import {
  createInitialDepartureRange,
  serializeDepartureTimeConfig,
  validateStartTimeRanges,
} from "../../../helpers/modelBuilder/departureTimeConfig";
import { fetchCsvTextFromStorage, fetchJsonFromStorage } from "../../../helpers/modelBuilder/mbStorageUtils";
import { bindingLayerKey, isResultLayerId, resultLayerDoc } from "../../../helpers/modelBuilder/entityBinding";
import { useParams } from "react-router-dom";
import OperationBlock from "../blocks/OperationBlock";
import MBMapComponent from "./MBMapComponent";
import MBLayerSelectionTab from "./MBLayerSelectionTab";
import MBConfigTab from "./MBConfigTab";
import MBHazardsTab from "./MBHazardsTab";
import MBRunTab from "./MBRunTab";
import WuiAgentChat from "./WuiAgentChat";
import MBCriticalFacilitiesPanel from "./MBCriticalFacilitiesPanel";
import AgentResultsWorkspace from "./AgentResultsWorkspace";
import ScrollJumpButton from "./ScrollJumpButton";
import MBResultsMap from "./MBResultsMap";
import MBKnowledgeTab from "./MBKnowledgeTab";
import MBSettingsTab from "./MBSettingsTab";
import MBPodcastsTab from "./MBPodcastsTab";
import useBuilderSettings from "./useBuilderSettings";
import useBuilderPodcastRuns from "./useBuilderPodcastRuns";

// Hardcoded per requirement
const PREFIX = "berkeley";
const GCS_BUCKET_URI = "gs://wui-go-97d87.appspot.com";

// Put your key here or use env var
const GOOGLE_MAPS_API_KEY = "AIzaSyASU8wb6swDIo3dNxkyjfraWijCImc3-OU";

const latestOperationMessage = (value) => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  const entries = Object.entries(value).sort(([a], [b]) => Number(a) - Number(b));
  const latest = entries[entries.length - 1]?.[1];
  if (typeof latest === "string") return latest.trim();
  return String(latest?.message || latest?.text || "").trim();
};

const operationBelongsToSimulation = (blockId, block, simulationId) => {
  const wanted = String(simulationId || "").trim();
  if (!wanted) return false;
  const explicitId =
    block?.simulationId ??
    block?.simulation_id ??
    block?.modelId ??
    block?.model_id ??
    block?.request?.simulationId ??
    block?.request?.simulation_id ??
    block?.request?.modelId ??
    block?.request?.model_id;
  if (explicitId != null) return String(explicitId) === wanted;
  return String(blockId || "").startsWith(`${wanted}_`);
};

const operationKind = (blockId, block) => {
  const hint = `${block?.kind || ""} ${block?.type || ""} ${block?.operation || ""} ${blockId || ""}`.toLowerCase();
  if (hint.includes("parcel")) return "parcel";
  if (hint.includes("critical")) return "critical";
  return "osm";
};

const isTerminalOperationMessage = (message) =>
  /^(completed|complete|done|success|succeeded|failed|error:|cancelled|canceled)/i.test(
    String(message || "").trim()
  );

const waitForOperationCompletion = (database, blockId) =>
  new Promise((resolve, reject) => {
    const messageRef = databaseRef(
      database,
      `model_builders/blocks/${blockId}/message`
    );
    let unsubscribe = () => {};
    unsubscribe = onValue(
      messageRef,
      (snapshot) => {
        const message = latestOperationMessage(snapshot.val());
        if (/^completed\.?$/i.test(message)) {
          unsubscribe();
          resolve(message);
        } else if (/^error:/i.test(message)) {
          unsubscribe();
          reject(new Error(message.replace(/^error:\s*/i, "") || "OSM network job failed."));
        }
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });




const DEFAULT_CENTER = { lat: 37.640244, lng: -122.418184 };
const DEFAULT_ZOOM = 14.8;
const CONFIG_FILE_NAME = "config.json";
const NON_DELETABLE_LAYER_IDS = new Set(defaultLayers().map((row) => String(row.id)));
const DEFAULT_START_TIMES = Object.freeze({
  minDepartureTime: 0,
  maxDepartureTime: 0,
  totalPopulationPct: 100,
  granularSurvey: false,
  startTimeRanges: [],
});

function toDepartureMinutes(raw, fallback = 0) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.trunc(n));
}

export default function MBChatComponent() {
  // -------------------------
  // Chat state
  // -------------------------
  const [chatLog, setChatLog] = useState([
    { role: "system", text: 'Type "/dt" after drawing a polygon to create a digital twin.', ts: Date.now() },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isRoadNetworkRunning, setIsRoadNetworkRunning] = useState(false);
  const [isParcelDataRunning, setIsParcelDataRunning] = useState(false);

  const { simulationId } = useParams();
  const SIMULATION_ID = simulationId;
  const simulationIdSafe = String(SIMULATION_ID || "").trim();
  const userId = String(auth.currentUser?.uid || "").trim();


  // -------------------------
  // Operation blocks state
  // -------------------------
  const [operationBlocks, setOperationBlocks] = useState([]); // [{ blockId, kind }]
  const [liveOperationBlocks, setLiveOperationBlocks] = useState([]);
  const liveOperationStatusRef = useRef(null);
  // kind: "osm" | "parcel"

  const [rightTab, setRightTab] = useState("chat");
  const { runs: podcastRuns, hydrated: podcastsHydrated } = useBuilderPodcastRuns(simulationIdSafe);
  useEffect(() => {
    if (rightTab === "podcasts" && podcastsHydrated && !podcastRuns.length) setRightTab("chat");
  }, [rightTab, podcastsHydrated, podcastRuns.length]);
  const [hasVisitedResults, setHasVisitedResults] = useState(false);
  const [returnToResultsFromAnalysis, setReturnToResultsFromAnalysis] = useState(false);
  const [dismissAnalysisKey, setDismissAnalysisKey] = useState(0);
  useEffect(() => {
    if (rightTab === "results") setHasVisitedResults(true);
    if (["chat", "edit", "config", "run", "results"].includes(rightTab)) {
      trackBuilderEvent(`builder_${rightTab}_opened`, { builder_id: simulationIdSafe, tab: rightTab });
    }
  }, [rightTab, simulationIdSafe]);
  const {
    permissions: builderPermissions,
    savingKey: settingsSavingKey,
    error: settingsError,
    changePermission: changeBuilderPermission,
  } = useBuilderSettings(simulationIdSafe);
  const [activeAgentSessionId, setActiveAgentSessionId] = useState("");
  const activeAgentSessionIdRef = useRef("");
  const [agentMapState, setAgentMapState] = useState(null);
  const [destinationMapConfigName, setDestinationMapConfigName] = useState("");
  const handledAgentMapStateRef = useRef(null);
  const [presentationSnapshot, setPresentationSnapshot] = useState({ sessionId: "", rows: [], hydrated: false });
  const agentPresentations = presentationSnapshot.sessionId === activeAgentSessionId ? presentationSnapshot.rows : [];
  const agentPresentationsHydrated = presentationSnapshot.sessionId === activeAgentSessionId && presentationSnapshot.hydrated;
  const hydratedMapSessionRef = useRef("");
  const [requestedPresentationId, setRequestedPresentationId] = useState("");
  const [presentationActivationKey, setPresentationActivationKey] = useState(0);
  const [focusedPresentationMessageId, setFocusedPresentationMessageId] = useState("");
  const [analysisJobSnapshot, setAnalysisJobSnapshot] = useState({ sessionId: "", jobs: [] });
  const agentAnalysisJobs = analysisJobSnapshot.sessionId === activeAgentSessionId ? analysisJobSnapshot.jobs : [];
  const [latestAgentQuestion, setLatestAgentQuestion] = useState({ sessionId: "", at: null });
  const [requestedAnalysisJobId, setRequestedAnalysisJobId] = useState("");
  const [requestedAnalysisJob, setRequestedAnalysisJob] = useState(null);
  const [requestedAnalysisTab, setRequestedAnalysisTab] = useState("");
  const [requestedAnalysisSessionId, setRequestedAnalysisSessionId] = useState("");
  const [closedAnalysisJobId, setClosedAnalysisJobId] = useState("");
  const [analysisActivationKey, setAnalysisActivationKey] = useState(0);
  const [analysisMapOverlay, setAnalysisMapOverlay] = useState(null);
  const [isAgentHazardVisible, setIsAgentHazardVisible] = useState(false);
  const handledAgentHazardStateRef = useRef(null);
  useEffect(() => {
    if (rightTab !== "edit" && rightTab !== "config") return;
    setAnalysisMapOverlay(null);
    setIsAgentHazardVisible(false);
  }, [rightTab]);
  const [workingPanePercent, setWorkingPanePercent] = useState(50);
  const [toolbarPortal, setToolbarPortal] = useState(null);
  const builderLayoutRef = useRef(null);
  const workingScrollRef = useRef(null);
  const [showBackToChat, setShowBackToChat] = useState(false);
  const dividerDragRef = useRef(false);

  useEffect(() => {
    setToolbarPortal(document.getElementById("editor-toolbar-root"));
  }, []);
  const [activeLayer, setActiveLayer] = useState("links");
  const [gisLayers, setGisLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState("");
  const [activeLayerByType, setActiveLayerByType] = useState({
    links: null,
    nodes: null,
    parcels: null,
    other: null,
    polygon: null,
  });

  // -------------------------
  // Map & polygon state
  // -------------------------
  const [isSelectingPolygon, setIsSelectingPolygon] = useState(false);
  const [draftPolygon, setDraftPolygon] = useState([]); // [{lat,lng},...]
  const [savedPolygon, setSavedPolygon] = useState(null); // [{lat,lng},...]

  const [mapSearch, setMapSearch] = useState("");
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const onMapViewportChange = useCallback(({ center, zoom }) => {
    setMapCenter((previous) =>
      Math.abs(previous.lat - center.lat) < 1e-7 && Math.abs(previous.lng - center.lng) < 1e-7
        ? previous : center
    );
    setMapZoom((previous) => previous === zoom ? previous : zoom);
  }, []);
  const [mapFitSelection, setMapFitSelection] = useState(null);

  // -------------------------
  // Inputs data (from Storage)
  // -------------------------
  const [links, setLinks] = useState([]);   // [{link_id, geometry, path, ...row}]
  const [nodes, setNodes] = useState([]);   // [{node_id, lat, lng, ...}]
  const [parcels, setParcels] = useState([]); // [{parcel_id, lat, lng, pop, ...}]
  const [otherRows, setOtherRows] = useState([]); // [{_row_id, ...csv row}]
  const [polygonRows, setPolygonRows] = useState([]); // [{polygon_id, feature_name, geometry_type, paths}]

  const [selectedLinkIds, setSelectedLinkIds] = useState(() => new Set());
  const [selectedNodeIds, setSelectedNodeIds] = useState(() => new Set());
  const [selectedParcelIds, setSelectedParcelIds] = useState(() => new Set());
  const [selectedOtherIds, setSelectedOtherIds] = useState(() => new Set());
  const [selectedPolygonIds, setSelectedPolygonIds] = useState(() => new Set());
  const [resultSelectedLinkIds, setResultSelectedLinkIds] = useState(() => new Set());
  const [resultSelectedNodeIds, setResultSelectedNodeIds] = useState(() => new Set());
  const [resultMapContext, setResultMapContext] = useState(null);
  const [selectedPolygonColor, setSelectedPolygonColor] = useState("#facc15");
  const [mapFocusTarget, setMapFocusTarget] = useState(null);
  const [customConfigVars, setCustomConfigVars] = useState([]);
  const customConfigVarsRef = useRef(customConfigVars);
  customConfigVarsRef.current = customConfigVars;
  const [activeConfigVarName, setActiveConfigVarName] = useState("destinations");
  const [configNodes, setConfigNodes] = useState([]);
  const [destinationVisibilityLinks, setDestinationVisibilityLinks] = useState([]);
  const [contraflowLinks, setContraflowLinks] = useState([]);
  const [hazardLayers, setHazardLayers] = useState([]);
  const [activeHazardLayerId, setActiveHazardLayerId] = useState("");
  const [hazardSnapshots, setHazardSnapshots] = useState([]);
  const [activeHazardSnapshotId, setActiveHazardSnapshotId] = useState("");
  const [activeHazardRows, setActiveHazardRows] = useState([]);
  const [activeHazardGeoJson, setActiveHazardGeoJson] = useState(null);
  const [selectedHazardFeatureIndex, setSelectedHazardFeatureIndex] = useState("");
  const [hazardState, setHazardState] = useState({ isBusy: false, error: "", success: "" });
  const [isEditingDestinations, setIsEditingDestinations] = useState(false);
  const [isEditingStartTimes, setIsEditingStartTimes] = useState(false);
  const [isEditingHazardConfig, setIsEditingHazardConfig] = useState(false);
  const [isEditingContraflow, setIsEditingContraflow] = useState(false);
  const [isEditingBlockedRoad, setIsEditingBlockedRoad] = useState(false);
  const [blockedRoadTwoSided, setBlockedRoadTwoSided] = useState(true);
  const [hazardConfigNameDraft, setHazardConfigNameDraft] = useState("");
  const [editingConfigVarName, setEditingConfigVarName] = useState("");
  const configEditSnapshotRef = useRef(null);
  const configEditingRef = useRef(false);
  const latestCustomConfigVarsRef = useRef(null);
  const customConfigMetadataRef = useRef({});
  const customConfigStorageRef = useRef(null);
  const [configSaveState, setConfigSaveState] = useState({
    isSaving: false,
    error: "",
    success: "",
  });
  const [layerSaveState, setLayerSaveState] = useState({
    isSaving: false,
    error: "",
    success: "",
  });
  
  const configWasSavingRef = useRef(false);
  useEffect(() => {
    if (layerSaveState.error) trackBuilderEvent("builder_layer_save_failed", { builder_id: simulationIdSafe, tab: "edit", error_code: "operation_failed" });
    if (!layerSaveState.success) return;
    const label = layerSaveState.success.toLowerCase();
    const action = label.startsWith("duplicated") ? "builder_layer_duplicated"
      : label.startsWith("deleted layer") ? "builder_layer_deleted"
      : label.startsWith("downloaded") ? "builder_layer_downloaded"
      : label.startsWith("created") ? "builder_layer_created" : "builder_layer_saved";
    trackBuilderEvent(action, { builder_id: simulationIdSafe, tab: "edit" });
  }, [layerSaveState.error, layerSaveState.success, simulationIdSafe]);
  useEffect(() => {
    if (configSaveState.error) trackBuilderEvent(configWasSavingRef.current ? "builder_config_save_failed" : "builder_config_validation_failed", { builder_id: simulationIdSafe, tab: "config", error_code: "operation_failed" });
    configWasSavingRef.current = configSaveState.isSaving;
    if (!configSaveState.success) return;
    const label = configSaveState.success.toLowerCase();
    const action = label.startsWith("created") ? "builder_config_created"
      : label.startsWith("deleted config") ? "builder_config_deleted" : "builder_config_saved";
    trackBuilderEvent(action, { builder_id: simulationIdSafe, tab: "config" });
  }, [configSaveState.error, configSaveState.success, configSaveState.isSaving, simulationIdSafe]);

  const [isFilteringLayer, setIsFilteringLayer] = useState(false);
  const [selectedFilterPolygonLayerIds, setSelectedFilterPolygonLayerIds] = useState([]);
  const [filterPolygonOverlayRows, setFilterPolygonOverlayRows] = useState([]);
  const [addLinkDraftMode, setAddLinkDraftMode] = useState("idle"); // idle | drawing | node_layer | ready
  const [addLinkDraftPath, setAddLinkDraftPath] = useState([]);
  const [addLinkDraftInfo, setAddLinkDraftInfo] = useState(null);
  const [addLinkSelectedNodeLayerId, setAddLinkSelectedNodeLayerId] = useState("");
  const [addLinkNodeLayerLoading, setAddLinkNodeLayerLoading] = useState(false);
  const addLinkDraftPathRef = useRef([]);
  const [draftTopologyStep, setDraftTopologyStep] = useState("start"); // start | end | done
  const [draftTopologyChoices, setDraftTopologyChoices] = useState({
    start: "leave_as_is",
    end: "leave_as_is",
  });
  const [draftNearestLinkOptions, setDraftNearestLinkOptions] = useState({ start: [], end: [] });
  const [draftNearestLinkSelected, setDraftNearestLinkSelected] = useState({ start: [], end: [] });
  const activeLayerIdRef = useRef("");
  const activeLayerRef = useRef("links");
  const filterPolygonLoadSeqRef = useRef(0);
  const layerLoadSeqRef = useRef(0);
  const latestLayerLoadTokenRef = useRef({
    __selected: 0,
    links: 0,
    nodes: 0,
    parcels: 0,
    other: 0,
    polygon: 0,
  });

  // Networks belonging to a stored result rather than to an authored layer. They are resolvable and
  // selectable like a layer, but deliberately absent from gisLayers so they never appear in the
  // layer list or as a run input.
  const [resultLayers, setResultLayers] = useState([]);

  const layerById = useMemo(() => {
    const out = new Map();
    (gisLayers || []).forEach((layer) => out.set(String(layer.id), layer));
    (resultLayers || []).forEach((layer) => out.set(String(layer.id), layer));
    return out;
  }, [gisLayers, resultLayers]);
  const layerByIdRef = useRef(layerById);
  layerByIdRef.current = layerById;

  const selectedLayer = useMemo(
    () => layerById.get(String(activeLayerId || "")) || null,
    [layerById, activeLayerId]
  );
  const canDeleteSelectedLayer = useMemo(() => {
    const id = String(selectedLayer?.id || "").trim();
    if (!id) return false;
    // A result's network is a view of a finished run, not a layer the user owns.
    if (isResultLayerId(id)) return false;
    return !NON_DELETABLE_LAYER_IDS.has(id);
  }, [selectedLayer?.id]);

  const nodeLayerOptions = useMemo(
    () => (gisLayers || []).filter((layer) => layer.type === "nodes"),
    [gisLayers]
  );
  const linksLayerOptions = useMemo(
    () => (gisLayers || []).filter((layer) => layer.type === "links"),
    [gisLayers]
  );
  const polygonLayerOptions = useMemo(
    () => (gisLayers || []).filter((layer) => layer.type === "polygon"),
    [gisLayers]
  );

  useEffect(() => {
    const wanted = String(addLinkSelectedNodeLayerId || "").trim();
    if (wanted && nodeLayerOptions.some((row) => String(row.id) === wanted)) return;
    setAddLinkSelectedNodeLayerId(String(nodeLayerOptions[0]?.id || ""));
  }, [nodeLayerOptions, addLinkSelectedNodeLayerId]);

  useEffect(() => {
    const validIds = new Set((polygonLayerOptions || []).map((row) => String(row.id)));
    setSelectedFilterPolygonLayerIds((prev) =>
      (prev || []).filter((id) => validIds.has(String(id)))
    );
  }, [polygonLayerOptions]);

  const availableConfigVars = useMemo(() =>
    customConfigVars.filter((row) => builderPermissions.hazardLayer || row.type !== "hazard_layer"),
    [customConfigVars, builderPermissions.hazardLayer]
  );
  const activeConfigVar = useMemo(() => {
    if (!availableConfigVars.length) return null;
    const wanted = String(activeConfigVarName || "").trim();
    return availableConfigVars.find((row) => row.name === wanted) || availableConfigVars[0] || null;
  }, [availableConfigVars, activeConfigVarName]);
  const activeConfigNodeLayerId = useMemo(() => {
    if (activeConfigVar?.type !== "destination") return "";
    const configured = String(activeConfigVar?.nodeLayerId || "").trim();
    if (configured && nodeLayerOptions.some((row) => String(row.id) === configured)) {
      return configured;
    }
    return String(nodeLayerOptions[0]?.id || "");
  }, [activeConfigVar, nodeLayerOptions]);
  const effectiveConfigContext = useMemo(() => {
    const activeName = String(activeConfigVarName || "").trim();
    const editName = String(editingConfigVarName || "").trim();
    const preferredName = isEditingDestinations || isEditingStartTimes || isEditingHazardConfig || isEditingContraflow || isEditingBlockedRoad
      ? editName
      : activeName;
    const fallbackVar =
      availableConfigVars.find((row) => row.name === activeName) ||
      availableConfigVars[0] ||
      null;
    const row =
      (preferredName && availableConfigVars.find((item) => item.name === preferredName)) ||
      fallbackVar;
    const rowType = String(row?.type || "destination");
    const configuredLayerId = rowType === "destination" ? String(row?.nodeLayerId || "").trim() : "";
    const hasConfiguredLayer =
      !!configuredLayerId &&
      nodeLayerOptions.some((item) => String(item.id) === configuredLayerId);
    const nodeLayerId = rowType === "destination"
      ? (hasConfiguredLayer
      ? configuredLayerId
      : String(nodeLayerOptions[0]?.id || ""))
      : "";
    const configuredVisibilityLinksLayerId =
      rowType === "destination" ? String(row?.visibilityLinksLayerId || "").trim() : "";
    const visibilityLinksLayerId =
      configuredVisibilityLinksLayerId &&
      linksLayerOptions.some((item) => String(item.id) === configuredVisibilityLinksLayerId)
        ? configuredVisibilityLinksLayerId
        : "";
    const minDepartureTime = toDepartureMinutes(row?.minDepartureTime, DEFAULT_START_TIMES.minDepartureTime);
    const maxDepartureTime = Math.max(
      minDepartureTime,
      toDepartureMinutes(row?.maxDepartureTime, minDepartureTime)
    );
    return {
      row,
      type: rowType,
      name: String(row?.name || "").trim(),
      nodeLayerId,
      visibilityLinksLayerId,
      destinationIds: rowType === "destination" ? new Set(row?.destinationIds || []) : new Set(),
      minDepartureTime,
      maxDepartureTime,
      totalPopulationPct: 100,
      granularSurvey: Boolean(row?.granularSurvey),
      startTimeRanges: Array.isArray(row?.startTimeRanges) ? row.startTimeRanges : [],
      linksLayerId: rowType === "contraflow" || rowType === "blocked_road" ? String(row?.linksLayerId || "") : "",
      blockedRoadEntries: rowType === "blocked_road" ? normalizeBlockedRoadEntries(row?.blockedRoadEntries) : [],
      destinationConfigName: rowType === "contraflow" ? String(row?.destinationConfigName || "") : "",
      contraflowEntries: rowType === "contraflow" ? normalizeContraflowEntries(row?.contraflowEntries || row?.linkIds) : [],
      linkIds: rowType === "contraflow"
        ? new Set(normalizeContraflowEntries(row?.contraflowEntries || row?.linkIds).map((entry) => entry.linkId))
        : rowType === "blocked_road" ? new Set(normalizeBlockedRoadEntries(row?.blockedRoadEntries).map((entry) => entry.linkId)) : new Set(),
    };
  }, [
    availableConfigVars,
    activeConfigVarName,
    editingConfigVarName,
    isEditingDestinations,
    isEditingStartTimes,
    isEditingHazardConfig,
    isEditingContraflow,
    isEditingBlockedRoad,
    nodeLayerOptions,
    linksLayerOptions,
  ]);

  useEffect(() => {
    if (addLinkDraftMode !== "ready") return;
    setDraftTopologyStep("start");
    setDraftTopologyChoices({ start: "leave_as_is", end: "leave_as_is" });
    setDraftNearestLinkOptions({ start: [], end: [] });
    setDraftNearestLinkSelected({ start: [], end: [] });
  }, [addLinkDraftMode, addLinkDraftInfo?.linkId]);

  useEffect(() => {
    if (!customConfigVars.length) return;
    if (customConfigVars.some((row) => row.name === activeConfigVarName)) return;
    setActiveConfigVarName(customConfigVars[0].name);
  }, [customConfigVars, activeConfigVarName]);

  useEffect(() => {
    activeLayerIdRef.current = String(activeLayerId || "");
  }, [activeLayerId]);

  useEffect(() => {
    activeLayerRef.current = String(activeLayer || "links");
  }, [activeLayer]);

  useEffect(() => {
    if (!isEditingDestinations && !isEditingStartTimes && !isEditingHazardConfig) return;
    const wanted = String(editingConfigVarName || "").trim();
    if (!wanted) return;
    if (customConfigVars.some((row) => row.name === wanted)) return;
    setIsEditingDestinations(false);
    setIsEditingStartTimes(false);
    setIsEditingHazardConfig(false);
    setEditingConfigVarName("");
    configEditSnapshotRef.current = null;
  }, [customConfigVars, isEditingDestinations, isEditingStartTimes, isEditingHazardConfig, editingConfigVarName]);

  useEffect(() => {
    const fallbackNodeLayerId = String(nodeLayerOptions[0]?.id || "");
    if (!fallbackNodeLayerId) return;
    setCustomConfigVars((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.type !== "destination") return row;
        const currentId = String(row.nodeLayerId || "");
        const isValid = currentId && nodeLayerOptions.some((opt) => String(opt.id) === currentId);
        if (isValid) return row;
        changed = true;
        return { ...row, nodeLayerId: fallbackNodeLayerId };
      });
      return changed ? next : prev;
    });
  }, [nodeLayerOptions]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: "google-maps-script", // IMPORTANT: use the SAME id across your whole app
  });

  const papaOpts = useMemo(
    () => ({
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      transformHeader: (h) => h.trim().replace(/^\uFEFF/, "").toLowerCase(),
    }),
    []
  );

  const toggleLink = (id) => {
    setSelectedLinkIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleNode = (id) => {
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleParcel = (id) => {
    setSelectedParcelIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleOther = (id) => {
    setSelectedOtherIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePolygon = (id) => {
    setSelectedPolygonIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearSelections = useCallback(() => {
    setSelectedLinkIds(new Set());
    setSelectedNodeIds(new Set());
    setSelectedParcelIds(new Set());
    setSelectedOtherIds(new Set());
    setSelectedPolygonIds(new Set());
  }, []);

  const centerSelectionOnMap = useCallback((layer, id) => {
    setMapFocusTarget({ layer, id: String(id), ts: Date.now() });
  }, []);

  const selectHazardFeature = useCallback((featureIndex) => {
    const id = String(featureIndex || "");
    setSelectedHazardFeatureIndex(id);
    centerSelectionOnMap("hazard", id || "__all__");
  }, [centerSelectionOnMap]);

  const resetAddLinkDraftState = useCallback(() => {
    setAddLinkDraftMode("idle");
    setAddLinkDraftPath([]);
    addLinkDraftPathRef.current = [];
    setAddLinkDraftInfo((prevDraft) => {
      if (!prevDraft?.linkId) return null;
      const linkId = String(prevDraft.linkId);
      const startDraftNodeId = String(prevDraft.draftNodeIds?.start || "");
      const endDraftNodeId = String(prevDraft.draftNodeIds?.end || "");
      setLinks((prev) => prev.filter((l) => String(l?.link_id) !== linkId && !isDraftLikeRow(l)));
      setNodes((prev) =>
        prev.filter((n) => {
          const id = String(n?.node_id);
          if (id === startDraftNodeId || id === endDraftNodeId) return false;
          return !isDraftLikeRow(n);
        })
      );
      return null;
    });
    setAddLinkNodeLayerLoading(false);
    setDraftTopologyStep("start");
    setDraftTopologyChoices({ start: "leave_as_is", end: "leave_as_is" });
    setDraftNearestLinkOptions({ start: [], end: [] });
    setDraftNearestLinkSelected({ start: [], end: [] });
  }, []);

  const updateDestinationConfigVariable = useCallback((name, updater) => {
    const targetName = String(name || "").trim();
    if (!targetName || typeof updater !== "function") return;
    setCustomConfigVars((prev) =>
      prev.map((row) => {
        if (row.name !== targetName || row.type !== "destination") return row;
        const nextRow = updater(row);
        return nextRow || row;
      })
    );
  }, []);

  const updateStartTimesConfigVariable = useCallback((name, updater) => {
    const targetName = String(name || "").trim();
    if (!targetName || typeof updater !== "function") return;
    setCustomConfigVars((prev) =>
      prev.map((row) => {
        if (row.name !== targetName || row.type !== "start_times") return row;
        const nextRow = updater({
          ...DEFAULT_START_TIMES,
          ...row,
        });
        return nextRow || row;
      })
    );
  }, []);

  const updateHazardConfigVariable = useCallback((name, updater) => {
    const targetName = String(name || "").trim();
    if (!targetName || typeof updater !== "function") return;
    setCustomConfigVars((prev) => prev.map((row) => {
      if (row.name !== targetName || row.type !== "hazard_layer") return row;
      return updater(row) || row;
    }));
  }, []);

  const updateBlockedRoadConfigVariable = useCallback((name, updater) => {
    const targetName = String(name || "").trim();
    setCustomConfigVars((prev) => prev.map((row) => row.name === targetName && row.type === "blocked_road" ? (updater(row) || row) : row));
  }, []);

  const updateContraflowConfigVariable = useCallback((name, updater) => {
    const targetName = String(name || "").trim();
    if (!targetName || typeof updater !== "function") return;
    setCustomConfigVars((prev) => prev.map((row) => {
      if (row.name !== targetName || row.type !== "contraflow") return row;
      return updater(row) || row;
    }));
  }, []);

  const setActiveHazardConfigLayer = useCallback((index, layerId) => {
    if (effectiveConfigContext.type !== "hazard_layer") return;
    const wanted = String(layerId || "");
    const layer = hazardLayers.find((row) => String(row.id) === wanted);
    updateHazardConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      hazardEntries: (row.hazardEntries || []).map((entry, entryIndex) =>
        entryIndex === index
          ? { ...entry, hazardLayerId: wanted, hazardLayerName: String(layer?.name || "") }
          : entry
      ),
    }));
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, hazardLayers, updateHazardConfigVariable]);

  const setActiveHazardRerouting = useCallback((index, enabled) => {
    if (effectiveConfigContext.type !== "hazard_layer") return;
    updateHazardConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      hazardEntries: (row.hazardEntries || []).map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, hazardRerouting: Boolean(enabled) } : entry
      ),
    }));
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, updateHazardConfigVariable]);

  const addActiveHazardEntry = useCallback(() => {
    if (effectiveConfigContext.type !== "hazard_layer") return;
    updateHazardConfigVariable(effectiveConfigContext.name, (row) => {
      const entries = Array.isArray(row.hazardEntries) ? row.hazardEntries : [];
      return {
        ...row,
        hazardEntries: [...entries, {
          name: `hazard_layer_${entries.length + 1}`,
          hazardLayerId: String(hazardLayers[0]?.id || ""),
          hazardLayerName: String(hazardLayers[0]?.name || ""),
          hazardRerouting: false,
        }],
      };
    });
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, hazardLayers, updateHazardConfigVariable]);

  const removeActiveHazardEntry = useCallback((index) => {
    if (effectiveConfigContext.type !== "hazard_layer") return;
    updateHazardConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      hazardEntries: (row.hazardEntries || [])
        .filter((_, entryIndex) => entryIndex !== index)
        .map((entry, entryIndex) => ({ ...entry, name: `hazard_layer_${entryIndex + 1}` })),
    }));
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, updateHazardConfigVariable]);

  const toggleDestinationNode = useCallback(
    (id) => {
      const key = String(id);
      if (!key) return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateDestinationConfigVariable(targetName, (row) => {
          const current = Array.isArray(row.destinationIds) ? row.destinationIds : [];
          const has = current.includes(key);
          const destinationIds = has ? current.filter((v) => v !== key) : [...current, key];
          return { ...row, destinationIds };
        });
    },
    [effectiveConfigContext.name, updateDestinationConfigVariable]
  );

  const setActiveDepartureMin = useCallback(
    (rawMin) => {
      if (effectiveConfigContext.type !== "start_times") return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateStartTimesConfigVariable(targetName, (row) => {
        return {
          ...row,
          minDepartureTime: rawMin,
          totalPopulationPct: 100,
        };
      });
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, updateStartTimesConfigVariable]
  );

  const setActiveDepartureMax = useCallback(
    (rawMax) => {
      if (effectiveConfigContext.type !== "start_times") return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateStartTimesConfigVariable(targetName, (row) => {
        return {
          ...row,
          maxDepartureTime: rawMax,
          totalPopulationPct: 100,
        };
      });
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, updateStartTimesConfigVariable]
  );

  const setActiveGranularSurvey = useCallback(
    (enabled) => {
      if (effectiveConfigContext.type !== "start_times") return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateStartTimesConfigVariable(targetName, (row) => {
        let startTimeRanges = Array.isArray(row.startTimeRanges) ? row.startTimeRanges : [];
        if (enabled && !startTimeRanges.length) {
          startTimeRanges = [createInitialDepartureRange(row.minDepartureTime, row.maxDepartureTime)];
        }
        return { ...row, granularSurvey: Boolean(enabled), startTimeRanges };
      });
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, updateStartTimesConfigVariable]
  );

  const updateActiveStartTimeRange = useCallback(
    (index, field, value) => {
      if (effectiveConfigContext.type !== "start_times") return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateStartTimesConfigVariable(targetName, (row) => ({
        ...row,
        startTimeRanges: (Array.isArray(row.startTimeRanges) ? row.startTimeRanges : []).map(
          (range, rangeIndex) => rangeIndex === index ? { ...range, [field]: value } : range
        ),
      }));
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, updateStartTimesConfigVariable]
  );

  const addActiveStartTimeRange = useCallback(() => {
    if (effectiveConfigContext.type !== "start_times") return;
    const targetName = String(effectiveConfigContext.name || "").trim();
    if (!targetName) return;
    updateStartTimesConfigVariable(targetName, (row) => {
      const ranges = Array.isArray(row.startTimeRanges) ? row.startTimeRanges : [];
      const previousEnd = ranges.length ? Number(ranges[ranges.length - 1]?.rangeEnd) : 0;
      const rangeStart = Number.isFinite(previousEnd) ? previousEnd : 0;
      return {
        ...row,
        startTimeRanges: [
          ...ranges,
          { rangeStart, rangeEnd: rangeStart + 5, probabilityWeightPct: "" },
        ],
      };
    });
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, updateStartTimesConfigVariable]);

  const removeActiveStartTimeRange = useCallback(
    (index) => {
      if (effectiveConfigContext.type !== "start_times") return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateStartTimesConfigVariable(targetName, (row) => ({
        ...row,
        startTimeRanges: (Array.isArray(row.startTimeRanges) ? row.startTimeRanges : []).filter(
          (_, rangeIndex) => rangeIndex !== index
        ),
      }));
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, updateStartTimesConfigVariable]
  );

  const onChangeLayerSelection = useCallback(
    (nextLayerId) => {
      const next = layerByIdRef.current.get(String(nextLayerId || ""));
      if (!next) return;
      trackBuilderEvent("builder_layer_selected", { builder_id: simulationIdSafe, tab: "edit", layer_id: next.id, layer_type: next.type });
      const isSameLayer = activeLayerIdRef.current === String(next.id);
      resetAddLinkDraftState();
      setMapFocusTarget(null);
      if (!isSameLayer) {
        setLinks([]);
        setNodes([]);
        setParcels([]);
        setOtherRows([]);
        setPolygonRows([]);
      }
      setIsFilteringLayer(false);
      setSelectedFilterPolygonLayerIds([]);
      setFilterPolygonOverlayRows([]);
      clearSelections();
      setActiveLayerId(next.id);
      setActiveLayer(next.type);
      setActiveLayerByType((prev) => ({ ...prev, [next.type]: next }));
      setLayerSaveState({ isSaving: false, error: "", success: "" });
    },
    [clearSelections, resetAddLinkDraftState, simulationIdSafe]
  );

  const onManualLayerSelection = useCallback((nextLayerId) => {
    setDestinationMapConfigName("");
    setAgentMapState((current) => current ? {
      ...current,
      primaryLayer: null,
      destinationLayer: { visible: false },
      hazardLayer: { visible: false },
    } : current);
    setIsAgentHazardVisible(false);
    setRequestedPresentationId("");
    setFocusedPresentationMessageId("");
    setMapFitSelection(null);
    onChangeLayerSelection(nextLayerId);
  }, [onChangeLayerSelection]);

  useEffect(() => {
    setAgentMapState(null);
    setIsAgentHazardVisible(false);
    if (!simulationIdSafe || !activeAgentSessionId) return undefined;

    const sessionKey = simulationIdSafe + "/" + activeAgentSessionId;
    const mapStateRef = databaseRef(
      database,
      `model_builders/${simulationIdSafe}/chat_sessions/${activeAgentSessionId}/ui/map`
    );
    return onValue(mapStateRef, (snapshot) => {
      if (hydratedMapSessionRef.current !== sessionKey) {
        hydratedMapSessionRef.current = sessionKey;
        return;
      }
      const nextState = snapshot.val();
      setAgentMapState(nextState && typeof nextState === "object" ? nextState : null);
    }, (error) => {
      console.warn("Failed to subscribe to agent map state:", error);
    });
  }, [simulationIdSafe, activeAgentSessionId]);

  useEffect(() => {
    setRequestedPresentationId("");
    setFocusedPresentationMessageId("");
    if (!simulationIdSafe || !activeAgentSessionId) return undefined;
    const presentationsRef = collection(
      firestore,
      "model_builders",
      simulationIdSafe,
      "chat_sessions",
      activeAgentSessionId,
      "presentations"
    );
    return onSnapshot(presentationsRef, (snapshot) => {
      const createdAtMs = (value) => {
        if (typeof value?.toMillis === "function") return value.toMillis();
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : 0;
      };
      const rows = snapshot.docs
        .map((item) => ({ id: item.id, ...(item.data() || {}) }))
        .filter((item) => item.kind !== "hypothesis_board")
        .sort((a, b) => createdAtMs(a.createdAt) - createdAtMs(b.createdAt));
      setPresentationSnapshot({ sessionId: activeAgentSessionId, rows, hydrated: true });
    }, (error) => {
      console.warn("Failed to subscribe to agent presentations:", error);
    });
  }, [simulationIdSafe, activeAgentSessionId]);

  useEffect(() => {
    if (!agentMapState || handledAgentMapStateRef.current === agentMapState) return;
    if (rightTab === "edit" || rightTab === "config") {
      handledAgentMapStateRef.current = agentMapState;
      return;
    }
    const requested = agentMapState?.primaryLayer;
    const layerId = String(requested?.layerId || "").trim();
    const layerType = String(requested?.type || "").trim().toLowerCase();
    const hasPrimaryRequest = !!layerId && ["links", "nodes", "parcels"].includes(layerType);
    const layer = hasPrimaryRequest ? layerById.get(layerId) : null;
    if (hasPrimaryRequest && (!layer || layer.type !== layerType)) return;
    handledAgentMapStateRef.current = agentMapState;
    const destinationName = agentMapState?.destinationLayer?.visible === true
      ? String(agentMapState.destinationLayer.configName || "").trim() : "";
    if (hasPrimaryRequest) {
      setDestinationMapConfigName(layerType === "nodes" ? destinationName : "");
      if (activeLayerIdRef.current !== layerId) onChangeLayerSelection(layerId);
    } else if (destinationName) {
      setDestinationMapConfigName(destinationName);
    }
    if (destinationName && rightTab !== "config" && !configEditingRef.current) setActiveConfigVarName(destinationName);
  }, [agentMapState, layerById, onChangeLayerSelection, rightTab]);

  const ensureTypeLayerSelected = useCallback(
    (type) => {
      const targetType = String(type || "").toLowerCase();
      if (!LAYER_TYPES.includes(targetType)) return;
      if (activeLayerByType[targetType]?.id) {
        const current = activeLayerByType[targetType];
        setActiveLayer(targetType);
        setActiveLayerId(String(current.id));
        return;
      }
      const first = (gisLayers || []).find((row) => row.type === targetType);
      if (!first) return;
      setActiveLayer(targetType);
      setActiveLayerId(String(first.id));
      setActiveLayerByType((prev) => ({ ...prev, [targetType]: first }));
    },
    [activeLayerByType, gisLayers]
  );

  const startAddLinkDraft = useCallback(() => {
    if (addLinkDraftMode !== "idle") {
      const shouldDiscard = window.confirm(
        "A draft link is in progress. Start a new draft and discard the current unsaved draft?"
      );
      if (!shouldDiscard) return;
    }
    setAddLinkDraftInfo(null);
    ensureTypeLayerSelected("links");
    setMapFocusTarget(null);
    setLayerSaveState({ isSaving: false, error: "", success: "" });
    setAddLinkNodeLayerLoading(false);
    setAddLinkDraftMode("drawing");
    setAddLinkDraftPath([]);
    addLinkDraftPathRef.current = [];
    setDraftTopologyStep("start");
    setDraftTopologyChoices({ start: "leave_as_is", end: "leave_as_is" });
    setDraftNearestLinkOptions({ start: [], end: [] });
    setDraftNearestLinkSelected({ start: [], end: [] });
    clearSelections();
  }, [addLinkDraftMode, clearSelections, ensureTypeLayerSelected]);

  const undoAddLinkDraftPoint = useCallback(() => {
    setAddLinkDraftPath((prev) => {
      const next = prev.slice(0, -1);
      addLinkDraftPathRef.current = next;
      return next;
    });
  }, []);

  const cancelAddLinkDraft = useCallback(() => {
    resetAddLinkDraftState();
    setMapFocusTarget(null);
    setNodes([]);
    clearSelections();
    setLayerSaveState({ isSaving: false, error: "", success: "" });
  }, [resetAddLinkDraftState, clearSelections]);

  const backAddLinkNodeLayer = useCallback(() => {
    if (addLinkDraftMode !== "node_layer") return;
    setAddLinkNodeLayerLoading(false);
    setAddLinkDraftMode("drawing");
    setLayerSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [addLinkDraftMode]);

  const discardAddLinkDraft = useCallback(() => {
    setAddLinkDraftInfo((draft) => {
      if (!draft?.linkId) return null;
      const linkId = String(draft.linkId);
      setLinks((prev) => prev.filter((l) => String(l?.link_id) !== linkId && !isDraftLikeRow(l)));
      return null;
    });
    setNodes([]);
    clearSelections();
    resetAddLinkDraftState();
    setMapFocusTarget(null);
    setLayerSaveState({ isSaving: false, error: "", success: "" });
  }, [resetAddLinkDraftState, clearSelections]);

  const finishAddLinkDraft = useCallback(() => {
    const path = [...(addLinkDraftPathRef.current || [])];
    if (!Array.isArray(path) || path.length < 2) {
      setLayerSaveState({
        isSaving: false,
        error: "At least two points are required to create a draft link.",
        success: "",
      });
      return;
    }
    if (!nodeLayerOptions.length) {
      setLayerSaveState({
        isSaving: false,
        error: "No node layer available. Create or select a node layer first.",
        success: "",
      });
      return;
    }
    setAddLinkDraftInfo(null);
    setNodes([]);
    setSelectedNodeIds(new Set());
    setAddLinkNodeLayerLoading(false);
    setAddLinkDraftMode("node_layer");
    setLayerSaveState({
      isSaving: false,
      error: "",
      success: "Link geometry accepted. Select a node layer to continue.",
    });
  }, [nodeLayerOptions]);

  const onAddLinkDraftPoint = useCallback((pt) => {
    if (addLinkDraftMode !== "drawing") return;
    setAddLinkDraftPath((prev) => {
      const next = [...prev, pt];
      addLinkDraftPathRef.current = next;
      return next;
    });
  }, [addLinkDraftMode]);

  const buildNearestLinkOptions = useCallback((endpoint) => {
    if (addLinkDraftMode !== "ready" || !addLinkDraftInfo?.linkId) return;
    const draftLinkId = String(addLinkDraftInfo.linkId);
    const draftLinkRow = (links || []).find((l) => String(l?.link_id) === draftLinkId);
    if (!draftLinkRow?.path?.length) return;
    const endpointPt = endpoint === "start"
      ? draftLinkRow.path[0]
      : draftLinkRow.path[draftLinkRow.path.length - 1];
    if (!endpointPt) return;

    const candidates = (links || [])
      .filter((l) => String(l?.link_id) !== draftLinkId && Array.isArray(l?.path) && l.path.length >= 2)
      .map((l) => ({
        id: String(l.link_id),
        start: getLinkStartNodeId(l),
        end: getLinkEndNodeId(l),
        proj: closestPointOnPath(l.path, endpointPt),
      }))
      .filter((c) => !!c.proj)
      .sort((a, b) => a.proj.dist2 - b.proj.dist2);

    const nearest = candidates[0];
    if (!nearest) {
      setDraftNearestLinkOptions((prev) => ({ ...prev, [endpoint]: [] }));
      setDraftNearestLinkSelected((prev) => ({ ...prev, [endpoint]: [] }));
      return;
    }

    const opposite = candidates.find(
      (c) => c.start === nearest.end && c.end === nearest.start
    );
    const opts = opposite && opposite.id !== nearest.id ? [nearest.id, opposite.id] : [nearest.id];
    setDraftNearestLinkOptions((prev) => ({ ...prev, [endpoint]: opts }));
    setDraftNearestLinkSelected((prev) => ({ ...prev, [endpoint]: [] }));
  }, [addLinkDraftMode, addLinkDraftInfo, links]);

  const toggleDraftNearestLinkSelection = useCallback((endpoint, linkId) => {
    const id = String(linkId);
    setDraftNearestLinkSelected((prev) => {
      const current = Array.isArray(prev?.[endpoint]) ? prev[endpoint] : [];
      const has = current.includes(id);
      const next = has ? current.filter((v) => v !== id) : [...current, id];
      return { ...prev, [endpoint]: next };
    });
  }, []);

  useEffect(() => {
    if (addLinkDraftMode !== "ready") return;
    if (draftTopologyStep === "start" && draftTopologyChoices?.start === "nearest_link") {
      buildNearestLinkOptions("start");
    }
    if (draftTopologyStep === "end" && draftTopologyChoices?.end === "nearest_link") {
      buildNearestLinkOptions("end");
    }
  }, [
    addLinkDraftMode,
    draftTopologyStep,
    draftTopologyChoices?.start,
    draftTopologyChoices?.end,
    buildNearestLinkOptions,
  ]);
  
  const pick = useCallback((row, keys) => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
  }, []);
  
  const fixLatLng = useCallback((latRaw, lngRaw) => {
    let lat = Number(latRaw);
    let lng = Number(lngRaw);
  
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  
    // If swapped (common), fix it
    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) [lat, lng] = [lng, lat];
  
    // Hard reject impossible values (prevents Google silently failing)
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  
    return { lat, lng };
  }, []);


  // -------------------------
  // Fetch inputs
  // -------------------------
  const inputsBase = useMemo(() => `model_builders/${SIMULATION_ID}/inputs`, [SIMULATION_ID]);
  const configBase = useMemo(() => `model_builders/${SIMULATION_ID}/config`, [SIMULATION_ID]);

  const normalizeParsedRowsForType = useCallback((type, parsedRows) => {
    const rows = Array.isArray(parsedRows) ? parsedRows : [];
    const normalizedType = String(type || "").toLowerCase();

    if (normalizedType === "links") {
      return rows
        .map((row) => {
          const link_id = pick(row, ["link_id", "linkid", "id"]);
          const geometry = pick(row, ["geometry", "geom", "wkt"]);
          const path = parseLineStringToPath(geometry);
          if (!link_id || !path || path.length < 2) return null;
          return { ...row, link_id: String(link_id), geometry, path };
        })
        .filter(Boolean);
    }

    if (normalizedType === "nodes") {
      return rows
        .map((row) => {
          const node_id = pick(row, ["node_id", "nodeid", "id"]);
          const latRaw = pick(row, ["lat", "latitude"]);
          const lngRaw = pick(row, ["lon", "lng", "longitude"]);
          const ll = fixLatLng(latRaw, lngRaw);
          if (!node_id || !ll) return null;
          return { ...row, node_id: String(node_id), lat: ll.lat, lng: ll.lng };
        })
        .filter(Boolean);
    }

    if (normalizedType === "parcels") {
      return rows
        .map((row) => {
          const parcel_id = pick(row, ["parcel_id", "parcelid", "id"]);
          const latRaw = pick(row, ["lat", "latitude"]);
          const lngRaw = pick(row, ["lon", "lng", "longitude"]);
          const ll = fixLatLng(latRaw, lngRaw);
          if (!parcel_id || !ll) return null;

          const popRaw = pick(row, ["pop", "population"]);
          const pop = popRaw != null && popRaw !== "" ? Number(popRaw) : undefined;
          return { ...row, parcel_id: String(parcel_id), lat: ll.lat, lng: ll.lng, pop };
        })
        .filter(Boolean);
    }

    return rows.map((row, idx) => ({
      ...row,
      _row_id: String(idx + 1),
    }));
  }, [pick, fixLatLng]);

  const parseRowsForType = useCallback((type, rawText) => {
    const text = String(rawText || "");
    if (!text) return [];
    if (type === "polygon") {
      return parsePolygonGeoJsonFeatures(text);
    }
    const parsedCsv = Papa.parse(text, papaOpts);
    return normalizeParsedRowsForType(type, parsedCsv?.data);
  }, [papaOpts, normalizeParsedRowsForType]);

  const parseRowsForTypeAsync = useCallback(
    async (type, rawText) => {
      const text = String(rawText || "");
      if (!text) return [];
      if (type === "polygon") {
        return parsePolygonGeoJsonFeatures(text);
      }
      // Parsing already-downloaded large strings with worker:true can stall due string copy into worker.
      // Keep the async API, but use the stable sync parser path for now.
      return parseRowsForType(type, text);
    },
    [parseRowsForType]
  );

  const readLayerData = useCallback(async (layerDoc) => {
    if (!layerDoc?.filename || !layerDoc?.type) return [];
    // A result's network lives beside its output, not in the Builder's inputs folder.
    const base = layerDoc.resultId ? `${layerDoc.resultId}/inputs` : inputsBase;
    const fileText = await fetchCsvTextFromStorage(`${base}/${layerDoc.filename}`);
    return parseRowsForTypeAsync(layerDoc.type, fileText || "");
  }, [inputsBase, parseRowsForTypeAsync]);

  const sortHazardSnapshots = useCallback(
    (rows) => [...(rows || [])].sort((a, b) => Number(a.timeMin) - Number(b.timeMin)),
    []
  );

  const updateHazardLayerSummary = useCallback(async (layerId, snapshots) => {
    const ordered = sortHazardSnapshots(snapshots);
    await setDoc(
      doc(firestore, "model_builders", simulationIdSafe, "hazard_layers", String(layerId)),
      {
        snapshotCount: ordered.length,
        minTimeMin: ordered.length ? Number(ordered[0].timeMin) : null,
        maxTimeMin: ordered.length ? Number(ordered[ordered.length - 1].timeMin) : null,
        updatedAt: Date.now(),
      },
      { merge: true }
    );
  }, [simulationIdSafe, sortHazardSnapshots]);

  useEffect(() => {
    if (!simulationIdSafe) return undefined;
    return onSnapshot(
      collection(firestore, "model_builders", simulationIdSafe, "hazard_layers"),
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
        setHazardLayers(rows);
        setActiveHazardLayerId((current) => rows.some((row) => row.id === current) ? current : String(rows[0]?.id || ""));
      },
      (error) => setHazardState({ isBusy: false, error: String(error?.message || error), success: "" })
    );
  }, [simulationIdSafe]);

  useEffect(() => {
    const layerId = String(activeHazardLayerId || "");
    if (!simulationIdSafe || !layerId) {
      setHazardSnapshots([]);
      setActiveHazardSnapshotId("");
      return undefined;
    }
    return onSnapshot(
      collection(firestore, "model_builders", simulationIdSafe, "hazard_layers", layerId, "snapshots"),
      (snapshot) => {
        const rows = sortHazardSnapshots(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() || {}) })));
        setHazardSnapshots(rows);
        setActiveHazardSnapshotId((current) => rows.some((row) => row.id === current) ? current : String(rows[0]?.id || ""));
      },
      (error) => setHazardState({ isBusy: false, error: String(error?.message || error), success: "" })
    );
  }, [simulationIdSafe, activeHazardLayerId, sortHazardSnapshots]);

  useEffect(() => {
    if (handledAgentHazardStateRef.current === agentMapState) return;
    if (rightTab === "edit" || rightTab === "config") {
      handledAgentHazardStateRef.current = agentMapState;
      return;
    }
    const requested = agentMapState?.hazardLayer;
    if (!requested || requested.visible !== true) {
      handledAgentHazardStateRef.current = agentMapState;
      setIsAgentHazardVisible(false);
      return;
    }

    const layerId = String(requested.layerId || "").trim();
    if (!layerId || !hazardLayers.some((layer) => String(layer.id) === layerId)) {
      setIsAgentHazardVisible(false);
      console.warn(`Agent requested unavailable hazard layer: ${layerId || "(missing id)"}`);
      return;
    }

    handledAgentHazardStateRef.current = agentMapState;
    setActiveHazardLayerId(layerId);
    setIsAgentHazardVisible(true);
  }, [agentMapState, hazardLayers, rightTab]);

  useEffect(() => {
    if (rightTab === "edit" || rightTab === "config") return;
    const requested = agentMapState?.hazardLayer;
    const layerId = String(requested?.layerId || "").trim();
    const snapshotId = String(requested?.snapshotId || "").trim();
    if (!requested?.visible || !snapshotId || activeHazardLayerId !== layerId) return;

    if (!hazardSnapshots.some((snapshot) => String(snapshot.id) === snapshotId)) {
      console.warn(`Agent requested unavailable hazard snapshot: ${snapshotId}`);
      return;
    }
    setActiveHazardSnapshotId(snapshotId);
  }, [agentMapState, activeHazardLayerId, hazardSnapshots, rightTab]);

  useEffect(() => {
    const snapshot = hazardSnapshots.find((row) => row.id === activeHazardSnapshotId);
    if (!snapshot?.storagePath) {
      setActiveHazardRows([]);
      setActiveHazardGeoJson(null);
      return;
    }
    let cancelled = false;
    fetchJsonFromStorage(snapshot.storagePath)
      .then((json) => {
        if (cancelled) return;
        const parsed = parseHazardGeoJsonSnapshot(JSON.stringify(json || {}));
        if (!parsed?.ok) throw new Error(parsed?.error || "Could not parse hazard snapshot.");
        setActiveHazardRows(parsed.rows || []);
        setActiveHazardGeoJson(parsed.geojson || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setActiveHazardRows([]);
        setActiveHazardGeoJson(null);
        setHazardState((prev) => ({ ...prev, error: String(err?.message || err), success: "" }));
      });
    return () => { cancelled = true; };
  }, [hazardSnapshots, activeHazardSnapshotId]);

  useEffect(() => {
    setSelectedHazardFeatureIndex("");
    centerSelectionOnMap("hazard", "__all__");
  }, [activeHazardLayerId, activeHazardSnapshotId, centerSelectionOnMap]);

  const createHazardLayer = useCallback(async (rawName) => {
    const name = String(rawName || "").trim();
    if (!name || !simulationIdSafe) return false;
    if (hazardLayers.some((row) => String(row.name || "").toLowerCase() === name.toLowerCase())) {
      setHazardState({ isBusy: false, error: `A hazard layer named "${name}" already exists.`, success: "" });
      return false;
    }
    setHazardState({ isBusy: true, error: "", success: "" });
    try {
      const layerRef = doc(collection(firestore, "model_builders", simulationIdSafe, "hazard_layers"));
      const row = { id: layerRef.id, name, snapshotCount: 0, minTimeMin: null, maxTimeMin: null, createdAt: Date.now(), updatedAt: Date.now() };
      await setDoc(layerRef, row);
      setHazardLayers((prev) => [...prev.filter((item) => item.id !== row.id), row]
        .sort((a, b) => String(a.name).localeCompare(String(b.name))));
      setActiveHazardLayerId(row.id);
      setHazardState({ isBusy: false, error: "", success: `Created hazard layer "${name}".` });
      return true;
    } catch (err) {
      setHazardState({ isBusy: false, error: String(err?.message || err), success: "" });
      return false;
    }
  }, [simulationIdSafe, hazardLayers]);

  const uploadHazardSnapshots = useCallback(async (files) => {
    const layerId = String(activeHazardLayerId || "").trim();
    const incoming = Array.isArray(files) ? files : [];
    if (!simulationIdSafe || !layerId || !incoming.length) return false;
    setHazardState({ isBusy: true, error: "", success: "" });
    const prepared = [];
    try {
      for (const file of incoming) {
        const parsed = parseHazardGeoJsonSnapshot(await file.text());
        if (!parsed?.ok) throw new Error(`${file.name}: ${parsed?.error || "Invalid hazard GeoJSON."}`);
        prepared.push({ file, parsed });
      }
      const occupiedTimes = new Set(hazardSnapshots.map((row) => Number(row.timeMin)));
      for (const item of prepared) {
        if (occupiedTimes.has(Number(item.parsed.timeMin))) {
          throw new Error(`A snapshot at ${item.parsed.timeMin} min already exists in this hazard layer.`);
        }
        occupiedTimes.add(Number(item.parsed.timeMin));
      }

      const created = [];
      try {
        for (const item of prepared) {
          const snapshotRef = doc(collection(
            firestore,
            "model_builders",
            simulationIdSafe,
            "hazard_layers",
            layerId,
            "snapshots"
          ));
          const storagePath = `${inputsBase}/hazards/${layerId}/${snapshotRef.id}.geojson`;
          const row = {
            id: snapshotRef.id,
            name: String(item.parsed.geojson?.name || item.file.name.replace(/\.(geojson|json)$/i, "")),
            originalFilename: item.file.name,
            storageFilename: `${snapshotRef.id}.geojson`,
            storagePath,
            timeMin: Number(item.parsed.timeMin),
            featureCount: Number(item.parsed.featureCount),
            geometryTypes: item.parsed.geometryTypes || [],
            createdAt: Date.now(),
          };
          await uploadString(storageRef(storage, storagePath), item.parsed.text, "raw", { contentType: "application/geo+json" });
          created.push(row);
          await setDoc(snapshotRef, row);
        }
      } catch (err) {
        await Promise.allSettled(created.flatMap((row) => [
          deleteObject(storageRef(storage, row.storagePath)),
          deleteDoc(doc(firestore, "model_builders", simulationIdSafe, "hazard_layers", layerId, "snapshots", row.id)),
        ]));
        throw err;
      }

      const createdIds = new Set(created.map((row) => row.id));
      const next = sortHazardSnapshots([...hazardSnapshots.filter((row) => !createdIds.has(row.id)), ...created]);
      await updateHazardLayerSummary(layerId, next);
      setHazardSnapshots(next);
      setActiveHazardSnapshotId(String(created[created.length - 1]?.id || next[0]?.id || ""));
      setHazardLayers((prev) => prev.map((row) => row.id === layerId ? {
        ...row,
        snapshotCount: next.length,
        minTimeMin: next.length ? Number(next[0].timeMin) : null,
        maxTimeMin: next.length ? Number(next[next.length - 1].timeMin) : null,
        updatedAt: Date.now(),
      } : row));
      setHazardState({ isBusy: false, error: "", success: `Uploaded ${created.length} hazard snapshot${created.length === 1 ? "" : "s"}.` });
      return true;
    } catch (err) {
      setHazardState({ isBusy: false, error: String(err?.message || err), success: "" });
      return false;
    }
  }, [activeHazardLayerId, simulationIdSafe, hazardSnapshots, inputsBase, sortHazardSnapshots, updateHazardLayerSummary]);

  const deleteHazardSnapshot = useCallback(async (snapshotId) => {
    const layerId = String(activeHazardLayerId || "");
    const snapshot = hazardSnapshots.find((row) => row.id === String(snapshotId));
    if (!layerId || !snapshot) return false;
    if (!window.confirm(`Delete hazard snapshot "${snapshot.originalFilename || snapshot.name}"?`)) return false;
    setHazardState({ isBusy: true, error: "", success: "" });
    try {
      try { await deleteObject(storageRef(storage, snapshot.storagePath)); } catch (err) {
        if (!String(err?.code || "").includes("object-not-found")) throw err;
      }
      await deleteDoc(doc(firestore, "model_builders", simulationIdSafe, "hazard_layers", layerId, "snapshots", snapshot.id));
      const next = sortHazardSnapshots(hazardSnapshots.filter((row) => row.id !== snapshot.id));
      await updateHazardLayerSummary(layerId, next);
      setHazardSnapshots(next);
      setActiveHazardSnapshotId((prev) => prev === snapshot.id ? String(next[0]?.id || "") : prev);
      setHazardLayers((prev) => prev.map((row) => row.id === layerId ? {
        ...row,
        snapshotCount: next.length,
        minTimeMin: next.length ? Number(next[0].timeMin) : null,
        maxTimeMin: next.length ? Number(next[next.length - 1].timeMin) : null,
        updatedAt: Date.now(),
      } : row));
      setHazardState({ isBusy: false, error: "", success: "Deleted hazard snapshot." });
      return true;
    } catch (err) {
      setHazardState({ isBusy: false, error: String(err?.message || err), success: "" });
      return false;
    }
  }, [activeHazardLayerId, hazardSnapshots, simulationIdSafe, sortHazardSnapshots, updateHazardLayerSummary]);

  const deleteHazardLayer = useCallback(async () => {
    const layerId = String(activeHazardLayerId || "");
    const layer = hazardLayers.find((row) => row.id === layerId);
    if (!layer || !window.confirm(`Delete hazard layer "${layer.name}" and all of its snapshots?`)) return false;
    setHazardState({ isBusy: true, error: "", success: "" });
    try {
      for (const snapshot of hazardSnapshots) {
        try { await deleteObject(storageRef(storage, snapshot.storagePath)); } catch (err) {
          if (!String(err?.code || "").includes("object-not-found")) throw err;
        }
        await deleteDoc(doc(firestore, "model_builders", simulationIdSafe, "hazard_layers", layerId, "snapshots", snapshot.id));
      }
      await deleteDoc(doc(firestore, "model_builders", simulationIdSafe, "hazard_layers", layerId));
      const remaining = hazardLayers.filter((row) => row.id !== layerId);
      setHazardLayers(remaining);
      setActiveHazardLayerId(String(remaining[0]?.id || ""));
      setHazardSnapshots([]);
      setActiveHazardSnapshotId("");
      setActiveHazardRows([]);
      setActiveHazardGeoJson(null);
      setHazardState({ isBusy: false, error: "", success: `Deleted hazard layer "${layer.name}".` });
      return true;
    } catch (err) {
      setHazardState({ isBusy: false, error: String(err?.message || err), success: "" });
      return false;
    }
  }, [activeHazardLayerId, hazardLayers, hazardSnapshots, simulationIdSafe]);

  const applyRowsForType = useCallback((type, rows) => {
    if (type === "links") setLinks(rows);
    else if (type === "nodes") setNodes(rows);
    else if (type === "parcels") setParcels(rows);
    else if (type === "other") setOtherRows(rows);
    else if (type === "polygon") setPolygonRows(rows);
  }, []);

  const clearAllLayerRows = useCallback(() => {
    setLinks([]);
    setNodes([]);
    setParcels([]);
    setOtherRows([]);
    setPolygonRows([]);
  }, []);

  const loadTypeRows = useCallback(
    async (type, layerDoc, options = {}) => {
      const normalizedType = String(type || "").toLowerCase();
      if (!LAYER_TYPES.includes(normalizedType)) return [];
      const shouldClearOthers = !!options?.clearOthers;
      const tokenKey = shouldClearOthers ? "__selected" : normalizedType;

      const token = layerLoadSeqRef.current + 1;
      layerLoadSeqRef.current = token;
      latestLayerLoadTokenRef.current[tokenKey] = token;

      if (shouldClearOthers) {
        clearAllLayerRows();
      }

      if (!layerDoc?.filename) {
        if (latestLayerLoadTokenRef.current[tokenKey] === token) {
          applyRowsForType(normalizedType, []);
        }
        return [];
      }

      try {
        const rows = await readLayerData(layerDoc);
        if (latestLayerLoadTokenRef.current[tokenKey] !== token) return [];
        applyRowsForType(normalizedType, rows);
        return rows;
      } catch {
        if (latestLayerLoadTokenRef.current[tokenKey] !== token) return [];
        applyRowsForType(normalizedType, []);
        return [];
      }
    },
    [readLayerData, applyRowsForType, clearAllLayerRows]
  );

  const refreshSelectedLayer = useCallback(async () => {
    if (!selectedLayer?.type) {
      clearAllLayerRows();
      return;
    }
    await loadTypeRows(selectedLayer.type, selectedLayer, { clearOthers: true });
  }, [selectedLayer, loadTypeRows, clearAllLayerRows]);

  const refreshParcels = useCallback(async () => {
    if (selectedLayer?.type === "parcels") {
      await refreshSelectedLayer();
    }
  }, [selectedLayer?.type, refreshSelectedLayer]);

  useEffect(() => {
    if (!isFilteringLayer) {
      setFilterPolygonOverlayRows([]);
      return;
    }
    const wantedIds = (selectedFilterPolygonLayerIds || [])
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    if (!wantedIds.length) {
      setFilterPolygonOverlayRows([]);
      return;
    }

    const token = filterPolygonLoadSeqRef.current + 1;
    filterPolygonLoadSeqRef.current = token;

    (async () => {
      try {
        const loadedRows = await Promise.all(
          wantedIds.map(async (layerId) => {
            const layerDoc = layerById.get(layerId);
            if (!layerDoc?.filename || layerDoc.type !== "polygon") return [];
            const rows = await readLayerData(layerDoc);
            return (rows || []).map((row, idx) => ({
              ...row,
              polygon_id: String(row?.polygon_id || `${layerId}_${idx + 1}`),
            }));
          })
        );
        if (filterPolygonLoadSeqRef.current !== token) return;
        setFilterPolygonOverlayRows(loadedRows.flat());
      } catch {
        if (filterPolygonLoadSeqRef.current !== token) return;
        setFilterPolygonOverlayRows([]);
      }
    })();
  }, [isFilteringLayer, selectedFilterPolygonLayerIds, layerById, readLayerData]);

  const acceptAddLinkNodeLayer = useCallback(async () => {
    if (addLinkDraftMode !== "node_layer") return false;
    const path = [...(addLinkDraftPathRef.current || [])];
    if (!Array.isArray(path) || path.length < 2) {
      setLayerSaveState({
        isSaving: false,
        error: "Draw a valid link before selecting a node layer.",
        success: "",
      });
      return false;
    }

    const nodeLayerId = String(addLinkSelectedNodeLayerId || "").trim();
    const nodeLayerDoc = nodeLayerOptions.find((row) => String(row.id) === nodeLayerId);
    if (!nodeLayerDoc?.filename) {
      setLayerSaveState({
        isSaving: false,
        error: "Select a valid node layer.",
        success: "",
      });
      return false;
    }

    setAddLinkNodeLayerLoading(true);
    setLayerSaveState({ isSaving: false, error: "", success: "" });
    try {
      const baseNodes = await readLayerData(nodeLayerDoc);
      const maxNodeId = baseNodes.reduce((acc, row) => {
        const v = Number(row?.node_id);
        return Number.isFinite(v) ? Math.max(acc, Math.trunc(v)) : acc;
      }, 0);
      const maxLinkId = links.reduce((acc, row) => {
        const v = Number(row?.link_id);
        return Number.isFinite(v) ? Math.max(acc, Math.trunc(v)) : acc;
      }, 0);

      const startNodeId = String(maxNodeId + 1);
      const endNodeId = String(maxNodeId + 2);
      const linkId = String(maxLinkId + 1);
      const first = path[0];
      const last = path[path.length - 1];

      const draftLink = {
        link_id: linkId,
        id: linkId,
        start_node_id: startNodeId,
        end_node_id: endNodeId,
        name: `Draft link ${linkId}`,
        type: "draft",
        length: pathLengthMeters(path),
        maxmph: "",
        lanes: "",
        capacity: "",
        fft: "",
        start_osmid: "",
        end_osmid: "",
        status: "draft",
        geometry: pathToLineString(path),
        path,
        __isDraft: true,
      };
      const startDraftNode = {
        node_id: startNodeId,
        name: `Draft node ${startNodeId}`,
        status: "draft",
        lat: Number(first.lat),
        lng: Number(first.lng),
        lon: Number(first.lng),
        __isDraft: true,
      };
      const endDraftNode = {
        node_id: endNodeId,
        name: `Draft node ${endNodeId}`,
        status: "draft",
        lat: Number(last.lat),
        lng: Number(last.lng),
        lon: Number(last.lng),
        __isDraft: true,
      };

      setLinks((prev) => [...prev, draftLink]);
      setNodes([...(baseNodes || []), startDraftNode, endDraftNode]);
      setSelectedLinkIds(new Set([linkId]));
      setSelectedNodeIds(new Set([startNodeId, endNodeId]));
      setAddLinkDraftInfo({
        linkId,
        nodeLayerId: nodeLayerDoc.id,
        nodeLayerFileName: nodeLayerDoc.filename,
        draftNodeIds: { start: startNodeId, end: endNodeId },
        endpointNodeIds: { start: startNodeId, end: endNodeId },
        activeDraftEndpoints: { start: true, end: true },
        originalPath: path.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })),
        endpointSnapshots: { start: null, end: null },
      });
      setAddLinkDraftMode("ready");
      setAddLinkDraftPath([]);
      addLinkDraftPathRef.current = [];
      setDraftTopologyStep("start");
      setDraftTopologyChoices({ start: "leave_as_is", end: "leave_as_is" });
      setDraftNearestLinkOptions({ start: [], end: [] });
      setDraftNearestLinkSelected({ start: [], end: [] });
      setLayerSaveState({
        isSaving: false,
        error: "",
        success: `Node layer loaded. Draft link ${linkId} is ready for topology.`,
      });
      return true;
    } catch (err) {
      setLayerSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    } finally {
      setAddLinkNodeLayerLoading(false);
    }
  }, [addLinkDraftMode, addLinkSelectedNodeLayerId, nodeLayerOptions, readLayerData, links]);

  const applyGisLayerCatalog = useCallback((rows) => {
    const loaded = [...rows].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    setGisLayers(loaded);
    const byType = { links: null, nodes: null, parcels: null, other: null, polygon: null };
    loaded.forEach((row) => { if (!byType[row.type]) byType[row.type] = row; });
    setActiveLayerByType(byType);

    const selected = loaded.find((row) => row.id === activeLayerIdRef.current)
      || byType[activeLayerRef.current] || loaded[0];
    if (selected) {
      activeLayerIdRef.current = selected.id;
      activeLayerRef.current = selected.type;
      setActiveLayerId(selected.id);
      setActiveLayer(selected.type);
    }
  }, []);

  const loadGisLayers = useCallback(async () => {
    const colRef = collection(firestore, "model_builders", String(SIMULATION_ID), "gis_layers");
    let loaded = [];
    try {
      const snap = await getDocs(colRef);
      loaded = snap.docs
        .map((d) => parseLayerDoc(d.id, d.data()))
        .filter(Boolean);

      const defaults = defaultLayers();
      const missingDefaults = defaults.filter((baseLayer) => {
        const wantedFile = String(baseLayer.filename || "").trim().toLowerCase();
        return !loaded.some((row) => {
          const rowType = String(row?.type || "").toLowerCase();
          const rowFile = String(row?.filename || "").trim().toLowerCase();
          return rowType === baseLayer.type && rowFile === wantedFile;
        });
      });

      if (missingDefaults.length) {
        const bootstrapped = [];
        await Promise.all(
          missingDefaults.map(async (layer) => {
            try {
              await setDoc(doc(colRef, String(layer.id)), layer, { merge: true });
              bootstrapped.push(layer);
            } catch (err) {
              console.warn(`Failed to bootstrap default ${layer.type} layer:`, err);
            }
          })
        );
        if (bootstrapped.length) {
          loaded = [...loaded, ...bootstrapped];
        }
      }
    } catch (err) {
      console.warn("Failed to load gis_layers:", err);
    }
    if (!loaded.length) loaded = defaultLayers();

    applyGisLayerCatalog(loaded);
  }, [SIMULATION_ID, applyGisLayerCatalog]);

  useEffect(() => {
    loadGisLayers();
  }, [loadGisLayers]);

  useEffect(() => onSnapshot(
    collection(firestore, "model_builders", String(SIMULATION_ID), "gis_layers"),
    (snapshot) => {
      const rows = snapshot.docs.map((item) => parseLayerDoc(item.id, item.data())).filter(Boolean);
      if (rows.length) applyGisLayerCatalog(rows);
    },
    (error) => console.warn("Failed to subscribe to GIS layers:", error)
  ), [SIMULATION_ID, applyGisLayerCatalog]);

  useEffect(() => {
    if (!simulationIdSafe) {
      setLiveOperationBlocks([]);
      liveOperationStatusRef.current = null;
      return undefined;
    }

    const blocksRef = databaseRef(database, "model_builders/blocks");
    return onValue(blocksRef, (snapshot) => {
      const value = snapshot.val();
      const rows = value && typeof value === "object" ? Object.entries(value) : [];
      const relevant = rows.filter(([blockId, block]) =>
        operationBelongsToSimulation(blockId, block, simulationIdSafe)
      );
      const nextStatuses = new Map(
        relevant.map(([blockId, block]) => [blockId, latestOperationMessage(block?.message)])
      );
      const previousStatuses = liveOperationStatusRef.current;
      liveOperationStatusRef.current = nextStatuses;

      if (previousStatuses) {
        const completedSinceLastSnapshot = [...nextStatuses].some(([blockId, message]) => {
          const previous = previousStatuses.get(blockId);
          return previous && !isTerminalOperationMessage(previous) && isTerminalOperationMessage(message);
        });
        if (completedSinceLastSnapshot) loadGisLayers();
      }

      setLiveOperationBlocks(
        relevant
          .filter(([, block]) => {
            const message = latestOperationMessage(block?.message);
            return message && !isTerminalOperationMessage(message);
          })
          .map(([blockId, block]) => ({ blockId, kind: operationKind(blockId, block) }))
      );
    }, (error) => {
      console.warn("Failed to subscribe to builder data-pull operations:", error);
    });
  }, [simulationIdSafe, loadGisLayers]);

  const visibleOperationBlocks = useMemo(() => {
    const byId = new Map();
    [...operationBlocks, ...liveOperationBlocks].forEach((block) => byId.set(block.blockId, block));
    return [...byId.values()];
  }, [operationBlocks, liveOperationBlocks]);

  useEffect(() => {
    if (!selectedLayer?.id || !selectedLayer?.type) {
      clearAllLayerRows();
      return;
    }
    const type = selectedLayer.type;
    setActiveLayer(type);
    setActiveLayerByType((prev) => ({ ...prev, [type]: selectedLayer }));
    loadTypeRows(type, selectedLayer, { clearOthers: true });
  }, [selectedLayer, loadTypeRows, clearAllLayerRows]);

  configEditingRef.current = isEditingDestinations || isEditingStartTimes || isEditingHazardConfig || isEditingContraflow || isEditingBlockedRoad;

  const applyCustomConfigCatalog = useCallback((nextVars) => {
    setCustomConfigVars(nextVars);
    setActiveConfigVarName((current) => nextVars.some((row) => row.name === current)
      ? current : nextVars[0]?.name || "destinations");
  }, []);

  useEffect(() => {
    if (!simulationIdSafe) return undefined;
    customConfigMetadataRef.current = {};
    customConfigStorageRef.current = null;
    const unsubscribe = onSnapshot(
      collection(firestore, "model_builders", simulationIdSafe, "custom_config"),
      (snapshot) => {
        const firestoreConfig = Object.fromEntries(snapshot.docs.map((item) => [item.id, item.data() || {}]));
        customConfigMetadataRef.current = firestoreConfig;
        const nextVars = mergeCustomConfigVars(firestoreConfig, customConfigStorageRef.current || {});
        latestCustomConfigVarsRef.current = nextVars;
        if (!configEditingRef.current) applyCustomConfigCatalog(nextVars);
      },
      (error) => setConfigSaveState((current) => ({ ...current, error: String(error?.message || error) }))
    );
    return unsubscribe;
  }, [simulationIdSafe, applyCustomConfigCatalog]);

  const displayedConfigName = rightTab === "config"
    ? activeConfigVarName
    : destinationMapConfigName;

  useEffect(() => {
    if (!simulationIdSafe || !displayedConfigName) return undefined;
    let cancelled = false;
    fetchJsonFromStorage(`${configBase}/${CONFIG_FILE_NAME}`).then((storageConfig) => {
      if (cancelled || !storageConfig) return;
      customConfigStorageRef.current = storageConfig;
      const nextVars = mergeCustomConfigVars(customConfigMetadataRef.current, storageConfig);
      latestCustomConfigVarsRef.current = nextVars;
      if (!configEditingRef.current) applyCustomConfigCatalog(nextVars);
    });
    return () => { cancelled = true; };
  }, [simulationIdSafe, configBase, displayedConfigName, applyCustomConfigCatalog]);

  useEffect(() => {
    if (!configEditingRef.current && latestCustomConfigVarsRef.current) {
      applyCustomConfigCatalog(latestCustomConfigVarsRef.current);
    }
  }, [isEditingDestinations, isEditingStartTimes, isEditingHazardConfig, isEditingContraflow, isEditingBlockedRoad, applyCustomConfigCatalog]);

  const destinationMapContext = useMemo(() => {
    if (rightTab === "config") return effectiveConfigContext.type === "destination"
      && activeLayer === "nodes"
      && String(activeLayerId) === String(effectiveConfigContext.nodeLayerId)
        ? effectiveConfigContext : null;
    const row = customConfigVars.find((config) => config.type === "destination" && config.name === destinationMapConfigName);
    if (!row) return null;
    const configuredNodeLayerId = String(row.nodeLayerId || "");
    const nodeLayerId = nodeLayerOptions.some((layer) => String(layer.id) === configuredNodeLayerId)
      ? configuredNodeLayerId : String(nodeLayerOptions[0]?.id || "");
    if (activeLayer !== "nodes" || String(activeLayerId) !== nodeLayerId) return null;
    return {
      name: row.name,
      nodeLayerId,
      visibilityLinksLayerId: String(row.visibilityLinksLayerId || ""),
      destinationIds: new Set((row.destinationIds || []).map(String)),
    };
  }, [rightTab, effectiveConfigContext, customConfigVars, destinationMapConfigName, nodeLayerOptions, activeLayer, activeLayerId]);
  const isDestinationConfigVisible = !!destinationMapContext;
  const roadConfigLayerId = String(effectiveConfigContext.linksLayerId || linksLayerOptions[0]?.id || "");
  const isRoadConfigLayerActive = rightTab === "config" && activeLayer === "links"
    && !!roadConfigLayerId && String(activeLayerId) === roadConfigLayerId;
  const isContraflowConfigVisible = isRoadConfigLayerActive && effectiveConfigContext.type === "contraflow";
  const isBlockedRoadConfigVisible = isRoadConfigLayerActive && effectiveConfigContext.type === "blocked_road";
  const isRoadConfigVisible = isContraflowConfigVisible || isBlockedRoadConfigVisible;
  const mapTabIntentKeyRef = useRef("");
  useEffect(() => {
    if (rightTab !== "edit" && rightTab !== "config") {
      mapTabIntentKeyRef.current = "";
      return;
    }
    if (rightTab === "edit") {
      if (mapTabIntentKeyRef.current === "edit") return;
      const layer = gisLayers.find((row) => String(row.id) === String(activeLayerId))
        || gisLayers.find((row) => row.type === activeLayer)
        || gisLayers[0];
      if (!layer) return;
      mapTabIntentKeyRef.current = "edit";
      onManualLayerSelection(layer.id);
      return;
    }
    const { type, name, nodeLayerId, linksLayerId } = effectiveConfigContext;
    const layerId = type === "destination" ? nodeLayerId
      : type === "blocked_road" || type === "contraflow"
        ? linksLayerId || String(linksLayerOptions[0]?.id || "") : "";
    const key = `config:${name}:${type}:${layerId}`;
    if (mapTabIntentKeyRef.current === key) return;
    if (layerId && !layerById.has(String(layerId))) return;
    mapTabIntentKeyRef.current = key;
    setDestinationMapConfigName(type === "destination" ? name : "");
    if (layerId) onChangeLayerSelection(layerId);
  }, [rightTab, gisLayers, activeLayerId, activeLayer, effectiveConfigContext, linksLayerOptions, layerById, onManualLayerSelection, onChangeLayerSelection]);

  const contraflowDestinationConfig = useMemo(
    () => customConfigVars.find((row) =>
      row.type === "destination" && row.name === effectiveConfigContext.destinationConfigName
    ) || null,
    [customConfigVars, effectiveConfigContext.destinationConfigName]
  );

  const configNodeLayerId = isDestinationConfigVisible
    ? String(destinationMapContext.nodeLayerId || "")
    : isContraflowConfigVisible && contraflowDestinationConfig
      ? nodeLayerOptions.some((layer) => String(layer.id) === String(contraflowDestinationConfig.nodeLayerId || ""))
        ? String(contraflowDestinationConfig.nodeLayerId)
        : String(nodeLayerOptions[0]?.id || "")
      : "";

  useEffect(() => {
    if (!configNodeLayerId) {
      setConfigNodes([]);
      return;
    }
    const layerDoc = nodeLayerOptions.find(
      (row) => String(row.id) === configNodeLayerId
    );
    if (!layerDoc) {
      setConfigNodes([]);
      return;
    }
    let cancelled = false;
    setConfigNodes([]);
    readLayerData(layerDoc)
      .then((rows) => {
        if (cancelled) return;
        setConfigNodes(rows);
      })
      .catch(() => {
        if (cancelled) return;
        setConfigNodes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    configNodeLayerId,
    nodeLayerOptions,
    readLayerData,
  ]);

  useEffect(() => {
    if (!isRoadConfigVisible) {
      setContraflowLinks([]);
      return;
    }
    const layerDoc = linksLayerOptions.find(
      (row) => String(row.id) === String(effectiveConfigContext.linksLayerId)
    );
    if (!layerDoc) {
      setContraflowLinks([]);
      return;
    }
    let cancelled = false;
    readLayerData(layerDoc).then((rows) => {
      if (!cancelled) setContraflowLinks(rows);
    }).catch(() => {
      if (!cancelled) setContraflowLinks([]);
    });
    return () => { cancelled = true; };
  }, [isRoadConfigVisible, effectiveConfigContext.linksLayerId, linksLayerOptions, readLayerData]);

  useEffect(() => {
    if (!isDestinationConfigVisible) {
      setDestinationVisibilityLinks([]);
      return;
    }
    const layerId = String(destinationMapContext.visibilityLinksLayerId || "").trim();
    if (!layerId) {
      setDestinationVisibilityLinks([]);
      return;
    }
    const layerDoc = linksLayerOptions.find((row) => String(row.id) === layerId);
    if (!layerDoc) {
      setDestinationVisibilityLinks([]);
      return;
    }
    let cancelled = false;
    readLayerData(layerDoc)
      .then((rows) => {
        if (!cancelled) setDestinationVisibilityLinks(rows);
      })
      .catch(() => {
        if (!cancelled) setDestinationVisibilityLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    isDestinationConfigVisible,
    destinationMapContext?.visibilityLinksLayerId,
    linksLayerOptions,
    readLayerData,
  ]);

  const resolveLayerDocForType = useCallback(
    (layerType) => {
      const type = String(layerType || "").trim().toLowerCase();
      if (!type) return null;
      if (selectedLayer?.type !== type) return null;
      return selectedLayer?.filename ? selectedLayer : null;
    },
    [selectedLayer]
  );

  const saveLayerEdits = useCallback(
    async (layer, editsById = {}) => {
      const layerDoc = resolveLayerDocForType(layer);
      const fileName = String(layerDoc?.filename || "");
      if (!fileName) {
        setLayerSaveState({
          isSaving: false,
          error: `Select a ${layer} layer before saving edits.`,
          success: "",
        });
        return false;
      }

      const editIds = Object.keys(editsById || {});
      if (!editIds.length) return false;

      setLayerSaveState({ isSaving: true, error: "", success: "" });

      try {
        const path = `${inputsBase}/${fileName}`;
        const csvText = await fetchCsvTextFromStorage(path);
        if (!csvText) throw new Error(`Could not load ${fileName} from storage.`);

        const parsed = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
        });

        const rows = Array.isArray(parsed?.data) ? parsed.data : [];
        const fields = Array.isArray(parsed?.meta?.fields) ? [...parsed.meta.fields] : [];

        if (!rows.length || !fields.length) throw new Error(`No rows found in ${fileName}.`);

        let changed = 0;
        let updatedCsv = "";

        if (layer === "other") {
          const indexedRows = rows.map((row, idx) => ({ ...row, _row_id: String(idx + 1) }));
          const lowerToField = {};
          fields.forEach((f) => {
            lowerToField[String(f).toLowerCase()] = f;
          });

          indexedRows.forEach((row) => {
            const id = String(row?._row_id ?? "");
            const patch = editsById[id];
            if (!patch) return;

            Object.entries(patch).forEach(([rawKey, rawVal]) => {
              if (rawKey === "_row_id") return;
              const keyLower = String(rawKey).toLowerCase();
              const targetHeader = lowerToField[keyLower] || rawKey;
              if (!fields.includes(targetHeader)) {
                fields.push(targetHeader);
                lowerToField[keyLower] = targetHeader;
              }
              row[targetHeader] = rawVal;
            });
            changed += 1;
          });

          if (!changed) throw new Error("No matching selected rows were found to update.");
          const finalRows = indexedRows.map((row) => {
            const out = { ...row };
            delete out._row_id;
            return out;
          });
          updatedCsv = Papa.unparse({
            fields,
            data: finalRows,
          });
        } else {
          const lowerToField = {};
          fields.forEach((f) => {
            lowerToField[String(f).toLowerCase()] = f;
          });

          const idHeader = (LAYER_ID_KEYS[layer] || [])
            .map((k) => lowerToField[String(k).toLowerCase()])
            .find(Boolean);

          if (!idHeader) throw new Error(`Could not find ID column in ${fileName}.`);

          rows.forEach((row) => {
            const id = String(row?.[idHeader] ?? "").trim();
            const patch = editsById[id];
            if (!patch) return;

            Object.entries(patch).forEach(([rawKey, rawVal]) => {
              const keyLower = String(rawKey).toLowerCase();
              const targetHeader = lowerToField[keyLower] || rawKey;
              if (!fields.includes(targetHeader)) {
                fields.push(targetHeader);
                lowerToField[keyLower] = targetHeader;
              }
              row[targetHeader] = rawVal;
            });
            changed += 1;
          });

          if (!changed) throw new Error("No matching selected rows were found to update.");
          updatedCsv = Papa.unparse({
            fields,
            data: rows,
          });
        }

        await uploadString(storageRef(storage, path), updatedCsv, "raw", {
          contentType: contentTypeForFileName(fileName),
        });

        await refreshSelectedLayer();

        setLayerSaveState({
          isSaving: false,
          error: "",
          success: `Saved ${changed} ${layer.slice(0, -1)} record${changed === 1 ? "" : "s"} to cloud storage.`,
        });

        setChatLog((prev) => [
          ...prev,
          {
            role: "system",
            text: `Saved ${changed} ${layer} row${changed === 1 ? "" : "s"} to ${fileName}.`,
            ts: Date.now(),
          },
        ]);
        return true;
      } catch (err) {
        setLayerSaveState({
          isSaving: false,
          error: String(err?.message || err),
          success: "",
        });
        return false;
      }
    },
    [resolveLayerDocForType, inputsBase, refreshSelectedLayer]
  );

  const deleteLayerRow = useCallback(
    async (layer, id) => {
      if (layer !== "links" && layer !== "nodes" && layer !== "parcels" && layer !== "other") return false;
      const rowId = String(id ?? "");
      if (!rowId) return false;
      const rowLabel =
        layer === "nodes" ? "node" : layer === "parcels" ? "parcel" : layer === "other" ? "row" : "link";

      const shouldDelete = window.confirm(
        `Delete ${rowLabel} ${rowId}? This will remove it from the selected ${layer} layer file.`
      );
      if (!shouldDelete) return false;

      setLayerSaveState({ isSaving: true, error: "", success: "" });
      try {
        const layerDoc = resolveLayerDocForType(layer);
        const fileName = String(layerDoc?.filename || "");
        if (!fileName) throw new Error(`Select a ${layer} layer before deleting rows.`);
        const path = `${inputsBase}/${fileName}`;
        const csvText = await fetchCsvTextFromStorage(path);
        if (!csvText) throw new Error(`Could not load ${fileName} from storage.`);

        const parsed = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
        });
        const rows = Array.isArray(parsed?.data) ? parsed.data : [];
        const fields = Array.isArray(parsed?.meta?.fields) ? [...parsed.meta.fields] : [];
        if (!fields.length) throw new Error(`No headers found in ${fileName}.`);
        let nextRows = [];
        if (layer === "other") {
          const rowIndex = Number(rowId) - 1;
          if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= rows.length) {
            throw new Error(`Row ${rowId} was not found in ${fileName}.`);
          }
          nextRows = rows.filter((_, idx) => idx !== rowIndex);
        } else {
          const lowerToField = {};
          fields.forEach((f) => {
            lowerToField[String(f).toLowerCase()] = f;
          });
          const idHeader = (LAYER_ID_KEYS[layer] || [])
            .map((k) => lowerToField[String(k).toLowerCase()])
            .find(Boolean);
          if (!idHeader) throw new Error(`Could not find ID column in ${fileName}.`);

          nextRows = rows.filter((row) => !idsEqual(row?.[idHeader], rowId));
          if (nextRows.length === rows.length) {
            throw new Error(`${rowLabel[0].toUpperCase() + rowLabel.slice(1)} ${rowId} was not found in ${fileName}.`);
          }
        }

        const updatedCsv = Papa.unparse({
          fields,
          data: nextRows,
        });
        await uploadString(storageRef(storage, path), updatedCsv, "raw", {
          contentType: contentTypeForFileName(fileName),
        });

        await refreshSelectedLayer();
        if (layer === "links") {
          setSelectedLinkIds((prev) => {
            const next = new Set(prev);
            next.delete(rowId);
            return next;
          });
        } else if (layer === "nodes") {
          setSelectedNodeIds((prev) => {
            const next = new Set(prev);
            next.delete(rowId);
            return next;
          });
        } else if (layer === "parcels") {
          setSelectedParcelIds((prev) => {
            const next = new Set(prev);
            next.delete(rowId);
            return next;
          });
        } else {
          setSelectedOtherIds((prev) => {
            const next = new Set(prev);
            next.delete(rowId);
            return next;
          });
        }
        setLayerSaveState({
          isSaving: false,
          error: "",
          success: `Deleted ${rowLabel} ${rowId} from cloud storage.`,
        });
        setChatLog((prev) => [
          ...prev,
          {
            role: "system",
            text: `Deleted ${rowLabel} ${rowId} from ${fileName}.`,
            ts: Date.now(),
          },
        ]);
        return true;
      } catch (err) {
        setLayerSaveState({
          isSaving: false,
          error: String(err?.message || err),
          success: "",
        });
        return false;
      }
    },
    [resolveLayerDocForType, inputsBase, refreshSelectedLayer]
  );

  const deleteSelectedLayerRows = useCallback(
    async (layer, ids = []) => {
      if (layer !== "links" && layer !== "nodes" && layer !== "parcels") return false;
      const targetIds = Array.from(
        new Set((ids || []).map((v) => String(v ?? "").trim()).filter(Boolean))
      );
      if (!targetIds.length) return false;

      const rowLabel = layer === "nodes" ? "node" : layer === "parcels" ? "parcel" : "link";
      const shouldDelete = window.confirm(
        `Delete all selected ${layer} (${targetIds.length})? This will remove them from the selected ${layer} layer file.`
      );
      if (!shouldDelete) return false;

      setLayerSaveState({ isSaving: true, error: "", success: "" });
      try {
        const layerDoc = resolveLayerDocForType(layer);
        const fileName = String(layerDoc?.filename || "");
        if (!fileName) throw new Error(`Select a ${layer} layer before deleting rows.`);
        const path = `${inputsBase}/${fileName}`;
        const csvText = await fetchCsvTextFromStorage(path);
        if (!csvText) throw new Error(`Could not load ${fileName} from storage.`);

        const parsed = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
        });
        const rows = Array.isArray(parsed?.data) ? parsed.data : [];
        const fields = Array.isArray(parsed?.meta?.fields) ? [...parsed.meta.fields] : [];
        if (!fields.length) throw new Error(`No headers found in ${fileName}.`);

        const lowerToField = {};
        fields.forEach((f) => {
          lowerToField[String(f).toLowerCase()] = f;
        });
        const idHeader = (LAYER_ID_KEYS[layer] || [])
          .map((k) => lowerToField[String(k).toLowerCase()])
          .find(Boolean);
        if (!idHeader) throw new Error(`Could not find ID column in ${fileName}.`);

        let deleted = 0;
        const nextRows = rows.filter((row) => {
          const remove = targetIds.some((targetId) => idsEqual(row?.[idHeader], targetId));
          if (remove) deleted += 1;
          return !remove;
        });
        if (!deleted) throw new Error(`No selected ${layer} were found in ${fileName}.`);

        const updatedCsv = Papa.unparse({
          fields,
          data: nextRows,
        });
        await uploadString(storageRef(storage, path), updatedCsv, "raw", {
          contentType: contentTypeForFileName(fileName),
        });

        await refreshSelectedLayer();

        const dropDeletedFromSet = (prev) => {
          const next = new Set();
          prev.forEach((value) => {
            const shouldKeep = !targetIds.some((targetId) => idsEqual(value, targetId));
            if (shouldKeep) next.add(value);
          });
          return next;
        };
        if (layer === "links") {
          setSelectedLinkIds(dropDeletedFromSet);
        } else if (layer === "nodes") {
          setSelectedNodeIds(dropDeletedFromSet);
        } else {
          setSelectedParcelIds(dropDeletedFromSet);
        }

        setLayerSaveState({
          isSaving: false,
          error: "",
          success: `Deleted ${deleted} ${rowLabel} record${deleted === 1 ? "" : "s"} from cloud storage.`,
        });
        setChatLog((prev) => [
          ...prev,
          {
            role: "system",
            text: `Deleted ${deleted} ${layer} row${deleted === 1 ? "" : "s"} from ${fileName}.`,
            ts: Date.now(),
          },
        ]);
        return true;
      } catch (err) {
        setLayerSaveState({
          isSaving: false,
          error: String(err?.message || err),
          success: "",
        });
        return false;
      }
    },
    [resolveLayerDocForType, inputsBase, refreshSelectedLayer]
  );

  const saveSelectedLayerMeta = useCallback(async (layerId, updates = {}) => {
    const id = String(layerId || "");
    if (!id) return false;
    const current = layerById.get(id);
    if (!current) return false;

    const nextName = String(updates?.name ?? current.name ?? "").trim();
    if (!nextName) {
      setLayerSaveState({
        isSaving: false,
        error: "Layer name is required.",
        success: "",
      });
      return false;
    }

    const nextLayer = {
      id: current.id,
      name: nextName,
      filename: current.filename,
      type: current.type,
    };

    setLayerSaveState({ isSaving: true, error: "", success: "" });
    try {
      const layerRef = doc(
        firestore,
        "model_builders",
        String(SIMULATION_ID),
        "gis_layers",
        String(nextLayer.id)
      );
      await setDoc(layerRef, nextLayer, { merge: true });

      setGisLayers((prev) =>
        (prev || [])
          .map((row) => (String(row.id) === String(nextLayer.id) ? nextLayer : row))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      );
      setActiveLayerByType((prev) => {
        if (String(prev?.[nextLayer.type]?.id || "") !== String(nextLayer.id)) return prev;
        return { ...prev, [nextLayer.type]: nextLayer };
      });
      setLayerSaveState({
        isSaving: false,
        error: "",
        success: `Layer renamed to "${nextLayer.name}".`,
      });
      return true;
    } catch (err) {
      setLayerSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    }
  }, [layerById, SIMULATION_ID]);

  const createLayerFromText = useCallback(
    async ({ name, type, sourceText, sourceLabel = "source" }) => {
      const layerName = String(name || "").trim();
      const layerType = normalizeCreateLayerType(type || "other");
      if (!layerName) {
        setLayerSaveState({
          isSaving: false,
          error: "Layer name is required.",
          success: "",
        });
        return false;
      }
      if (!isCreateLayerTypeSupported(layerType)) {
        setLayerSaveState({
          isSaving: false,
          error: "Unsupported layer type.",
          success: "",
        });
        return false;
      }
      if (!String(sourceText || "").trim()) {
        setLayerSaveState({
          isSaving: false,
          error: layerType === "polygon" ? "Provide polygon GeoJSON data." : "Provide CSV data.",
          success: "",
        });
        return false;
      }

      setLayerSaveState({ isSaving: true, error: "", success: "" });
      try {
        const fileText = String(sourceText || "");
        let uploadText = fileText;
        let polygonSanitizeStats = null;

        if (layerType === "polygon") {
          const sanitized = sanitizePolygonGeoJsonUpload(fileText);
          if (!sanitized?.ok) {
            throw new Error(String(sanitized?.error || "Invalid polygon GeoJSON file."));
          }
          uploadText = String(sanitized.text || "");
          polygonSanitizeStats = sanitized.stats || null;
        }

        if (layerType !== "polygon") {
          const parsedCsv = Papa.parse(fileText, papaOpts);
          const headers = Array.isArray(parsedCsv?.meta?.fields) ? parsedCsv.meta.fields : [];
          const headerError = validateCreateLayerHeaders(layerType, headers);
          if (headerError) throw new Error(headerError);
        }
        const parsedRows = await parseRowsForTypeAsync(layerType, uploadText);
        if (!parsedRows.length) {
          throw new Error(noValidRowsMessageForLayerType(layerType));
        }
        const colRef = collection(firestore, "model_builders", String(SIMULATION_ID), "gis_layers");
        const newRef = doc(colRef);
        const nextFileName = `${String(newRef.id)}${createLayerFileExtensionForType(layerType)}`;
        const nextLayer = {
          id: String(newRef.id),
          name: layerName,
          filename: nextFileName,
          type: layerType,
        };

        await uploadString(storageRef(storage, `${inputsBase}/${nextLayer.filename}`), uploadText, "raw", {
          contentType: contentTypeForFileName(nextLayer.filename),
        });
        await setDoc(newRef, nextLayer, { merge: true });

        setGisLayers((prev) => [...prev.filter((row) => row.id !== nextLayer.id), nextLayer]
          .sort((a, b) => String(a.name).localeCompare(String(b.name))));
        activeLayerIdRef.current = nextLayer.id;
        activeLayerRef.current = nextLayer.type;
        setActiveLayerId(nextLayer.id);
        setActiveLayer(nextLayer.type);
        setActiveLayerByType((prev) => ({ ...prev, [layerType]: nextLayer }));
        clearAllLayerRows();
        applyRowsForType(layerType, parsedRows);
        clearSelections();
        setLayerSaveState({
          isSaving: false,
          error: "",
          success:
            layerType === "polygon" && polygonSanitizeStats
              ? `Created ${layerType} layer "${nextLayer.name}" from ${sourceLabel}. Cleaned ${Number(
                  polygonSanitizeStats.removedDuplicateVertices || 0
                )} duplicate vertices, closed ${Number(
                  polygonSanitizeStats.closedRings || 0
                )} rings, removed ${Number(
                  polygonSanitizeStats.removedInvalidRings || 0
                )} invalid rings.`
              : `Created ${layerType} layer "${nextLayer.name}" from ${sourceLabel}.`,
        });
        return true;
      } catch (err) {
        setLayerSaveState({
          isSaving: false,
          error: String(err?.message || err),
          success: "",
        });
        return false;
      }
    },
    [
      SIMULATION_ID,
      inputsBase,
      papaOpts,
      parseRowsForTypeAsync,
      clearSelections,
      applyRowsForType,
      clearAllLayerRows,
    ]
  );

  const createLayerFromUpload = useCallback(
    async ({ name, file, type }) => {
      const inputFile = file || null;
      const layerType = normalizeCreateLayerType(type || "other");
      if (!inputFile) {
        setLayerSaveState({
          isSaving: false,
          error: layerType === "polygon" ? "Upload a GeoJSON file." : "Upload a CSV file.",
          success: "",
        });
        return false;
      }
      const fileTypeError = validateCreateLayerFileName(layerType, inputFile.name || "");
      if (fileTypeError) {
        setLayerSaveState({
          isSaving: false,
          error: fileTypeError,
          success: "",
        });
        return false;
      }
      return createLayerFromText({
        name,
        type: layerType,
        sourceText: await inputFile.text(),
        sourceLabel: "uploaded file",
      });
    },
    [createLayerFromText]
  );

  const downloadSelectedLayerData = useCallback(async () => {
    if (!selectedLayer?.filename) return false;
    try {
      const csvText = await fetchCsvTextFromStorage(`${inputsBase}/${selectedLayer.filename}`);
      if (csvText == null) throw new Error(`Could not load ${selectedLayer.filename} from storage.`);

      const blob = new Blob([csvText], { type: contentTypeForFileName(selectedLayer.filename) });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = selectedLayer.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setLayerSaveState((prev) => ({
        ...prev,
        error: "",
        success: `Downloaded ${selectedLayer.filename}.`,
      }));
      return true;
    } catch (err) {
      setLayerSaveState((prev) => ({
        ...prev,
        error: String(err?.message || err),
        success: "",
      }));
      return false;
    }
  }, [selectedLayer, inputsBase]);

  const downloadSelectedPolygonFeatures = useCallback(() => {
    const ids = new Set([...selectedPolygonIds].map((id) => String(id)));
    if (!ids.size) return false;

    const closeRing = (coords) => {
      const ring = Array.isArray(coords) ? coords.filter((pt) => Array.isArray(pt) && pt.length >= 2) : [];
      if (ring.length < 3) return null;
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (Number(first[0]) !== Number(last[0]) || Number(first[1]) !== Number(last[1])) {
        ring.push([...first]);
      }
      return ring.length >= 4 ? ring : null;
    };

    const features = (polygonRows || [])
      .filter((row) => ids.has(String(row?.polygon_id || "")))
      .map((row) => {
        const coordinates = (Array.isArray(row?.paths) ? row.paths : [])
          .map((ring) =>
            closeRing(
              (Array.isArray(ring) ? ring : [])
                .map((pt) => {
                  const lng = Number(pt?.lng);
                  const lat = Number(pt?.lat);
                  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
                  return [lng, lat];
                })
                .filter(Boolean)
            )
          )
          .filter(Boolean);

        if (!coordinates.length) return null;

        return {
          type: "Feature",
          id: String(row?.polygon_id || ""),
          properties: {
            polygon_id: String(row?.polygon_id || ""),
            feature_name: String(row?.feature_name || row?.polygon_id || ""),
            source_layer_id: String(activeLayerId || ""),
            source_layer_name: String(selectedLayer?.name || activeLayer || "polygon"),
            selected_color: selectedPolygonColor,
          },
          geometry: {
            type: "Polygon",
            coordinates,
          },
        };
      })
      .filter(Boolean);

    if (!features.length) {
      setLayerSaveState((prev) => ({
        ...prev,
        error: "No selected polygon features can be exported.",
        success: "",
      }));
      return false;
    }

    const geojson = {
      type: "FeatureCollection",
      features,
    };
    const baseName = String(selectedLayer?.filename || selectedLayer?.id || activeLayerId || "polygon")
      .replace(/\.(geojson|json)$/i, "")
      .replace(/[^\w.-]+/g, "_");
    const fileName = `${baseName}_selected_${features.length}.geojson`;
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: "application/geo+json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setLayerSaveState((prev) => ({
      ...prev,
      error: "",
      success: `Downloaded ${features.length} selected polygon feature${features.length === 1 ? "" : "s"}.`,
    }));
    return true;
  }, [activeLayer, activeLayerId, polygonRows, selectedLayer, selectedPolygonColor, selectedPolygonIds]);

  const duplicateLayerAndSelect = useCallback(
    async (sourceLayer, options = {}) => {
      if (!sourceLayer?.id || !sourceLayer?.filename || !sourceLayer?.type) {
        throw new Error("No active layer available to duplicate.");
      }
      const nameSuffix = String(options?.nameSuffix || "Copy").trim() || "Copy";

      const srcPath = `${inputsBase}/${sourceLayer.filename}`;
      const srcText = await fetchCsvTextFromStorage(srcPath);
      if (srcText == null) throw new Error(`Could not load ${sourceLayer.filename} from storage.`);

      const colRef = collection(firestore, "model_builders", String(SIMULATION_ID), "gis_layers");
      const newRef = doc(colRef);
      const nextFileName = duplicatedFileNameFromId(newRef.id, sourceLayer.filename);
      if (!nextFileName) throw new Error("Could not compute destination filename for duplicated layer.");
      const destPath = `${inputsBase}/${nextFileName}`;
      await uploadString(storageRef(storage, destPath), srcText, "raw", {
        contentType: contentTypeForFileName(nextFileName),
      });

      const nextLayer = {
        id: String(newRef.id),
        name: `${sourceLayer.name} ${nameSuffix}`.trim(),
        filename: nextFileName,
        type: sourceLayer.type,
      };
      await setDoc(newRef, nextLayer, { merge: true });

      setGisLayers((prev) => [...prev.filter((row) => row.id !== nextLayer.id), nextLayer]
        .sort((a, b) => String(a.name).localeCompare(String(b.name))));
      activeLayerIdRef.current = nextLayer.id;
      activeLayerRef.current = nextLayer.type;
      setActiveLayerId(nextLayer.id);
      setActiveLayer(nextLayer.type);
      setActiveLayerByType((prev) => ({ ...prev, [nextLayer.type]: nextLayer }));
      const rows = await readLayerData(nextLayer);
      clearAllLayerRows();
      applyRowsForType(nextLayer.type, rows);
      clearSelections();
      return nextLayer;
    },
    [inputsBase, SIMULATION_ID, readLayerData, clearAllLayerRows, applyRowsForType, clearSelections]
  );

  const duplicateSelectedLayer = useCallback(async () => {
    if (!selectedLayer?.id || !selectedLayer?.filename || !selectedLayer?.type) return false;
    setLayerSaveState({ isSaving: true, error: "", success: "" });
    try {
      const nextLayer = await duplicateLayerAndSelect(selectedLayer, { nameSuffix: "Copy" });

      setLayerSaveState({
        isSaving: false,
        error: "",
        success: `Duplicated layer "${selectedLayer.name}" as "${nextLayer.name}".`,
      });
      return true;
    } catch (err) {
      setLayerSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    }
  }, [selectedLayer, duplicateLayerAndSelect]);

  const deleteSelectedLayer = useCallback(async () => {
    const layer = selectedLayer;
    if (!layer?.id || !layer?.filename || !layer?.type) return false;
    const layerId = String(layer.id || "").trim();
    if (!layerId) return false;
    if (NON_DELETABLE_LAYER_IDS.has(layerId)) {
      setLayerSaveState({
        isSaving: false,
        error: "Default layers cannot be deleted.",
        success: "",
      });
      return false;
    }

    const shouldDelete = window.confirm(
      `Delete layer "${layer.name}"?\n\nThis removes the layer metadata and its uploaded file from storage.`
    );
    if (!shouldDelete) return false;

    setLayerSaveState({ isSaving: true, error: "", success: "" });
    try {
      const layerPath = `${inputsBase}/${layer.filename}`;
      try {
        await deleteObject(storageRef(storage, layerPath));
      } catch (storageErr) {
        const code = String(storageErr?.code || "");
        const msg = String(storageErr?.message || "");
        const isMissing =
          code === "storage/object-not-found" || /object-not-found|does not exist/i.test(msg);
        if (!isMissing) throw storageErr;
      }

      await deleteDoc(
        doc(
          firestore,
          "model_builders",
          String(SIMULATION_ID),
          "gis_layers",
          String(layerId)
        )
      );

      const remainingLayers = (gisLayers || [])
        .filter((row) => String(row.id) !== layerId)
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));

      const replacementSameType =
        remainingLayers.find((row) => String(row.type) === String(layer.type)) || null;
      const fallbackLayer = replacementSameType || remainingLayers[0] || null;

      setGisLayers(remainingLayers);
      setActiveLayerByType((prev) => {
        const next = { ...prev, [layer.type]: replacementSameType };
        if (fallbackLayer) next[fallbackLayer.type] = fallbackLayer;
        return next;
      });
      clearSelections();

      if (fallbackLayer) {
        setActiveLayerId(String(fallbackLayer.id));
        setActiveLayer(String(fallbackLayer.type));
      } else {
        setActiveLayerId("");
        clearAllLayerRows();
      }

      setLayerSaveState({
        isSaving: false,
        error: "",
        success: `Deleted layer "${layer.name}".`,
      });
      return true;
    } catch (err) {
      setLayerSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    }
  }, [selectedLayer, inputsBase, SIMULATION_ID, gisLayers, clearSelections, clearAllLayerRows]);

  const startFilterLayer = useCallback(() => {
    if (!selectedLayer?.id || !selectedLayer?.filename) return;
    if (!["links", "nodes", "parcels"].includes(String(selectedLayer.type || ""))) return;
    setIsFilteringLayer(true);
    setSelectedFilterPolygonLayerIds([]);
    setFilterPolygonOverlayRows([]);
    setLayerSaveState({ isSaving: false, error: "", success: "" });
  }, [selectedLayer]);

  const cancelFilterLayer = useCallback(() => {
    setIsFilteringLayer(false);
    setSelectedFilterPolygonLayerIds([]);
    setFilterPolygonOverlayRows([]);
    setLayerSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, []);

  const toggleFilterPolygonLayer = useCallback((layerId) => {
    const wanted = String(layerId || "").trim();
    if (!wanted) return;
    setSelectedFilterPolygonLayerIds((prev) => {
      const current = new Set((prev || []).map((id) => String(id)));
      if (current.has(wanted)) current.delete(wanted);
      else current.add(wanted);
      return [...current];
    });
  }, []);

  const saveFilterLayer = useCallback(async () => {
    if (!selectedLayer?.id || !selectedLayer?.filename || !selectedLayer?.type) return false;
    const baseType = String(selectedLayer.type || "");
    const targetLayerId = String(selectedLayer.id || "");
    const targetLayerType = String(selectedLayer.type || "");
    const targetLayerName = String(selectedLayer.name || selectedLayer.id || "layer");
    if (!["links", "nodes", "parcels"].includes(baseType)) {
      setLayerSaveState({
        isSaving: false,
        error: "Filtering is only supported for links, nodes, or parcels layers.",
        success: "",
      });
      return false;
    }

    const zoneIds = [...new Set((selectedFilterPolygonLayerIds || []).map((id) => String(id).trim()).filter(Boolean))];
    if (!zoneIds.length) {
      setLayerSaveState({
        isSaving: false,
        error: "Select at least one polygon layer to filter by.",
        success: "",
      });
      return false;
    }

    const zoneLayers = zoneIds
      .map((id) => layerById.get(id))
      .filter((row) => row?.type === "polygon" && row?.filename);
    if (zoneLayers.length !== zoneIds.length) {
      setLayerSaveState({
        isSaving: false,
        error: "One or more selected polygon layers are invalid.",
        success: "",
      });
      return false;
    }

    const toInputsGcsUri = (fileName) => `${GCS_BUCKET_URI}/${inputsBase}/${String(fileName || "").replace(/^\/+/, "")}`;

    setLayerSaveState({ isSaving: true, error: "", success: "" });
    try {
      const layerUri = toInputsGcsUri(selectedLayer.filename);
      const zonesUris = zoneLayers.map((row) => toInputsGcsUri(row.filename));
      const metadataUri = `${GCS_BUCKET_URI}/${inputsBase}/filter_metadata/${targetLayerId}_${Date.now()}.json`;

      const res = await cloudRunFilterLayerByPolygons({
        layer_type: baseType,
        input_layer_gcs_uri: layerUri,
        zones_gcs_uris: zonesUris,
        output_layer_gcs_uri: layerUri,
        output_metadata_gcs_uri: metadataUri,
      });
      if (!res?.ok) {
        throw new Error(String(res?.error || "Layer filter request failed."));
      }

      setIsFilteringLayer(false);
      setSelectedFilterPolygonLayerIds([]);
      setFilterPolygonOverlayRows([]);

      const stillSameLayer =
        String(activeLayerIdRef.current || "") === targetLayerId &&
        String(activeLayerRef.current || "") === targetLayerType;
      if (stillSameLayer) {
        await refreshSelectedLayer();
      }

      setLayerSaveState({
        isSaving: false,
        error: "",
        success: stillSameLayer
          ? `Filtered layer "${targetLayerName}".`
          : `Filtered layer "${targetLayerName}". Current selection changed, so no refresh was applied.`,
      });
      return true;
    } catch (err) {
      setLayerSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    }
  }, [
    selectedLayer,
    selectedFilterPolygonLayerIds,
    layerById,
    inputsBase,
    refreshSelectedLayer,
  ]);

  const selectOppositeLink = useCallback((linkId) => {
    const id = String(linkId ?? "");
    if (!id) return;
    const row = (links || []).find((l) => String(l?.link_id) === id);
    if (!row) return;

    const start = getLinkStartNodeId(row);
    const end = getLinkEndNodeId(row);
    if (!start || !end) {
      setLayerSaveState({
        isSaving: false,
        error: `Link ${id} is missing start/end node IDs.`,
        success: "",
      });
      return;
    }

    const opposite = (links || []).find((l) => {
      if (String(l?.link_id) === id) return false;
      return getLinkStartNodeId(l) === end && getLinkEndNodeId(l) === start;
    });

    if (!opposite) {
      setLayerSaveState({
        isSaving: false,
        error: `No opposite link found for ${id}.`,
        success: "",
      });
      return;
    }

    const oppositeId = String(opposite.link_id);
    setSelectedLinkIds((prev) => {
      const next = new Set(prev);
      next.add(oppositeId);
      return next;
    });
    centerSelectionOnMap("links", oppositeId);
    setLayerSaveState({
      isSaving: false,
      error: "",
      success: `Selected opposite link ${oppositeId}.`,
    });
  }, [links, centerSelectionOnMap]);

  const restoreEndpointSnapshot = useCallback((endpoint) => {
    if (addLinkDraftMode !== "ready") return false;
    const snap = addLinkDraftInfo?.endpointSnapshots?.[endpoint];
    if (!snap) return false;

    setLinks((snap.links || []).map((l) => ({ ...l, path: Array.isArray(l.path) ? l.path.map((p) => ({ ...p })) : l.path })));
    setNodes((snap.nodes || []).map((n) => ({ ...n })));
    setAddLinkDraftInfo((prevDraft) => ({
      ...prevDraft,
      endpointNodeIds: { ...(snap.endpointNodeIds || prevDraft?.endpointNodeIds) },
      activeDraftEndpoints: { ...(snap.activeDraftEndpoints || prevDraft?.activeDraftEndpoints) },
      endpointSnapshots: {
        ...(prevDraft?.endpointSnapshots || {}),
        [endpoint]: null,
      },
    }));
    return true;
  }, [addLinkDraftMode, addLinkDraftInfo]);

  const connectDraftToNearestNode = useCallback((selection = "both") => {
    if (addLinkDraftMode !== "ready") return;
    if (selection === "start" || selection === "both") restoreEndpointSnapshot("start");
    if (selection === "end" || selection === "both") restoreEndpointSnapshot("end");

    const draft = addLinkDraftInfo;
    if (!draft?.linkId) return false;
    const linkId = String(draft.linkId);
    const draftStartId = String(draft.draftNodeIds?.start || "");
    const draftEndId = String(draft.draftNodeIds?.end || "");
    const draftNodeIdSet = new Set([draftStartId, draftEndId]);
    const nonDraftCandidates = (nodes || []).filter(
      (n) => !draftNodeIdSet.has(String(n?.node_id)) && !isDraftLikeRow(n)
    );
    const existingNodes = nonDraftCandidates.length
      ? nonDraftCandidates
      : (nodes || []).filter((n) => !draftNodeIdSet.has(String(n?.node_id)));
    if (!existingNodes.length) {
      setLayerSaveState({
        isSaving: false,
        error: "No existing graph nodes available to connect to.",
        success: "",
      });
      return false;
    }
    const linkRowPre = (links || []).find((l) => String(l?.link_id) === linkId);
    if (!linkRowPre?.path?.length) return false;
    const shouldStart = selection === "start" || selection === "both";
    const shouldEnd = selection === "end" || selection === "both";
    const canApplyStart = shouldStart && !!draft.activeDraftEndpoints?.start;
    const canApplyEnd = shouldEnd && !!draft.activeDraftEndpoints?.end;
    if (!canApplyStart && !canApplyEnd) return false;

    setAddLinkDraftInfo((prevDraft) => {
      if (!prevDraft?.linkId) return prevDraft;

      const linkId = String(prevDraft.linkId);
      const draftStartId = String(prevDraft.draftNodeIds?.start || "");
      const draftEndId = String(prevDraft.draftNodeIds?.end || "");
      const draftNodeIdSet = new Set([draftStartId, draftEndId]);
      const nonDraftCandidates = (nodes || []).filter(
        (n) => !draftNodeIdSet.has(String(n?.node_id)) && !isDraftLikeRow(n)
      );
      const existingNodes = nonDraftCandidates.length
        ? nonDraftCandidates
        : (nodes || []).filter((n) => !draftNodeIdSet.has(String(n?.node_id)));
      if (!existingNodes.length) {
        setLayerSaveState({
          isSaving: false,
          error: "No existing graph nodes available to connect to.",
          success: "",
        });
        return prevDraft;
      }

      const linkRow = (links || []).find((l) => String(l?.link_id) === linkId);
      if (!linkRow?.path?.length) return prevDraft;

      const shouldStart = selection === "start" || selection === "both";
      const shouldEnd = selection === "end" || selection === "both";
      const path = [...linkRow.path];
      const nodesToRemove = new Set();
      const snappedExistingNodeIds = new Set();
      const nextDraft = {
        ...prevDraft,
        endpointNodeIds: { ...(prevDraft.endpointNodeIds || {}) },
        activeDraftEndpoints: { ...(prevDraft.activeDraftEndpoints || {}) },
      };

      const connectEndpoint = (endpoint) => {
        const isStart = endpoint === "start";
        if (!nextDraft.activeDraftEndpoints?.[endpoint]) return false;
        const pt = isStart ? path[0] : path[path.length - 1];
        if (!pt) return false;

        let nearest = null;
        let best = Number.POSITIVE_INFINITY;
        existingNodes.forEach((n) => {
          const npt = { lat: Number(n?.lat), lng: Number(n?.lng) };
          if (!Number.isFinite(npt.lat) || !Number.isFinite(npt.lng)) return;
          const d = sqDist(pt, npt);
          if (d < best) {
            best = d;
            nearest = n;
          }
        });
        if (!nearest) return false;

        const nearestId = String(nearest.node_id);
        const nearestPt = { lat: Number(nearest.lat), lng: Number(nearest.lng) };
        if (isStart) {
          path.unshift(nearestPt);
        } else {
          path.push(nearestPt);
        }
        nextDraft.endpointNodeIds[endpoint] = nearestId;
        nextDraft.activeDraftEndpoints[endpoint] = false;
        nodesToRemove.add(isStart ? draftStartId : draftEndId);
        snappedExistingNodeIds.add(nearestId);
        return true;
      };

      const changedStart = shouldStart ? connectEndpoint("start") : false;
      const changedEnd = shouldEnd ? connectEndpoint("end") : false;
      if (!changedStart && !changedEnd) return prevDraft;

      setLinks((prev) =>
        prev.map((row) => {
          if (String(row?.link_id) !== linkId) return row;
          const next = {
            ...row,
            path,
            geometry: pathToLineString(path),
            length: pathLengthMeters(path),
          };
          if (nextDraft.endpointNodeIds?.start) {
            next.start_node_id = String(nextDraft.endpointNodeIds.start);
          }
          if (nextDraft.endpointNodeIds?.end) {
            next.end_node_id = String(nextDraft.endpointNodeIds.end);
          }
          return next;
        })
      );

      if (nodesToRemove.size) {
        setNodes((prev) => prev.filter((n) => !nodesToRemove.has(String(n?.node_id))));
        setSelectedNodeIds((prev) => {
          const next = new Set(prev);
          nodesToRemove.forEach((id) => next.delete(String(id)));
          snappedExistingNodeIds.forEach((id) => next.add(String(id)));
          return next;
        });
      }

      setLayerSaveState({
        isSaving: false,
        error: "",
        success: "Draft endpoint connected to nearest existing node.",
      });
      return nextDraft;
    });
    return true;
  }, [addLinkDraftMode, links, nodes, restoreEndpointSnapshot]);

  const connectDraftToNearestLink = useCallback((endpoint) => {
    if (addLinkDraftMode !== "ready") return;
    if (endpoint !== "start" && endpoint !== "end") return;

    restoreEndpointSnapshot(endpoint);

    const selectedIds = Array.from(
      new Set((draftNearestLinkSelected?.[endpoint] || []).map((v) => String(v)))
    );
    if (!selectedIds.length) {
      setLayerSaveState({
        isSaving: false,
        error: "Select at least one link option to connect to nearest link.",
        success: "",
      });
      return;
    }

    const draft = addLinkDraftInfo;
    if (!draft?.linkId) return false;
    const draftLinkId = String(draft.linkId);
    const draftLink = (links || []).find((l) => String(l?.link_id) === draftLinkId);
    if (!draftLink?.path?.length) return false;
    const draftNodeId = String(draft.draftNodeIds?.[endpoint] || "");
    const endpointPt = endpoint === "start" ? draftLink.path[0] : draftLink.path[draftLink.path.length - 1];
    if (!endpointPt || !draftNodeId) return false;
    const linkById = new Map((links || []).map((l) => [String(l?.link_id), l]));
    const selectedLinks = selectedIds
      .map((id) => linkById.get(id))
      .filter((l) => l && Array.isArray(l.path) && l.path.length >= 2 && String(l.link_id) !== draftLinkId);
    if (!selectedLinks.length) return false;
    let primary = null;
    let primaryProj = null;
    selectedLinks.forEach((row) => {
      const proj = closestPointOnPath(row.path, endpointPt);
      if (!proj) return;
      if (!primaryProj || proj.dist2 < primaryProj.dist2) {
        primaryProj = proj;
        primary = row;
      }
    });
    if (!primary || !primaryProj) return false;

    setAddLinkDraftInfo((prevDraft) => {
      if (!prevDraft?.linkId) return prevDraft;

      const foot = { lat: Number(primaryProj.point.lat), lng: Number(primaryProj.point.lng) };
      let maxNodeId = (nodes || []).reduce((acc, n) => {
        const v = Number(n?.node_id);
        return Number.isFinite(v) ? Math.max(acc, Math.trunc(v)) : acc;
      }, 0);
      let maxLinkId = (links || []).reduce((acc, l) => {
        const v = Number(l?.link_id);
        return Number.isFinite(v) ? Math.max(acc, Math.trunc(v)) : acc;
      }, 0);
      const connectorNodeId = String(maxNodeId + 1);

      const nextLinks = (links || []).map((l) => ({ ...l, path: Array.isArray(l.path) ? [...l.path] : l.path }));
      const nextNodes = (nodes || []).map((n) => ({ ...n }));

      // Replace/append endpoint on the draft link to hit the connector node.
      const dIdx = nextLinks.findIndex((l) => String(l?.link_id) === draftLinkId);
      if (dIdx >= 0) {
        const row = nextLinks[dIdx];
        const path = Array.isArray(row.path) ? [...row.path] : [];
        if (endpoint === "start") path.unshift(foot);
        else path.push(foot);
        nextLinks[dIdx] = {
          ...row,
          path,
          geometry: pathToLineString(path),
          length: pathLengthMeters(path),
          ...(endpoint === "start"
            ? { start_node_id: connectorNodeId, from_node_id: connectorNodeId }
            : { end_node_id: connectorNodeId, to_node_id: connectorNodeId }),
        };
      }

      // Remove the old draft endpoint node.
      const withoutDraftEndpoint = nextNodes.filter((n) => String(n?.node_id) !== draftNodeId);
      // Add connector node at perpendicular foot.
      withoutDraftEndpoint.push({
        node_id: connectorNodeId,
        name: `Draft node ${connectorNodeId}`,
        status: "draft",
        lat: foot.lat,
        lng: foot.lng,
        lon: foot.lng,
        __isDraft: true,
      });

      // Split selected links at connector node and replace originals.
      const selectedSet = new Set(selectedLinks.map((l) => String(l.link_id)));
      const keptLinks = nextLinks.filter((l) => !selectedSet.has(String(l?.link_id)));
      const splitLinks = [];
      selectedLinks.forEach((row, idx) => {
        const proj = idx === 0 ? primaryProj : closestPointOnPath(row.path, foot);
        const split = splitPathAtProjection(row.path, proj);
        if (!split?.left?.length || !split?.right?.length) return;

        const startId = getLinkStartNodeId(row);
        const endId = getLinkEndNodeId(row);
        const leftId = String(++maxLinkId);
        const rightId = String(++maxLinkId);

        const left = withLinkNodeIds({
          ...row,
          link_id: leftId,
          id: leftId,
          geometry: pathToLineString(split.left),
          path: split.left,
          length: pathLengthMeters(split.left),
        }, startId, connectorNodeId);
        const right = withLinkNodeIds({
          ...row,
          link_id: rightId,
          id: rightId,
          geometry: pathToLineString(split.right),
          path: split.right,
          length: pathLengthMeters(split.right),
        }, connectorNodeId, endId);

        splitLinks.push(left, right);
      });

      const finalLinks = [...keptLinks, ...splitLinks];
      setLinks(finalLinks);
      setNodes(withoutDraftEndpoint);
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        next.delete(draftNodeId);
        next.add(connectorNodeId);
        return next;
      });

      setLayerSaveState({
        isSaving: false,
        error: "",
        success: "Endpoint connected to selected nearest link(s).",
      });

      return {
        ...prevDraft,
        endpointNodeIds: {
          ...(prevDraft.endpointNodeIds || {}),
          [endpoint]: connectorNodeId,
        },
        activeDraftEndpoints: {
          ...(prevDraft.activeDraftEndpoints || {}),
          [endpoint]: false,
        },
        endpointSnapshots: {
          ...(prevDraft.endpointSnapshots || {}),
          [endpoint]: {
            links: (links || []).map((l) => ({ ...l, path: Array.isArray(l.path) ? l.path.map((p) => ({ ...p })) : l.path })),
            nodes: (nodes || []).map((n) => ({ ...n })),
            endpointNodeIds: { ...(prevDraft.endpointNodeIds || {}) },
            activeDraftEndpoints: { ...(prevDraft.activeDraftEndpoints || {}) },
          },
        },
      };
    });
    return true;
  }, [addLinkDraftMode, draftNearestLinkSelected, links, nodes, restoreEndpointSnapshot]);

  const restoreDraftEndpointDefault = useCallback((endpoint) => {
    if (addLinkDraftMode !== "ready") return;
    if (endpoint !== "start" && endpoint !== "end") return;
    if (restoreEndpointSnapshot(endpoint)) return;

    setAddLinkDraftInfo((prevDraft) => {
      if (!prevDraft?.linkId) return prevDraft;
      const linkId = String(prevDraft.linkId);
      const draftNodeId = String(prevDraft.draftNodeIds?.[endpoint] || "");
      const basePath = Array.isArray(prevDraft.originalPath) ? prevDraft.originalPath : [];
      if (!draftNodeId || basePath.length < 2) return prevDraft;

      const linkRow = (links || []).find((l) => String(l?.link_id) === linkId);
      if (!linkRow?.path?.length) return prevDraft;

      const defaultPt = endpoint === "start" ? basePath[0] : basePath[basePath.length - 1];
      if (!defaultPt) return prevDraft;

      const nextPath = [...linkRow.path];
      const isActiveDraftEndpoint = !!prevDraft.activeDraftEndpoints?.[endpoint];
      if (!isActiveDraftEndpoint && nextPath.length >= 2) {
        if (endpoint === "start") nextPath.shift();
        if (endpoint === "end") nextPath.pop();
      }
      if (endpoint === "start") {
        nextPath[0] = { lat: Number(defaultPt.lat), lng: Number(defaultPt.lng) };
      } else {
        nextPath[nextPath.length - 1] = { lat: Number(defaultPt.lat), lng: Number(defaultPt.lng) };
      }

      setLinks((prev) =>
        prev.map((row) => {
          if (String(row?.link_id) !== linkId) return row;
          const next = {
            ...row,
            path: nextPath,
            geometry: pathToLineString(nextPath),
            length: pathLengthMeters(nextPath),
          };
          if (endpoint === "start") next.start_node_id = draftNodeId;
          if (endpoint === "end") next.end_node_id = draftNodeId;
          return next;
        })
      );

      const recreatedNode = {
        node_id: draftNodeId,
        name: `Draft node ${draftNodeId}`,
        status: "draft",
        lat: Number(defaultPt.lat),
        lng: Number(defaultPt.lng),
        lon: Number(defaultPt.lng),
        __isDraft: true,
      };
      setNodes((prev) => {
        const exists = prev.some((n) => String(n?.node_id) === draftNodeId);
        if (exists) {
          return prev.map((n) =>
            String(n?.node_id) === draftNodeId
              ? { ...n, lat: recreatedNode.lat, lng: recreatedNode.lng, lon: recreatedNode.lon, status: "draft", __isDraft: true }
              : n
          );
        }
        return [...prev, recreatedNode];
      });
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        next.add(draftNodeId);
        return next;
      });

      return {
        ...prevDraft,
        endpointNodeIds: {
          ...(prevDraft.endpointNodeIds || {}),
          [endpoint]: draftNodeId,
        },
        activeDraftEndpoints: {
          ...(prevDraft.activeDraftEndpoints || {}),
          [endpoint]: true,
        },
      };
    });
  }, [addLinkDraftMode, links, restoreEndpointSnapshot]);

  const setDraftTopologyChoice = useCallback((endpoint, choice) => {
    if (endpoint !== "start" && endpoint !== "end") return;
    if (choice === "leave_as_is" || choice === "nearest_link") {
      // Apply rollback immediately so follow-up choices (e.g. nearest_link)
      // use the true default endpoint rather than a stale snapped endpoint.
      restoreDraftEndpointDefault(endpoint);
      setDraftNearestLinkOptions((prev) => ({ ...prev, [endpoint]: [] }));
      setDraftNearestLinkSelected((prev) => ({ ...prev, [endpoint]: [] }));
    }
    setDraftTopologyChoices((prev) => ({
      ...prev,
      [endpoint]: choice,
    }));
  }, [restoreDraftEndpointDefault]);

  const acceptDraftTopologyStep = useCallback(() => {
    if (addLinkDraftMode !== "ready") return;

    if (draftTopologyStep === "start") {
      const choice = draftTopologyChoices?.start || "nearest_node";
      if (choice === "nearest_node") {
        const ok = connectDraftToNearestNode("start");
        if (!ok) return;
      } else if (choice === "nearest_link") {
        const ok = connectDraftToNearestLink("start");
        if (!ok) return;
      } else if (choice === "leave_as_is") {
        restoreDraftEndpointDefault("start");
      }
      setDraftTopologyStep("end");
      return;
    }

    if (draftTopologyStep === "end") {
      const choice = draftTopologyChoices?.end || "leave_as_is";
      if (choice === "nearest_node") {
        const ok = connectDraftToNearestNode("end");
        if (!ok) return;
      } else if (choice === "nearest_link") {
        const ok = connectDraftToNearestLink("end");
        if (!ok) return;
      } else if (choice === "leave_as_is") {
        restoreDraftEndpointDefault("end");
      }
      setDraftTopologyStep("done");
    }
  }, [
    addLinkDraftMode,
    draftTopologyStep,
    draftTopologyChoices,
    connectDraftToNearestNode,
    connectDraftToNearestLink,
    restoreDraftEndpointDefault,
  ]);

  const backDraftTopologyStep = useCallback(() => {
    if (addLinkDraftMode !== "ready") return;
    if (draftTopologyStep === "done") {
      setDraftTopologyStep("end");
      return;
    }
    if (draftTopologyStep === "end") {
      setDraftTopologyStep("start");
      return;
    }
    if (draftTopologyStep !== "start") return;

    setAddLinkDraftInfo((draft) => {
      if (!draft?.linkId) return draft;
      const linkId = String(draft.linkId);
      const startDraftNodeId = String(draft.draftNodeIds?.start || "");
      const endDraftNodeId = String(draft.draftNodeIds?.end || "");
      const linkRow = (links || []).find((l) => String(l?.link_id) === linkId);
      const path = Array.isArray(linkRow?.path) ? [...linkRow.path] : [];

      setLinks((prev) => prev.filter((l) => String(l?.link_id) !== linkId));
      setNodes((prev) =>
        prev.filter((n) => {
          const id = String(n?.node_id);
          return id !== startDraftNodeId && id !== endDraftNodeId;
        })
      );
      setSelectedLinkIds((prev) => {
        const next = new Set(prev);
        next.delete(linkId);
        return next;
      });
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        next.delete(startDraftNodeId);
        next.delete(endDraftNodeId);
        return next;
      });

      setAddLinkDraftMode("drawing");
      setAddLinkDraftPath(path);
      addLinkDraftPathRef.current = path;
      setDraftTopologyStep("start");
      setLayerSaveState({ isSaving: false, error: "", success: "" });
      return null;
    });
  }, [addLinkDraftMode, draftTopologyStep, links]);

  const saveAddLinkDraft = useCallback(async () => {
    if (draftTopologyStep !== "done") {
      setLayerSaveState({
        isSaving: false,
        error: "Accept start and end topology steps before saving.",
        success: "",
      });
      return false;
    }
    if (addLinkDraftMode !== "ready" || !addLinkDraftInfo?.linkId) return false;

    setLayerSaveState({ isSaving: true, error: "", success: "" });
    try {
      const draftLinkId = String(addLinkDraftInfo.linkId);
      const draftLinkRow = (links || []).find((l) => String(l?.link_id) === draftLinkId);
      if (!draftLinkRow?.path?.length) throw new Error("Draft link was not found locally.");
      if (selectedLayer?.type !== "links") {
        throw new Error("Active layer must be links to save add-link draft.");
      }

      const linksFileName = String(selectedLayer?.filename || "");
      const nodesFileName = String(addLinkDraftInfo?.nodeLayerFileName || "");
      if (!linksFileName) throw new Error("No selected links layer file found.");
      if (!nodesFileName) throw new Error("No selected node layer file found for this draft.");
      const linksPath = `${inputsBase}/${linksFileName}`;
      const nodesPath = `${inputsBase}/${nodesFileName}`;
      const [linksCsvText, nodesCsvText] = await Promise.all([
        fetchCsvTextFromStorage(linksPath),
        fetchCsvTextFromStorage(nodesPath),
      ]);
      if (!linksCsvText) throw new Error(`Could not load ${linksFileName} from storage.`);
      if (!nodesCsvText) throw new Error(`Could not load ${nodesFileName} from storage.`);

      const parsedLinks = Papa.parse(linksCsvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
      });
      const parsedNodes = Papa.parse(nodesCsvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
      });

      const linkFields = Array.isArray(parsedLinks?.meta?.fields) ? [...parsedLinks.meta.fields] : [];
      const nodeFields = Array.isArray(parsedNodes?.meta?.fields) ? [...parsedNodes.meta.fields] : [];
      if (!linkFields.length) throw new Error(`No headers in ${linksFileName}.`);
      if (!nodeFields.length) throw new Error(`No headers in ${nodesFileName}.`);

      REQUIRED_LINK_FIELDS.forEach(({ candidates, fallback }) => {
        const existing = findHeader(linkFields, candidates, "");
        if (!existing && !linkFields.includes(fallback)) linkFields.push(fallback);
      });

      const localLinksById = new Map();
      (links || []).forEach((row) => {
        const id = String(row?.link_id ?? row?.id ?? "");
        if (!id) return;
        if (!Array.isArray(row?.path) || row.path.length < 2) return;
        localLinksById.set(id, row);
      });
      if (!localLinksById.has(draftLinkId)) {
        throw new Error(`Draft link ${draftLinkId} not found in current link state.`);
      }

      const localNodeById = new Map();
      (nodes || []).forEach((row) => {
        const id = String(row?.node_id ?? row?.id ?? "");
        if (!id) return;
        const lat = Number(row?.lat);
        const lng = Number(row?.lng ?? row?.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        localNodeById.set(id, { ...row, lat, lng, lon: lng });
      });

      const linkData = Array.from(localLinksById.values()).map((row) => makeLinkCsvRow(row, linkFields));
      const nodeData = Array.from(localNodeById.values()).map((row) => makeNodeCsvRow(row, nodeFields));

      const nextLinksCsv = Papa.unparse({ fields: linkFields, data: linkData });
      const nextNodesCsv = Papa.unparse({ fields: nodeFields, data: nodeData });

      await Promise.all([
        uploadString(storageRef(storage, linksPath), nextLinksCsv, "raw", { contentType: "text/csv" }),
        uploadString(storageRef(storage, nodesPath), nextNodesCsv, "raw", { contentType: "text/csv" }),
      ]);

      await refreshSelectedLayer();
      resetAddLinkDraftState();
      setLayerSaveState({
        isSaving: false,
        error: "",
        success: `Saved link edits and draft link ${draftLinkId} to cloud.`,
      });
      setChatLog((prev) => [
        ...prev,
          {
            role: "system",
            text: `Saved draft link ${draftLinkId} to ${linksFileName} and nodes to ${nodesFileName}.`,
            ts: Date.now(),
          },
        ]);
      return true;
    } catch (err) {
      setLayerSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    }
  }, [
    draftTopologyStep,
    addLinkDraftMode,
    addLinkDraftInfo,
    links,
    nodes,
    inputsBase,
    selectedLayer,
    refreshSelectedLayer,
    resetAddLinkDraftState,
  ]);

  const startEditDestinations = useCallback(() => {
    if (!activeConfigVar?.name) {
      setConfigSaveState({
        isSaving: false,
        error: "Create a destination config variable first.",
        success: "",
      });
      return;
    }
    if (String(activeConfigVar?.type || "") !== "destination") {
      setConfigSaveState({
        isSaving: false,
        error: "Destination editing is only available for destination configs.",
        success: "",
      });
      return;
    }
    if (!activeConfigNodeLayerId) {
      setConfigSaveState({
        isSaving: false,
        error: "No node layer available. Create or select a nodes layer first.",
        success: "",
      });
      return;
    }
    configEditSnapshotRef.current = {
      variableName: String(activeConfigVar.name),
      row: { ...activeConfigVar, destinationIds: [...(activeConfigVar.destinationIds || [])] },
    };
    setEditingConfigVarName(String(activeConfigVar.name));
    setIsEditingDestinations(true);
    setIsEditingStartTimes(false);
    setIsEditingHazardConfig(false);
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [activeConfigVar, activeConfigNodeLayerId]);

  const startEditStartTimes = useCallback(() => {
    if (!activeConfigVar?.name || String(activeConfigVar?.type || "") !== "start_times") return;
    configEditSnapshotRef.current = {
      variableName: String(activeConfigVar.name),
      row: { ...activeConfigVar },
    };
    setEditingConfigVarName(String(activeConfigVar.name));
    setIsEditingDestinations(false);
    setIsEditingStartTimes(true);
    setIsEditingHazardConfig(false);
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [activeConfigVar]);

  const startEditHazardConfig = useCallback(() => {
    if (!builderPermissions.hazardLayer) return;
    if (!activeConfigVar?.name || String(activeConfigVar?.type || "") !== "hazard_layer") return;
    configEditSnapshotRef.current = {
      variableName: String(activeConfigVar.name),
      row: { ...activeConfigVar },
    };
    setEditingConfigVarName(String(activeConfigVar.name));
    setIsEditingDestinations(false);
    setIsEditingStartTimes(false);
    setIsEditingHazardConfig(true);
    setHazardConfigNameDraft(String(activeConfigVar.name));
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [activeConfigVar, builderPermissions.hazardLayer]);

  const startEditContraflow = useCallback(() => {
    if (!activeConfigVar?.name || activeConfigVar.type !== "contraflow") return;
    const hydratedEntries = normalizeContraflowEntries(
      activeConfigVar.contraflowEntries || activeConfigVar.linkIds
    ).map((entry) => {
      const main = contraflowLinks.find((link) => String(link?.link_id) === entry.linkId);
      const start = getLinkStartNodeId(main);
      const end = getLinkEndNodeId(main);
      const reverse = contraflowLinks.find((link) =>
        String(link?.link_id) !== entry.linkId &&
        getLinkStartNodeId(link) === end && getLinkEndNodeId(link) === start
      );
      const mainLaneCount = Number(main?.lanes);
      const reverseLaneCount = Number(reverse?.lanes);
      return {
        ...entry,
        reverseLinkId: entry.reverseLinkId || String(reverse?.link_id || ""),
        mainLanes: entry.mainLanes ?? (Number.isInteger(mainLaneCount) && mainLaneCount >= 0 ? mainLaneCount : null),
        reverseLanes: entry.reverseLanes ?? (Number.isInteger(reverseLaneCount) && reverseLaneCount >= 0 ? reverseLaneCount : null),
      };
    });
    const hydratedRow = { ...activeConfigVar, contraflowEntries: hydratedEntries };
    configEditSnapshotRef.current = {
      variableName: String(activeConfigVar.name),
      row: { ...activeConfigVar, contraflowEntries: normalizeContraflowEntries(activeConfigVar.contraflowEntries || activeConfigVar.linkIds) },
    };
    setCustomConfigVars((prev) => prev.map((row) => row.name === activeConfigVar.name ? hydratedRow : row));
    setEditingConfigVarName(String(activeConfigVar.name));
    setIsEditingDestinations(false);
    setIsEditingStartTimes(false);
    setIsEditingHazardConfig(false);
    setIsEditingContraflow(true);
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [activeConfigVar, contraflowLinks]);

  const startEditBlockedRoad = useCallback(() => {
    if (!activeConfigVar?.name || activeConfigVar.type !== "blocked_road") return;
    configEditSnapshotRef.current = { variableName: activeConfigVar.name, row: { ...activeConfigVar, blockedRoadEntries: normalizeBlockedRoadEntries(activeConfigVar.blockedRoadEntries) } };
    setEditingConfigVarName(activeConfigVar.name);
    setBlockedRoadTwoSided(true);
    setIsEditingBlockedRoad(true);
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [activeConfigVar]);

  const setActiveBlockedRoadLinksLayer = useCallback((layerId) => {
    updateBlockedRoadConfigVariable(effectiveConfigContext.name, (row) => ({ ...row, linksLayerId: String(layerId || ""), blockedRoadEntries: [] }));
  }, [effectiveConfigContext.name, updateBlockedRoadConfigVariable]);

  const toggleBlockedRoadLink = useCallback((id) => {
    if (!isEditingBlockedRoad || effectiveConfigContext.type !== "blocked_road") return;
    const key = String(id);
    updateBlockedRoadConfigVariable(effectiveConfigContext.name, (row) => {
      const entries = normalizeBlockedRoadEntries(row.blockedRoadEntries);
      const link = contraflowLinks.find((item) => String(item?.link_id) === key);
      if (!link) return row;
      const start = getLinkStartNodeId(link);
      const end = getLinkEndNodeId(link);
      const reverse = blockedRoadTwoSided && start !== "" && end !== "" && start !== null && end !== null && start !== undefined && end !== undefined ? contraflowLinks.find((item) =>
        String(item?.link_id) !== key && getLinkStartNodeId(item) === end && getLinkEndNodeId(item) === start
      ) : null;
      const ids = [key, ...(reverse ? [String(reverse.link_id)] : [])];
      const selected = new Set(entries.map((entry) => entry.linkId));
      return { ...row, blockedRoadEntries: selected.has(key)
        ? entries.filter((entry) => !ids.includes(entry.linkId))
        : normalizeBlockedRoadEntries([...entries, ...ids.map((linkId) => ({ linkId }))]) };
    });
  }, [isEditingBlockedRoad, effectiveConfigContext.name, effectiveConfigContext.type, contraflowLinks, blockedRoadTwoSided, updateBlockedRoadConfigVariable]);

  const clearBlockedRoad = useCallback(() => updateBlockedRoadConfigVariable(effectiveConfigContext.name, (row) => ({ ...row, blockedRoadEntries: [] })), [effectiveConfigContext.name, updateBlockedRoadConfigVariable]);
  const removeBlockedRoadEntry = useCallback((linkId) => updateBlockedRoadConfigVariable(effectiveConfigContext.name, (row) => ({ ...row, blockedRoadEntries: normalizeBlockedRoadEntries(row.blockedRoadEntries).filter((entry) => entry.linkId !== String(linkId)) })), [effectiveConfigContext.name, updateBlockedRoadConfigVariable]);

  const cancelConfigEdit = useCallback(() => {
    const snapshot = configEditSnapshotRef.current;
    if (snapshot?.variableName && snapshot?.row) {
      setCustomConfigVars((prev) =>
        prev.map((row) => (row.name === snapshot.variableName ? snapshot.row : row))
      );
    }
    configEditSnapshotRef.current = null;
    setIsEditingDestinations(false);
    setIsEditingStartTimes(false);
    setIsEditingHazardConfig(false);
    setIsEditingContraflow(false);
    setIsEditingBlockedRoad(false);
    setHazardConfigNameDraft("");
    setEditingConfigVarName("");
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, []);

  useEffect(() => {
    if (!builderPermissions.hazardLayer && isEditingHazardConfig) cancelConfigEdit();
  }, [builderPermissions.hazardLayer, isEditingHazardConfig, cancelConfigEdit]);

  const selectConfigVariable = useCallback((name) => {
    if (isEditingDestinations || isEditingStartTimes || isEditingHazardConfig || isEditingContraflow || isEditingBlockedRoad) return;
    const wanted = String(name || "").trim();
    if (!wanted) return;
    if (!availableConfigVars.some((row) => row.name === wanted)) return;
    const picked = availableConfigVars.find((row) => row.name === wanted);
    trackBuilderEvent("builder_config_selected", { builder_id: simulationIdSafe, tab: "config", config_name: wanted, config_type: picked?.type });
    setActiveConfigVarName(wanted);
    setIsEditingDestinations(false);
    setIsEditingStartTimes(false);
    setIsEditingHazardConfig(false);
    setIsEditingContraflow(false);
    setIsEditingBlockedRoad(false);
    setEditingConfigVarName("");
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [isEditingDestinations, isEditingStartTimes, isEditingHazardConfig, isEditingContraflow, isEditingBlockedRoad, availableConfigVars, simulationIdSafe]);

  const setActiveContraflowLinksLayer = useCallback((layerId) => {
    if (effectiveConfigContext.type !== "contraflow") return;
    updateContraflowConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      linksLayerId: String(layerId || ""),
      contraflowEntries: [],
    }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, updateContraflowConfigVariable]);

  const setActiveContraflowDestinationConfig = useCallback((name) => {
    if (effectiveConfigContext.type !== "contraflow") return;
    updateContraflowConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      destinationConfigName: String(name || ""),
    }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, updateContraflowConfigVariable]);

  const toggleContraflowLink = useCallback((id) => {
    if (!isEditingContraflow || effectiveConfigContext.type !== "contraflow") return;
    const key = String(id || "");
    updateContraflowConfigVariable(effectiveConfigContext.name, (row) => {
      const entries = normalizeContraflowEntries(row.contraflowEntries || row.linkIds);
      if (entries.some((entry) => entry.linkId === key)) {
        return { ...row, contraflowEntries: entries.filter((entry) => entry.linkId !== key) };
      }
      const main = contraflowLinks.find((link) => String(link?.link_id) === key);
      const start = getLinkStartNodeId(main);
      const end = getLinkEndNodeId(main);
      const reverse = contraflowLinks.find((link) =>
        String(link?.link_id) !== key && getLinkStartNodeId(link) === end && getLinkEndNodeId(link) === start
      );
      if (!reverse) {
        setConfigSaveState({ isSaving: false, error: `No reverse link found for ${key}.`, success: "" });
        return row;
      }
      const mainLaneCount = Number(main?.lanes);
      const reverseLaneCount = Number(reverse?.lanes);
      return {
        ...row,
        contraflowEntries: [...entries, {
          linkId: key,
          reverseLinkId: String(reverse.link_id),
          mainLanes: Number.isInteger(mainLaneCount) && mainLaneCount >= 0 ? mainLaneCount : null,
          reverseLanes: Number.isInteger(reverseLaneCount) && reverseLaneCount >= 0 ? reverseLaneCount : null,
        }],
      };
    });
  }, [isEditingContraflow, effectiveConfigContext.name, effectiveConfigContext.type, contraflowLinks, updateContraflowConfigVariable]);

  const addConfigLinkIdsByInput = useCallback(async (rawInput, type) => {
    const isBlocked = type === "blocked_road";
    if (effectiveConfigContext.type !== type || !(isBlocked ? isEditingBlockedRoad : isEditingContraflow)) return false;
    const targetName = effectiveConfigContext.name;
    const layerId = String(effectiveConfigContext.linksLayerId || "");
    const layerDoc = linksLayerOptions.find((layer) => String(layer.id) === layerId);
    if (!layerDoc) {
      setConfigSaveState({ isSaving: false, error: "Select a link layer first.", success: "" });
      return false;
    }
    const requested = [...new Set(String(rawInput || "").split(",").map((id) => id.trim()).filter(Boolean))];
    if (!requested.length) return false;

    let layerLinks;
    try {
      layerLinks = await readLayerData(layerDoc);
    } catch (error) {
      setConfigSaveState({ isSaving: false, error: `Could not load the selected link layer: ${String(error?.message || error)}`, success: "" });
      return false;
    }
    if (configEditSnapshotRef.current?.variableName !== targetName) return false;
    const currentRow = customConfigVarsRef.current.find((row) => row.name === targetName && row.type === type);
    if (String(currentRow?.linksLayerId || "") !== layerId) {
      setConfigSaveState({ isSaving: false, error: "The selected link layer changed. Try adding the IDs again.", success: "" });
      return false;
    }
    if (!layerLinks.length) {
      setConfigSaveState({ isSaving: false, error: "The selected link layer has no links.", success: "" });
      return false;
    }

    const linksById = new Map(layerLinks.map((link) => [String(link?.link_id), link]));
    const linksByEndpoints = new Map();
    layerLinks.forEach((link) => {
      const key = JSON.stringify([getLinkStartNodeId(link), getLinkEndNodeId(link)]);
      if (!linksByEndpoints.has(key)) linksByEndpoints.set(key, []);
      linksByEndpoints.get(key).push(link);
    });
    const existing = isBlocked
      ? normalizeBlockedRoadEntries(currentRow.blockedRoadEntries)
      : normalizeContraflowEntries(currentRow.contraflowEntries || currentRow.linkIds);
    const selected = new Set(existing.map((entry) => entry.linkId));
    const additions = [];
    const missing = [];
    const alreadySelected = [];
    const noReverse = [];
    requested.forEach((enteredId) => {
      const link = linksById.get(enteredId) || linksById.get(enteredId.replace(/^L(?=\d)/i, ""));
      if (!link) { missing.push(enteredId); return; }
      const linkId = String(link.link_id);
      const start = getLinkStartNodeId(link);
      const end = getLinkEndNodeId(link);
      const reverse = start != null && end != null && start !== "" && end !== ""
        ? (linksByEndpoints.get(JSON.stringify([end, start])) || [])
          .find((candidate) => String(candidate?.link_id) !== linkId)
        : null;
      if (isBlocked) {
        const ids = [linkId, ...(blockedRoadTwoSided && reverse ? [String(reverse.link_id)] : [])];
        let added = false;
        ids.forEach((id) => {
          if (selected.has(id)) return;
          selected.add(id);
          additions.push({ linkId: id });
          added = true;
        });
        if (!added) alreadySelected.push(enteredId);
        return;
      }
      if (selected.has(linkId)) { alreadySelected.push(enteredId); return; }
      if (!reverse) { noReverse.push(enteredId); return; }
      const mainLanes = Number(link.lanes);
      const reverseLanes = Number(reverse.lanes);
      additions.push({
        linkId,
        reverseLinkId: String(reverse.link_id),
        mainLanes: Number.isInteger(mainLanes) && mainLanes >= 0 ? mainLanes : null,
        reverseLanes: Number.isInteger(reverseLanes) && reverseLanes >= 0 ? reverseLanes : null,
      });
      selected.add(linkId);
    });

    const skipped = [
      missing.length && `not found: ${missing.join(", ")}`,
      noReverse.length && `no reverse link: ${noReverse.join(", ")}`,
      alreadySelected.length && `already selected: ${alreadySelected.join(", ")}`,
    ].filter(Boolean).join("; ");
    if (!additions.length) {
      setConfigSaveState({ isSaving: false, error: `No new links added${skipped ? ` (${skipped})` : ""}.`, success: "" });
      return false;
    }
    if (isBlocked) {
      updateBlockedRoadConfigVariable(targetName, (row) => ({
        ...row,
        blockedRoadEntries: normalizeBlockedRoadEntries([...(row.blockedRoadEntries || []), ...additions]),
      }));
    } else {
      updateContraflowConfigVariable(targetName, (row) => ({
        ...row,
        contraflowEntries: normalizeContraflowEntries([...(row.contraflowEntries || row.linkIds || []), ...additions]),
      }));
    }
    setConfigSaveState({ isSaving: false, error: "", success: `Added ${additions.length} link${additions.length === 1 ? "" : "s"}${skipped ? `; ${skipped}` : ""}.` });
    return true;
  }, [effectiveConfigContext, isEditingBlockedRoad, isEditingContraflow, linksLayerOptions, readLayerData, blockedRoadTwoSided, updateBlockedRoadConfigVariable, updateContraflowConfigVariable]);

  const switchContraflowLink = useCallback((linkId) => {
    const id = String(linkId || "");
    const row = contraflowLinks.find((link) => String(link?.link_id) === id);
    const start = getLinkStartNodeId(row);
    const end = getLinkEndNodeId(row);
    const opposite = contraflowLinks.find((link) =>
      String(link?.link_id) !== id && getLinkStartNodeId(link) === end && getLinkEndNodeId(link) === start
    );
    if (!opposite) {
      setConfigSaveState({ isSaving: false, error: `No reverse link found for ${id}.`, success: "" });
      return;
    }
    const oppositeId = String(opposite.link_id);
    updateContraflowConfigVariable(effectiveConfigContext.name, (config) => ({
      ...config,
      contraflowEntries: normalizeContraflowEntries(config.contraflowEntries || config.linkIds)
        .map((entry) => entry.linkId === id
          ? { ...entry, linkId: oppositeId, reverseLinkId: id }
          : entry)
        .filter((entry, index, entries) => entries.findIndex((item) => item.linkId === entry.linkId) === index),
    }));
    setConfigSaveState({ isSaving: false, error: "", success: `Contraflow direction switched to link ${oppositeId}.` });
  }, [contraflowLinks, effectiveConfigContext.name, updateContraflowConfigVariable]);

  const clearContraflow = useCallback(() => {
    updateContraflowConfigVariable(effectiveConfigContext.name, (row) => ({ ...row, contraflowEntries: [] }));
  }, [effectiveConfigContext.name, updateContraflowConfigVariable]);

  const removeContraflowEntry = useCallback((linkId) => {
    updateContraflowConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      contraflowEntries: normalizeContraflowEntries(row.contraflowEntries || row.linkIds)
        .filter((entry) => entry.linkId !== String(linkId)),
    }));
  }, [effectiveConfigContext.name, updateContraflowConfigVariable]);

  const setContraflowLaneAllocation = useCallback((linkId, mainLanes) => {
    updateContraflowConfigVariable(effectiveConfigContext.name, (row) => ({
      ...row,
      contraflowEntries: normalizeContraflowEntries(row.contraflowEntries || row.linkIds).map((entry) => {
        if (entry.linkId !== String(linkId)) return entry;
        if (!Number.isInteger(entry.mainLanes) || !Number.isInteger(entry.reverseLanes)) return entry;
        const total = entry.mainLanes + entry.reverseLanes;
        const nextMain = Number(mainLanes);
        if (!Number.isInteger(total) || !Number.isInteger(nextMain) || nextMain < 0 || nextMain > total) return entry;
        return { ...entry, mainLanes: nextMain, reverseLanes: total - nextMain };
      }),
    }));
  }, [effectiveConfigContext.name, updateContraflowConfigVariable]);

  const setActiveDestinationNodeLayer = useCallback(
    (layerId) => {
      if (effectiveConfigContext.type !== "destination") return;
      const wanted = String(layerId || "").trim();
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateDestinationConfigVariable(targetName, (row) => ({ ...row, nodeLayerId: wanted }));
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, updateDestinationConfigVariable]
  );

  const setActiveDestinationVisibilityLinksLayer = useCallback(
    (layerId) => {
      if (effectiveConfigContext.type !== "destination") return;
      const wanted = String(layerId || "").trim();
      if (wanted && !linksLayerOptions.some((row) => String(row.id) === wanted)) return;
      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return;
      updateDestinationConfigVariable(targetName, (row) => ({
        ...row,
        visibilityLinksLayerId: wanted,
      }));
      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
    },
    [effectiveConfigContext.name, effectiveConfigContext.type, linksLayerOptions, updateDestinationConfigVariable]
  );

  const persistCustomConfig = useCallback(
    async (configVars) => {
      if (!simulationIdSafe) {
        throw new Error("Missing simulation id. Open /builder/{simulationId}.");
      }

      const sourceVars = Array.isArray(configVars) ? configVars : [];
      const destinationVars = sourceVars
        .filter((row) => row?.type === "destination")
        .map((row) => ({
          name: String(row.name || "").trim(),
          nodeLayerId: String(row.nodeLayerId || ""),
          visibilityLinksLayerId: linksLayerOptions.some(
            (layer) => String(layer.id) === String(row.visibilityLinksLayerId || "")
          )
            ? String(row.visibilityLinksLayerId)
            : "",
          destinationIds: toNumericDestinationIds(row.destinationIds),
        }))
        .filter((row) => !!row.name);

      const startTimesVars = sourceVars
        .filter((row) => row?.type === "start_times")
        .map((row) => {
          const name = String(row?.name || "").trim();
          let serialized;
          try {
            serialized = serializeDepartureTimeConfig(row);
          } catch (error) {
            throw new Error(`Config "${name || "start_times"}": ${error.message}`);
          }
          return {
            name,
            minDepartureTime: serialized.min_departure_time,
            maxDepartureTime: serialized.max_departure_time,
            granularSurvey: serialized.granular_survey,
            serialized,
          };
        })
        .filter((row) => !!row.name);

      const hazardConfigVars = sourceVars
        .filter((row) => row?.type === "hazard_layer")
        .map((row) => {
          return {
            name: String(row.name || "").trim(),
            hazardEntries: (row.hazardEntries || []).map((entry, index) => {
              const hazardLayerId = String(entry.hazardLayerId || "").trim();
              const layer = hazardLayers.find((item) => String(item.id) === hazardLayerId);
              return {
                name: `hazard_layer_${index + 1}`,
                hazardLayerId,
                hazardLayerName: String(layer?.name || entry.hazardLayerName || "").trim(),
                hazardRerouting: Boolean(entry.hazardRerouting),
              };
            }),
          };
        })
        .filter((row) => !!row.name);
      const blockedRoadVars = sourceVars
        .filter((row) => row?.type === "blocked_road")
        .map((row) => ({ name: String(row.name || "").trim(), linksLayerId: String(row.linksLayerId || ""), blockedRoadEntries: normalizeBlockedRoadEntries(row.blockedRoadEntries) }))
        .filter((row) => !!row.name);
      const contraflowVars = sourceVars
        .filter((row) => row?.type === "contraflow")
        .map((row) => ({
          name: String(row.name || "").trim(),
          linksLayerId: String(row.linksLayerId || ""),
          destinationConfigName: String(row.destinationConfigName || ""),
          contraflowEntries: normalizeContraflowEntries(row.contraflowEntries || row.linkIds),
        }))
        .filter((row) => !!row.name);

      if (!destinationVars.length) {
        throw new Error("Create at least one destination config variable before saving.");
      }

      const configDestinations = {};
      destinationVars.forEach((row) => {
        configDestinations[row.name] = row.destinationIds;
      });
      const configStartTimes = {};
      startTimesVars.forEach((row) => {
        configStartTimes[row.name] = row.serialized;
      });
      const configHazards = {};
      hazardConfigVars.forEach((row) => {
        configHazards[row.name] = row.hazardEntries.map((entry) => ({
          name: entry.name,
          hazard_layer_id: entry.hazardLayerId,
          hazard_layer_name: entry.hazardLayerName,
          hazard_rerouting: entry.hazardRerouting,
        }));
      });
      const configBlockedRoad = {};
      blockedRoadVars.forEach((row) => { configBlockedRoad[row.name] = serializeBlockedRoadEntries(row.blockedRoadEntries); });
      const configContraflow = {};
      contraflowVars.forEach((row) => {
        configContraflow[row.name] = row.contraflowEntries.map((entry) => ({
          link_id: entry.linkId,
          reverse_link_id: entry.reverseLinkId,
          main_lanes: entry.mainLanes,
          reverse_lanes: entry.reverseLanes,
        }));
      });

      const metadataWrites = [
        ...destinationVars.map((row) =>
          setDoc(
            doc(
              firestore,
              "model_builders",
              simulationIdSafe,
              "custom_config",
              row.name
            ),
            {
              type: "destination",
              nodeLayerId: row.nodeLayerId,
              visibilityLinksLayerId: row.visibilityLinksLayerId,
            },
            { merge: true }
          )
        ),
        ...startTimesVars.map((row) =>
          setDoc(
            doc(
              firestore,
              "model_builders",
              simulationIdSafe,
              "custom_config",
              row.name
            ),
            {
              type: "start_times",
              granularSurvey: row.granularSurvey,
            },
            { merge: true }
          )
        ),
        ...hazardConfigVars.map((row) =>
          setDoc(
            doc(firestore, "model_builders", simulationIdSafe, "custom_config", row.name),
            {
              type: "hazard_layer",
              hazardEntries: row.hazardEntries,
            },
            { merge: true }
          )
        ),
        ...blockedRoadVars.map((row) =>
          setDoc(doc(firestore, "model_builders", simulationIdSafe, "custom_config", row.name), { type: "blocked_road", linksLayerId: row.linksLayerId }, { merge: true })
        ),
        ...contraflowVars.map((row) =>
          setDoc(
            doc(firestore, "model_builders", simulationIdSafe, "custom_config", row.name),
            {
              type: "contraflow",
              linksLayerId: row.linksLayerId,
              destinationConfigName: row.destinationConfigName,
            },
            { merge: true }
          )
        ),
      ];

      const departureDefault =
        startTimesVars.find((row) => row.name === "departure_times") || DEFAULT_START_TIMES;
      const depDefaultMin = toDepartureMinutes(
        departureDefault.minDepartureTime,
        DEFAULT_START_TIMES.minDepartureTime
      );
      const normalizedDepartureDefault = configStartTimes.departure_times || {
        min_departure_time: depDefaultMin,
        max_departure_time: Math.max(
          depDefaultMin,
          toDepartureMinutes(departureDefault.maxDepartureTime, depDefaultMin)
        ),
        granular_survey: false,
      };

      const path = `${configBase}/${CONFIG_FILE_NAME}`;
      const existingConfig = (await fetchJsonFromStorage(path)) || {};
      const nextConfig = {
        ...existingConfig,
        ...configDestinations,
        ...configStartTimes,
        ...configHazards,
        ...configContraflow,
        ...configBlockedRoad,
      };
      if (!Array.isArray(nextConfig.destinations)) {
        const firstName = destinationVars[0]?.name;
        nextConfig.destinations = firstName ? configDestinations[firstName] || [] : [];
      }
      if (
        !nextConfig.departure_times ||
        typeof nextConfig.departure_times !== "object" ||
        Array.isArray(nextConfig.departure_times)
      ) {
        nextConfig.departure_times = normalizedDepartureDefault;
      } else {
        nextConfig.departure_times = normalizedDepartureDefault;
      }

      await Promise.all([
        ...metadataWrites,
        uploadString(storageRef(storage, path), JSON.stringify(nextConfig, null, 2), "raw", {
          contentType: "application/json",
        }),
      ]);

      customConfigStorageRef.current = nextConfig;
      latestCustomConfigVarsRef.current = mergeCustomConfigVars(
        customConfigMetadataRef.current,
        nextConfig
      );

      return {
        count: destinationVars.length + startTimesVars.length + hazardConfigVars.length + contraflowVars.length + blockedRoadVars.length,
        destinationCount: destinationVars.length,
        startTimesCount: startTimesVars.length,
        hazardConfigCount: hazardConfigVars.length,
        contraflowCount: contraflowVars.length,
        blockedRoadCount: blockedRoadVars.length,
      };
    },
    [simulationIdSafe, configBase, linksLayerOptions, hazardLayers]
  );

  const persistHazardConfigVariable = useCallback(async (row, originalName = "") => {
    if (!simulationIdSafe) {
      throw new Error("Missing simulation id. Open /builder/{simulationId}.");
    }
    const name = String(row?.name || "").trim();
    if (!name) throw new Error("Hazard config name is required.");
    const sourceEntries = Array.isArray(row?.hazardEntries) ? row.hazardEntries : [];
    if (!sourceEntries.length) throw new Error(`Config "${name}" requires at least one hazard layer.`);
    const hazardEntries = sourceEntries.map((entry, index) => {
      const hazardLayerId = String(entry?.hazardLayerId || "").trim();
      const layer = hazardLayers.find((item) => String(item.id) === hazardLayerId);
      if (!layer) throw new Error(`Config "${name}" entry ${index + 1} requires an existing hazard layer.`);
      return {
        name: `hazard_layer_${index + 1}`,
        hazardLayerId,
        hazardLayerName: String(layer.name || layer.id),
        hazardRerouting: Boolean(entry?.hazardRerouting),
      };
    });
    const path = `${configBase}/${CONFIG_FILE_NAME}`;
    const existingConfig = (await fetchJsonFromStorage(path)) || {};
    const previousName = String(originalName || "").trim();
    const nextConfig = {
      ...existingConfig,
      [name]: hazardEntries.map((entry) => ({
        name: entry.name,
        hazard_layer_id: entry.hazardLayerId,
        hazard_layer_name: entry.hazardLayerName,
        hazard_rerouting: entry.hazardRerouting,
      })),
    };
    if (previousName && previousName !== name) delete nextConfig[previousName];

    await Promise.all([
      setDoc(
        doc(firestore, "model_builders", simulationIdSafe, "custom_config", name),
        {
          type: "hazard_layer",
          hazardEntries,
        },
        { merge: true }
      ),
      ...(previousName && previousName !== name
        ? [deleteDoc(doc(firestore, "model_builders", simulationIdSafe, "custom_config", previousName))]
        : []),
      uploadString(storageRef(storage, path), JSON.stringify(nextConfig, null, 2), "raw", {
        contentType: "application/json",
      }),
    ]);

    customConfigStorageRef.current = nextConfig;
    latestCustomConfigVarsRef.current = mergeCustomConfigVars(
      customConfigMetadataRef.current,
      nextConfig
    );

    return { ...row, hazardEntries };
  }, [simulationIdSafe, configBase, hazardLayers]);

  const persistBlockedRoadConfigVariable = useCallback(async (row) => {
    if (!simulationIdSafe) throw new Error("Missing simulation id. Open /builder/{simulationId}.");
    const name = String(row?.name || "").trim();
    const linksLayerId = String(row?.linksLayerId || "").trim();
    if (!name) throw new Error("Blocked-road config name is required.");
    if (!linksLayerOptions.some((layer) => String(layer.id) === linksLayerId)) throw new Error(`Config "${name}" requires an existing link layer.`);
    const path = `${configBase}/${CONFIG_FILE_NAME}`;
    const existingConfig = (await fetchJsonFromStorage(path)) || {};
    const blockedRoadEntries = normalizeBlockedRoadEntries(row?.blockedRoadEntries);
    const nextConfig = { ...existingConfig, [name]: serializeBlockedRoadEntries(blockedRoadEntries) };
    await Promise.all([
      setDoc(doc(firestore, "model_builders", simulationIdSafe, "custom_config", name), { type: "blocked_road", linksLayerId }, { merge: true }),
      uploadString(storageRef(storage, path), JSON.stringify(nextConfig, null, 2), "raw", { contentType: "application/json" }),
    ]);
    customConfigStorageRef.current = nextConfig;
    latestCustomConfigVarsRef.current = mergeCustomConfigVars(customConfigMetadataRef.current, nextConfig);
    return { ...row, linksLayerId, blockedRoadEntries };
  }, [simulationIdSafe, linksLayerOptions, configBase]);

  const persistContraflowConfigVariable = useCallback(async (row) => {
    if (!simulationIdSafe) throw new Error("Missing simulation id. Open /builder/{simulationId}.");
    const name = String(row?.name || "").trim();
    const linksLayerId = String(row?.linksLayerId || "").trim();
    const destinationConfigName = String(row?.destinationConfigName || "").trim();
    if (!name) throw new Error("Contraflow config name is required.");
    if (!linksLayerOptions.some((layer) => String(layer.id) === linksLayerId)) {
      throw new Error(`Config "${name}" requires an existing link layer.`);
    }
    if (!customConfigVars.some((config) => config.type === "destination" && config.name === destinationConfigName)) {
      throw new Error(`Config "${name}" requires an existing destination config.`);
    }
    const path = `${configBase}/${CONFIG_FILE_NAME}`;
    const existingConfig = (await fetchJsonFromStorage(path)) || {};
    const contraflowEntries = normalizeContraflowEntries(row?.contraflowEntries || row?.linkIds);
    const invalidEntry = contraflowEntries.find((entry) =>
      !entry.reverseLinkId || entry.mainLanes == null || entry.reverseLanes == null
    );
    if (invalidEntry) {
      throw new Error(`Contraflow link ${invalidEntry.linkId} requires a reverse link and valid lane counts.`);
    }
    const serializedEntries = contraflowEntries.map((entry) => ({
      link_id: entry.linkId,
      reverse_link_id: entry.reverseLinkId,
      main_lanes: entry.mainLanes,
      reverse_lanes: entry.reverseLanes,
    }));
    const nextConfig = { ...existingConfig, [name]: serializedEntries };
    await Promise.all([
      setDoc(
        doc(firestore, "model_builders", simulationIdSafe, "custom_config", name),
        { type: "contraflow", linksLayerId, destinationConfigName },
        { merge: true }
      ),
      uploadString(
        storageRef(storage, path),
        JSON.stringify(nextConfig, null, 2),
        "raw",
        { contentType: "application/json" }
      ),
    ]);
    customConfigStorageRef.current = nextConfig;
    latestCustomConfigVarsRef.current = mergeCustomConfigVars(
      customConfigMetadataRef.current,
      nextConfig
    );
    return { ...row, linksLayerId, destinationConfigName, contraflowEntries };
  }, [simulationIdSafe, configBase, linksLayerOptions, customConfigVars]);

  const createConfigVariable = useCallback(
    async ({ name, type }) => {
      const varName = String(name || "").trim();
      const varType = String(type || "").trim().toLowerCase();
      if (varType === "hazard_layer" && !builderPermissions.hazardLayer) return false;
      if (!varName) {
        setConfigSaveState({
          isSaving: false,
          error: "Variable name is required.",
          success: "",
        });
        return false;
      }
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName)) {
        setConfigSaveState({
          isSaving: false,
          error: "Variable name must start with a letter/underscore and use only letters, numbers, underscores.",
          success: "",
        });
        return false;
      }
      if (!["destination", "start_times", "hazard_layer", "contraflow", "blocked_road"].includes(varType)) {
        setConfigSaveState({
          isSaving: false,
          error: "Supported config types are destination, start_times, hazard_layer, contraflow, and blocked_road.",
          success: "",
        });
        return false;
      }
      if (customConfigVars.some((row) => row.name.toLowerCase() === varName.toLowerCase())) {
        setConfigSaveState({
          isSaving: false,
          error: `Variable "${varName}" already exists.`,
          success: "",
        });
        return false;
      }
      if ((varType === "contraflow" || varType === "blocked_road") && !linksLayerOptions.length) {
        setConfigSaveState({ isSaving: false, error: `Create a link layer before creating a ${varType === "contraflow" ? "contraflow" : "blocked-road"} config.`, success: "" });
        return false;
      }

      const defaultNodeLayerId = String(nodeLayerOptions[0]?.id || "");
      const nextVar = varType === "destination"
        ? {
          name: varName,
          type: "destination",
          nodeLayerId: defaultNodeLayerId,
          visibilityLinksLayerId: "",
          destinationIds: [],
        }
        : varType === "start_times" ? {
          name: varName,
          type: "start_times",
          ...DEFAULT_START_TIMES,
        } : varType === "blocked_road" ? {
          name: varName, type: "blocked_road", linksLayerId: String(linksLayerOptions[0]?.id || ""), blockedRoadEntries: [],
        } : varType === "contraflow" ? {
          name: varName,
          type: "contraflow",
          linksLayerId: String(linksLayerOptions[0]?.id || ""),
          destinationConfigName: String(customConfigVars.find((row) => row.type === "destination")?.name || ""),
          contraflowEntries: [],
        } : {
          name: varName,
          type: "hazard_layer",
          hazardEntries: [{
            name: "hazard_layer_1",
            hazardLayerId: String(hazardLayers[0]?.id || ""),
            hazardLayerName: String(hazardLayers[0]?.name || ""),
            hazardRerouting: false,
          }],
        };

      setConfigSaveState({ isSaving: true, error: "", success: "" });
      try {
        const nextVars = sortCustomConfigVars([...(customConfigVars || []), nextVar]);
        if (varType === "hazard_layer") {
          await persistHazardConfigVariable(nextVar);
        } else if (varType === "contraflow") {
          await persistContraflowConfigVariable(nextVar);
        } else if (varType === "blocked_road") {
          await persistBlockedRoadConfigVariable(nextVar);
        } else {
          await persistCustomConfig(nextVars);
        }

        setCustomConfigVars(nextVars);
        setActiveConfigVarName(varName);
        setIsEditingDestinations(false);
        setIsEditingStartTimes(false);
        setIsEditingHazardConfig(false);
        setIsEditingContraflow(false);
        setIsEditingBlockedRoad(false);
        setEditingConfigVarName("");
        setConfigSaveState({
          isSaving: false,
          error: "",
          success: `Created and saved ${varType} variable "${varName}".`,
        });
        return true;
      } catch (err) {
        setConfigSaveState({
          isSaving: false,
          error: String(err?.message || err),
          success: "",
        });
        return false;
      }
    },
    [customConfigVars, nodeLayerOptions, linksLayerOptions, hazardLayers, persistCustomConfig, persistHazardConfigVariable, persistContraflowConfigVariable, persistBlockedRoadConfigVariable, builderPermissions.hazardLayer]
  );

  const deleteConfigVariable = useCallback(async (name) => {
    const target = (customConfigVars || []).find((row) => row.name === name);
    if (!target || !["destination", "contraflow", "blocked_road"].includes(target.type) || name === "destinations") return false;
    if (target.type === "destination") {
      const dependents = customConfigVars.filter((row) => row.type === "contraflow" && row.destinationConfigName === name);
      if (dependents.length) {
        setConfigSaveState({ isSaving: false, error: `Change the destination config used by ${dependents.map((row) => row.name).join(", ")} before deleting "${name}".`, success: "" });
        return false;
      }
    }

    setConfigSaveState({ isSaving: true, error: "", success: "" });
    try {
      const runsSnapshot = await getDocs(collection(firestore, "model_builders", simulationIdSafe, "runs"));
      const runKey = target.type === "destination" ? "destinationConfigName"
        : target.type === "contraflow" ? "contraflowConfigName" : "blockedRoadConfigName";
      const legacyKey = target.type === "destination" ? "destination_config"
        : target.type === "contraflow" ? "contraflow_config" : "blocked_road_config";
      const draftUsers = runsSnapshot.docs.filter((run) => {
        const data = run.data() || {};
        if (String(data.status || "draft").toLowerCase() !== "draft") return false;
        const selection = data.runLayers || data.run_layers || {};
        return String(selection[runKey] || selection[legacyKey] || data[runKey] || data[legacyKey] || "") === name;
      }).map((run) => String(run.data()?.name || run.id));
      if (draftUsers.length) {
        setConfigSaveState({ isSaving: false, error: `Change the config used by draft run ${draftUsers.join(", ")} before deleting "${name}".`, success: "" });
        return false;
      }

      if (!window.confirm(`Delete ${target.type.replace("_", " ")} config "${name}"? This cannot be undone.`)) {
        setConfigSaveState({ isSaving: false, error: "", success: "" });
        return false;
      }

      const path = `${configBase}/${CONFIG_FILE_NAME}`;
      const existingConfig = (await fetchJsonFromStorage(path)) || {};
      const nextConfig = { ...existingConfig };
      delete nextConfig[name];
      await uploadString(storageRef(storage, path), JSON.stringify(nextConfig, null, 2), "raw", { contentType: "application/json" });
      customConfigStorageRef.current = nextConfig;
      try {
        await deleteDoc(doc(firestore, "model_builders", simulationIdSafe, "custom_config", name));
      } catch (deleteError) {
        await uploadString(storageRef(storage, path), JSON.stringify(existingConfig, null, 2), "raw", { contentType: "application/json" });
        customConfigStorageRef.current = existingConfig;
        throw deleteError;
      }

      const nextMetadata = { ...customConfigMetadataRef.current };
      delete nextMetadata[name];
      customConfigMetadataRef.current = nextMetadata;
      const nextVars = mergeCustomConfigVars(nextMetadata, nextConfig);
      latestCustomConfigVarsRef.current = nextVars;
      applyCustomConfigCatalog(nextVars);
      setConfigSaveState({ isSaving: false, error: "", success: `Deleted config "${name}".` });
      return true;
    } catch (error) {
      setConfigSaveState({ isSaving: false, error: String(error?.message || error), success: "" });
      return false;
    }
  }, [customConfigVars, simulationIdSafe, configBase, applyCustomConfigCatalog]);

  const clearDestinations = useCallback(() => {
    if (effectiveConfigContext.type !== "destination") return;
    const targetName = String(effectiveConfigContext.name || "").trim();
    if (!targetName) return;
    updateDestinationConfigVariable(targetName, (row) => ({ ...row, destinationIds: [] }));
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, updateDestinationConfigVariable]);

  const removeDestination = useCallback((id) => {
    if (!isEditingDestinations || effectiveConfigContext.type !== "destination") return;
    const targetName = String(effectiveConfigContext.name || "").trim();
    if (!targetName) return;
    updateDestinationConfigVariable(targetName, (row) => ({
      ...row,
      destinationIds: (row.destinationIds || []).filter((value) => String(value) !== String(id)),
    }));
    setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
  }, [effectiveConfigContext.name, effectiveConfigContext.type, isEditingDestinations, updateDestinationConfigVariable]);

  const addDestinationIdsByInput = useCallback(
    async (rawInput) => {
      if (effectiveConfigContext.type !== "destination") {
        setConfigSaveState({
          isSaving: false,
          error: "Destination ID input is only available for destination configs.",
          success: "",
        });
        return false;
      }
      if (!isEditingDestinations) {
        setConfigSaveState({
          isSaving: false,
          error: "Start destination edit mode first, then add IDs.",
          success: "",
        });
        return false;
      }

      const targetName = String(effectiveConfigContext.name || "").trim();
      if (!targetName) return false;

      const parsedIds = String(rawInput || "")
        .split(",")
        .map((v) => String(v || "").trim())
        .filter(Boolean);
      if (!parsedIds.length) return false;

      const requestedIds = [];
      const seen = new Set();
      parsedIds.forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        requestedIds.push(id);
      });

      const availableNodeIds = (Array.isArray(configNodes) ? configNodes : [])
        .map((row) => String(row?.node_id || "").trim())
        .filter(Boolean);

      if (!availableNodeIds.length) {
        setConfigSaveState({
          isSaving: false,
          error: "Node data is not loaded yet for this destination config.",
          success: "",
        });
        return false;
      }

      const currentIds = Array.isArray(effectiveConfigContext.row?.destinationIds)
        ? effectiveConfigContext.row.destinationIds.map((v) => String(v).trim()).filter(Boolean)
        : [];

      const toAdd = [];
      const alreadySelected = [];
      const missing = [];

      requestedIds.forEach((id) => {
        const exists = availableNodeIds.some((v) => idsEqual(v, id));
        if (!exists) {
          missing.push(id);
          return;
        }

        const already = currentIds.some((v) => idsEqual(v, id)) || toAdd.some((v) => idsEqual(v, id));
        if (already) {
          alreadySelected.push(id);
          return;
        }

        toAdd.push(id);
      });

      if (!toAdd.length) {
        const parts = [];
        if (alreadySelected.length) parts.push(`already selected: ${alreadySelected.join(", ")}`);
        if (missing.length) parts.push(`not found: ${missing.join(", ")}`);
        setConfigSaveState({
          isSaving: false,
          error: parts.length
            ? `No new destination IDs were added (${parts.join(" | ")}).`
            : "No new destination IDs were added.",
          success: "",
        });
        return false;
      }

      updateDestinationConfigVariable(targetName, (row) => {
        const existing = Array.isArray(row.destinationIds) ? row.destinationIds.map((v) => String(v)) : [];
        return { ...row, destinationIds: [...existing, ...toAdd] };
      });

      const notes = [];
      if (alreadySelected.length) notes.push(`already selected: ${alreadySelected.join(", ")}`);
      if (missing.length) notes.push(`not found: ${missing.join(", ")}`);
      setConfigSaveState({
        isSaving: false,
        error: "",
        success: notes.length
          ? `Added ${toAdd.length} destination ID(s): ${toAdd.join(", ")} (${notes.join(" | ")}).`
          : `Added ${toAdd.length} destination ID(s): ${toAdd.join(", ")}.`,
      });

      return true;
    },
    [
      effectiveConfigContext.type,
      effectiveConfigContext.name,
      effectiveConfigContext.row?.destinationIds,
      isEditingDestinations,
      configNodes,
      updateDestinationConfigVariable,
    ]
  );

  const saveConfig = useCallback(async () => {
    if (effectiveConfigContext.type === "blocked_road") {
      setConfigSaveState({ isSaving: true, error: "", success: "" });
      try {
        const savedRow = await persistBlockedRoadConfigVariable(effectiveConfigContext.row);
        setCustomConfigVars((prev) => prev.map((row) => row.name === savedRow.name ? savedRow : row));
        setActiveConfigVarName(savedRow.name);
        setConfigSaveState({ isSaving: false, error: "", success: `Saved blocked-road config "${savedRow.name}".` });
        setIsEditingBlockedRoad(false);
        setEditingConfigVarName("");
        configEditSnapshotRef.current = null;
        return true;
      } catch (err) {
        setConfigSaveState({ isSaving: false, error: String(err?.message || err), success: "" });
        return false;
      }
    }
    if (effectiveConfigContext.type === "contraflow") {
      setConfigSaveState({ isSaving: true, error: "", success: "" });
      try {
        const savedRow = await persistContraflowConfigVariable(effectiveConfigContext.row);
        setCustomConfigVars((prev) => prev.map((row) => row.name === savedRow.name ? savedRow : row));
        setActiveConfigVarName(savedRow.name);
        setConfigSaveState({ isSaving: false, error: "", success: `Saved contraflow config "${savedRow.name}".` });
        setIsEditingContraflow(false);
        setIsEditingBlockedRoad(false);
        setEditingConfigVarName("");
        configEditSnapshotRef.current = null;
        return true;
      } catch (err) {
        setConfigSaveState({ isSaving: false, error: String(err?.message || err), success: "" });
        return false;
      }
    }
    if (effectiveConfigContext.type === "hazard_layer") {
      setConfigSaveState({ isSaving: true, error: "", success: "" });
      try {
        const originalName = String(configEditSnapshotRef.current?.variableName || effectiveConfigContext.name || "").trim();
        const nextName = String(hazardConfigNameDraft || "").trim();
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(nextName)) {
          throw new Error("Hazard config name must start with a letter/underscore and use only letters, numbers, underscores.");
        }
        if (customConfigVars.some((row) => row.name !== originalName && row.name.toLowerCase() === nextName.toLowerCase())) {
          throw new Error(`A config variable named "${nextName}" already exists.`);
        }
        const savedRow = await persistHazardConfigVariable({ ...effectiveConfigContext.row, name: nextName }, originalName);
        setCustomConfigVars((prev) => prev.map((row) =>
          row.name === originalName ? savedRow : row
        ));
        setActiveConfigVarName(savedRow.name);
        setConfigSaveState({
          isSaving: false,
          error: "",
          success: `Saved hazard config "${savedRow.name}".`,
        });
        setIsEditingHazardConfig(false);
        setHazardConfigNameDraft("");
        setEditingConfigVarName("");
        configEditSnapshotRef.current = null;
        return true;
      } catch (err) {
        setConfigSaveState({
          isSaving: false,
          error: String(err?.message || err),
          success: "",
        });
        return false;
      }
    }

    const destinationVars = (customConfigVars || []).filter((row) => row.type === "destination");
    if (!destinationVars.length) {
      setConfigSaveState({
        isSaving: false,
        error: "Create at least one destination config variable before saving.",
        success: "",
      });
      return false;
    }

    const normalizedConfigVars = [];
    const sourceVars = Array.isArray(customConfigVars) ? customConfigVars : [];
    for (const row of sourceVars) {
      if (row?.type === "hazard_layer") {
        const varName = String(row?.name || "").trim();
        const entries = Array.isArray(row?.hazardEntries) ? row.hazardEntries : [];
        if (!entries.length) {
          setConfigSaveState({
            isSaving: false,
            error: `Config "${varName}" requires at least one hazard layer.`,
            success: "",
          });
          return false;
        }
        const normalizedEntries = [];
        for (let index = 0; index < entries.length; index += 1) {
          const entry = entries[index];
          const layerId = String(entry?.hazardLayerId || "").trim();
          const layer = hazardLayers.find((item) => String(item.id) === layerId);
          if (!layer) {
            setConfigSaveState({
              isSaving: false,
              error: `Config "${varName}" entry ${index + 1} requires an existing hazard layer.`,
              success: "",
            });
            return false;
          }
          normalizedEntries.push({
            name: `hazard_layer_${index + 1}`,
            hazardLayerId: layerId,
            hazardLayerName: String(layer.name || layer.id),
            hazardRerouting: Boolean(entry?.hazardRerouting),
          });
        }
        normalizedConfigVars.push({
          ...row,
          hazardEntries: normalizedEntries,
        });
        continue;
      }
      if (row?.type === "blocked_road") {
        const varName = String(row?.name || "").trim();
        if (!linksLayerOptions.some((layer) => String(layer.id) === String(row.linksLayerId || ""))) {
          setConfigSaveState({ isSaving: false, error: `Config "${varName}" requires an existing link layer.`, success: "" });
          return false;
        }
        normalizedConfigVars.push({ ...row, blockedRoadEntries: normalizeBlockedRoadEntries(row.blockedRoadEntries) });
        continue;
      }
      if (row?.type === "contraflow") {
        const varName = String(row?.name || "").trim();
        if (!linksLayerOptions.some((layer) => String(layer.id) === String(row.linksLayerId || ""))) {
          setConfigSaveState({ isSaving: false, error: `Config "${varName}" requires an existing link layer.`, success: "" });
          return false;
        }
        if (!sourceVars.some((config) => config.type === "destination" && config.name === row.destinationConfigName)) {
          setConfigSaveState({ isSaving: false, error: `Config "${varName}" requires an existing destination config.`, success: "" });
          return false;
        }
        const entries = normalizeContraflowEntries(row.contraflowEntries || row.linkIds);
        const invalidEntry = entries.find((entry) =>
          !entry.reverseLinkId || entry.mainLanes == null || entry.reverseLanes == null
        );
        if (invalidEntry) {
          setConfigSaveState({
            isSaving: false,
            error: `Contraflow link ${invalidEntry.linkId} requires a reverse link and valid lane counts.`,
            success: "",
          });
          return false;
        }
        normalizedConfigVars.push({ ...row, contraflowEntries: entries });
        continue;
      }
      if (row?.type !== "start_times") {
        normalizedConfigVars.push(row);
        continue;
      }

      const varName = String(row?.name || "").trim() || "start_times";
      if (row?.granularSurvey) {
        const hasBlankValue = (row.startTimeRanges || []).some((range) =>
          [range?.rangeStart, range?.rangeEnd, range?.probabilityWeightPct]
            .some((value) => String(value ?? "").trim() === "")
        );
        if (hasBlankValue) {
          setConfigSaveState({
            isSaving: false,
            error: `Config "${varName}" requires all granular range values.`,
            success: "",
          });
          return false;
        }
        const validation = validateStartTimeRanges(row.startTimeRanges);
        if (!validation.valid) {
          setConfigSaveState({
            isSaving: false,
            error: `Config "${varName}": ${validation.error}`,
            success: "",
          });
          return false;
        }
        normalizedConfigVars.push({
          ...row,
          minDepartureTime: validation.minDepartureTime,
          maxDepartureTime: validation.maxDepartureTime,
          totalPopulationPct: 100,
          granularSurvey: true,
          startTimeRanges: validation.ranges,
        });
        continue;
      }
      const minRaw = String(row?.minDepartureTime ?? "").trim();
      const maxRaw = String(row?.maxDepartureTime ?? "").trim();

      if (!minRaw || !maxRaw) {
        setConfigSaveState({
          isSaving: false,
          error: `Config "${varName}" requires both departure_min and departure_max.`,
          success: "",
        });
        return false;
      }

      const minNum = Number(minRaw);
      const maxNum = Number(maxRaw);
      if (!Number.isFinite(minNum) || !Number.isFinite(maxNum)) {
        setConfigSaveState({
          isSaving: false,
          error: `Config "${varName}" requires numeric departure values.`,
          success: "",
        });
        return false;
      }
      if (!Number.isInteger(minNum) || !Number.isInteger(maxNum)) {
        setConfigSaveState({
          isSaving: false,
          error: `Config "${varName}" requires integer minutes.`,
          success: "",
        });
        return false;
      }
      if (minNum < 0 || maxNum < 0) {
        setConfigSaveState({
          isSaving: false,
          error: `Config "${varName}" requires non-negative minutes.`,
          success: "",
        });
        return false;
      }
      if (maxNum < minNum) {
        setConfigSaveState({
          isSaving: false,
          error: `Config "${varName}" must have departure_max >= departure_min.`,
          success: "",
        });
        return false;
      }

      normalizedConfigVars.push({
        ...row,
        minDepartureTime: minNum,
        maxDepartureTime: maxNum,
        totalPopulationPct: 100,
        granularSurvey: false,
        startTimeRanges: Array.isArray(row?.startTimeRanges) ? row.startTimeRanges : [],
      });
    }

    setConfigSaveState({ isSaving: true, error: "", success: "" });
    try {
      const { count, destinationCount, startTimesCount, hazardConfigCount, contraflowCount, blockedRoadCount } = await persistCustomConfig(normalizedConfigVars);
      setCustomConfigVars(normalizedConfigVars);
      setConfigSaveState({
        isSaving: false,
        error: "",
        success: `Saved ${count} config variable${count === 1 ? "" : "s"} (${destinationCount} destination, ${startTimesCount} start_times, ${hazardConfigCount} hazard_layer, ${contraflowCount} contraflow, ${blockedRoadCount} blocked road).`,
      });
      setIsEditingDestinations(false);
      setIsEditingStartTimes(false);
      setIsEditingHazardConfig(false);
      setIsEditingContraflow(false);
      setIsEditingBlockedRoad(false);
      setEditingConfigVarName("");
      configEditSnapshotRef.current = null;
      setChatLog((prev) => [
        ...prev,
        {
          role: "system",
          text: "Config saved successfully.",
          ts: Date.now(),
        },
      ]);
      return true;
    } catch (err) {
      setConfigSaveState({
        isSaving: false,
        error: String(err?.message || err),
        success: "",
      });
      return false;
    }
  }, [customConfigVars, linksLayerOptions, hazardLayers, persistCustomConfig, persistHazardConfigVariable, persistContraflowConfigVariable, persistBlockedRoadConfigVariable, effectiveConfigContext, hazardConfigNameDraft]);

  const isConfigEditActive = isEditingDestinations || isEditingStartTimes || isEditingHazardConfig || isEditingContraflow || isEditingBlockedRoad;

  useEffect(() => {
    if (!isConfigEditActive) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [isConfigEditActive]);

  const scrollBackToChat = useCallback(() => {
    workingScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const changeRightTab = useCallback(
    (nextTab) => {
      const wanted = String(nextTab || "");
      if (!wanted) return;
      if (wanted === "hazards" && !builderPermissions.hazardLayer) return;
      if (wanted === rightTab) {
        if (wanted === "chat") scrollBackToChat();
        return;
      }
      if (rightTab === "config" && isConfigEditActive) {
        const shouldLeave = window.confirm("Discard unsaved config changes and leave the Config tab?");
        if (!shouldLeave) return;
        cancelConfigEdit();
      }
      setMapFocusTarget(null);
      setMapFitSelection(null);
      setRightTab(wanted);
      if (wanted === "results") setReturnToResultsFromAnalysis(false);
      if (wanted === "chat") scrollBackToChat();
    },
    [rightTab, isConfigEditActive, cancelConfigEdit, scrollBackToChat, builderPermissions.hazardLayer]
  );

  useEffect(() => {
    if (!builderPermissions.hazardLayer && rightTab === "hazards") setRightTab("settings");
  }, [builderPermissions.hazardLayer, rightTab]);

  const onSubmitMapSearch = useCallback(async () => {
    const q = mapSearch.trim();
    if (!q) return;

    const { lat, lng } = await geocodeAddress(q);
    if (lat == null || lng == null) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: `Could not geocode: "${q}"`, ts: Date.now() },
      ]);
      return;
    }

    setMapCenter({ lat, lng });
    setMapZoom(15.5);
  }, [mapSearch, geocodeAddress]);

  // -------------------------
  // Polygon helpers
  // -------------------------
  const polygonCoordsForCloudRun = useCallback(() => {
    const poly = savedPolygon;
    if (!poly || poly.length < 3) return null;

    // CloudRun expects [[lon,lat], ...]
    const coords = poly.map((p) => [p.lng, p.lat]);

    // Close polygon if not closed
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first);

    return coords;
  }, [savedPolygon]);

  const runOsmNetworkJob = useCallback(
    async (blockId) => {
      const coords = polygonCoordsForCloudRun();
      if (!coords) throw new Error("Draw and save a polygon first.");

      const result = await cloudRunBuildOsmNetwork({
        simulationId: SIMULATION_ID,
        layerId: "links_default",
        layerName: "Links (default)",
        runId: blockId,
        blockId,
        polygon: {
          type: "Polygon",
          coordinates: [coords],
        },
      });

      if (result?.status !== "submitted") {
        throw new Error("The OSM network API did not accept the job.");
      }

      await waitForOperationCompletion(database, blockId);
      await loadGisLayers();
      if (selectedLayer?.id === "links_default" || selectedLayer?.id === "nodes_default") {
        await refreshSelectedLayer();
      }
      return result;
    },
    [SIMULATION_ID, loadGisLayers, polygonCoordsForCloudRun, refreshSelectedLayer, selectedLayer?.id]
  );

  const createLayerFromSavedPolygon = useCallback(
    async ({ name }) => {
      const layerName = String(name || "").trim();
      if (!layerName) {
        setLayerSaveState({
          isSaving: false,
          error: "Layer name is required.",
          success: "",
        });
        return false;
      }

      const coords = polygonCoordsForCloudRun();
      if (!coords || coords.length < 4) {
        setLayerSaveState({
          isSaving: false,
          error: "Save a polygon on the map first.",
          success: "",
        });
        return false;
      }

      const baseId = layerName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "drawn_polygon";
      const geojson = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            id: baseId,
            properties: {
              polygon_id: baseId,
              feature_name: layerName,
              source: "saved_map_polygon",
            },
            geometry: {
              type: "Polygon",
              coordinates: [coords],
            },
          },
        ],
      };

      return createLayerFromText({
        name: layerName,
        type: "polygon",
        sourceText: JSON.stringify(geojson, null, 2),
        sourceLabel: "saved map polygon",
      });
    },
    [createLayerFromText, polygonCoordsForCloudRun]
  );

  const onStartPolygon = useCallback(() => {
    setIsSelectingPolygon(true);
    setDraftPolygon([]);
    setSavedPolygon(null);
  }, []);
  
  const onCancelPolygon = useCallback(() => {
    setIsSelectingPolygon(false);
    setDraftPolygon([]);
    setSavedPolygon(null);
  }, []);
  
  const onAddPolygonPoint = useCallback((pt) => {
    setDraftPolygon((prev) => [...prev, pt]);
  }, []);
  
  const onUndoPolygonPoint = useCallback(() => {
    setDraftPolygon((prev) => prev.slice(0, -1));
  }, []);
  
  const onSavePolygon = useCallback(() => {
    setDraftPolygon((poly) => {
      if (!poly || poly.length < 3) return poly;
      setSavedPolygon(poly);
      setIsSelectingPolygon(false);
      return poly;
    });
  }, []);

  // -------------------------
  // Create DT
  // -------------------------
  const runRoadNetworkFromPolygon = useCallback(async () => {
    if (!builderPermissions.fetchData) return;
    if (isRoadNetworkRunning) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: "Road network run is already in progress.", ts: Date.now() },
      ]);
      return;
    }

    const coords = polygonCoordsForCloudRun();
    if (!coords) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: "Draw and SAVE a polygon first, then get road network.", ts: Date.now() },
      ]);
      return;
    }

    const blockIdGraph = `${SIMULATION_ID}_${Date.now()}_osm_network`;
    setOperationBlocks((prev) => [{ blockId: blockIdGraph, kind: "osm" }, ...prev]);
    setChatLog((prev) => [
      ...prev,
      { role: "system", text: `Starting road network run: ${blockIdGraph}`, ts: Date.now() },
    ]);

    setIsRoadNetworkRunning(true);
    try {
      await runOsmNetworkJob(blockIdGraph);
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: `OSM road network complete: ${blockIdGraph}.`, ts: Date.now() },
      ]);
    } catch (error) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: `OSM road network failed: ${error?.message || error}`, ts: Date.now() },
      ]);
    } finally {
      setIsRoadNetworkRunning(false);
    }
  }, [isRoadNetworkRunning, polygonCoordsForCloudRun, SIMULATION_ID, runOsmNetworkJob, builderPermissions.fetchData]);

  const runParcelDataFromPolygon = useCallback(async () => {
    if (!builderPermissions.fetchData) return;
    if (isParcelDataRunning) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: "Parcel data run is already in progress.", ts: Date.now() },
      ]);
      return;
    }

    const coords = polygonCoordsForCloudRun();
    if (!coords) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: "Draw and SAVE a polygon first, then get parcel data.", ts: Date.now() },
      ]);
      return;
    }

    const blockIdParcel = `${SIMULATION_ID}_${Date.now()}_parcel`;
    setOperationBlocks((prev) => [{ blockId: blockIdParcel, kind: "parcel" }, ...prev]);
    setChatLog((prev) => [
      ...prev,
      { role: "system", text: `Starting parcel data run: ${blockIdParcel}`, ts: Date.now() },
    ]);

    setIsParcelDataRunning(true);
    try {
      const res = await cloudRunRunParcels({
        model_id: SIMULATION_ID,
        prefix: PREFIX,
        blockId: blockIdParcel,
        coords,
      });

      if (res?.ok) {
        const canRefresh = selectedLayer?.type === "parcels";
        setChatLog((prev) => [
          ...prev,
          {
            role: "system",
            text: canRefresh
              ? `Parcels run complete: ${blockIdParcel}. Refreshing current layer...`
              : `Parcels run complete: ${blockIdParcel}. Parcel output updated in storage.`,
            ts: Date.now(),
          },
        ]);
        await refreshParcels();
      } else {
        setChatLog((prev) => [
          ...prev,
          { role: "system", text: `Parcels run failed: ${res?.error || "unknown error"}`, ts: Date.now() },
        ]);
      }
    } finally {
      setIsParcelDataRunning(false);
    }
  }, [isParcelDataRunning, polygonCoordsForCloudRun, SIMULATION_ID, selectedLayer?.type, refreshParcels, builderPermissions.fetchData]);

  const createDigitalTwin = useCallback(async () => {
    if (!builderPermissions.fetchData) return;
    if (isRoadNetworkRunning || isParcelDataRunning) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: "A road/parcel run is already in progress. Please wait.", ts: Date.now() },
      ]);
      return;
    }

    const coords = polygonCoordsForCloudRun();
    if (!coords) {
      setChatLog((prev) => [
        ...prev,
        { role: "system", text: "Cannot run /dt: no saved polygon found.", ts: Date.now() },
      ]);
      return;
    }

    const ts = Date.now();
    const blockIdGraph = `${SIMULATION_ID}_${ts}_osm_network`;
    const blockIdParcel = `${SIMULATION_ID}_${ts}_parcel`;

    // Create blocks immediately in UI
    setOperationBlocks((prev) => [
      { blockId: blockIdGraph, kind: "osm" },
      { blockId: blockIdParcel, kind: "parcel" },
      ...prev,
    ]);

    setChatLog((prev) => [
      ...prev,
      { role: "system", text: `Starting DT runs: ${blockIdGraph} + ${blockIdParcel}`, ts: Date.now() },
    ]);

    setIsRoadNetworkRunning(true);
    setIsParcelDataRunning(true);

    // Run Graph + Parcels in parallel (each refreshes its own layer on completion)
    const runGraph = (async () => {
      try {
        await runOsmNetworkJob(blockIdGraph);
        setChatLog((prev) => [
          ...prev,
          { role: "system", text: `OSM road network complete: ${blockIdGraph}.`, ts: Date.now() },
        ]);
      } catch (error) {
        setChatLog((prev) => [
          ...prev,
          { role: "system", text: `OSM road network failed: ${error?.message || error}`, ts: Date.now() },
        ]);
      } finally {
        setIsRoadNetworkRunning(false);
      }
    })();

    const runParcels = (async () => {
      try {
        const res = await cloudRunRunParcels({
          model_id: SIMULATION_ID,
          prefix: PREFIX,
          blockId: blockIdParcel,
          coords,
        });

        if (res?.ok) {
          const canRefresh = selectedLayer?.type === "parcels";
          setChatLog((prev) => [
            ...prev,
            {
              role: "system",
              text: canRefresh
                ? `Parcels run complete: ${blockIdParcel}. Refreshing current layer...`
                : `Parcels run complete: ${blockIdParcel}. Parcel output updated in storage.`,
              ts: Date.now(),
            },
          ]);
          await refreshParcels();
        } else {
          setChatLog((prev) => [
            ...prev,
            { role: "system", text: `Parcels run failed: ${res?.error || "unknown error"}`, ts: Date.now() },
          ]);
        }
      } finally {
        setIsParcelDataRunning(false);
      }
    })();

    await Promise.allSettled([runGraph, runParcels]);
  }, [
    isRoadNetworkRunning,
    isParcelDataRunning,
    builderPermissions.fetchData,
    polygonCoordsForCloudRun,
    SIMULATION_ID,
    runOsmNetworkJob,
    refreshParcels,
    selectedLayer?.type,
  ]);

  // -------------------------
  // Chat submit
  // -------------------------
  const onChatSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const text = chatInput.trim();
      if (!text) return;

      setChatLog((prev) => [...prev, { role: "user", text, ts: Date.now() }]);
      setChatInput("");

      // Only command recognized: /dt
      if (text === "/dt") {
        if (!builderPermissions.fetchData) {
          setChatLog((prev) => [...prev, { role: "system", text: "Enable Fetch data in Settings before using /dt.", ts: Date.now() }]);
          return;
        }
        if (!savedPolygon || savedPolygon.length < 3) {
          setChatLog((prev) => [
            ...prev,
            { role: "system", text: "Draw and SAVE a polygon first, then run /dt.", ts: Date.now() },
          ]);
          return;
        }
        await createDigitalTwin();
      }
    },
    [chatInput, savedPolygon, createDigitalTwin, builderPermissions.fetchData]
  );

  const mapNodes = isDestinationConfigVisible || isContraflowConfigVisible ? configNodes : nodes;
  const mapLinks = isRoadConfigVisible && effectiveConfigContext.linksLayerId ? contraflowLinks : links;
  const mapLayer = activeLayer;
  const mapLayerId = String(activeLayerId || "");
  const mapSelectedNodeIds = useMemo(
    () => isDestinationConfigVisible
      ? destinationMapContext.destinationIds
      : isContraflowConfigVisible
        ? new Set((contraflowDestinationConfig?.destinationIds || []).map(String))
        : selectedNodeIds,
    [isDestinationConfigVisible, isContraflowConfigVisible, destinationMapContext, contraflowDestinationConfig, selectedNodeIds]
  );
  const mapSelectedLinkIds = isRoadConfigVisible
    ? effectiveConfigContext.linkIds
    : selectedLinkIds;
  const mapNodeToggle = isDestinationConfigVisible
    ? (isEditingDestinations ? toggleDestinationNode : undefined)
    : isContraflowConfigVisible
      ? undefined
    : toggleNode;
  const mapOverlayNodes = isContraflowConfigVisible
    ? configNodes.filter((node) => mapSelectedNodeIds.has(String(node?.node_id)))
    : rightTab === "edit" &&
    activeLayer === "links" &&
    (addLinkDraftMode === "node_layer" || addLinkDraftMode === "ready")
      ? nodes
      : [];
  const mapOverlayPolygons =
    rightTab === "edit" &&
    isFilteringLayer &&
    (activeLayer === "links" || activeLayer === "nodes" || activeLayer === "parcels")
      ? filterPolygonOverlayRows
      : [];
  const mapDraftLinkFocus =
    rightTab === "edit" && addLinkDraftMode === "ready" && addLinkDraftInfo?.linkId
      ? {
          linkId: String(addLinkDraftInfo.linkId),
          endpoint:
            draftTopologyStep === "start"
              ? "start"
              : draftTopologyStep === "end"
              ? "end"
              : null,
        }
      : null;
  const mapDraftLinkCandidateArrows = useMemo(() => {
    if (addLinkDraftMode !== "ready" || !addLinkDraftInfo?.linkId) return [];
    const endpoint = draftTopologyStep === "start" ? "start" : draftTopologyStep === "end" ? "end" : null;
    if (!endpoint) return [];
    const choice = draftTopologyChoices?.[endpoint];
    if (choice !== "nearest_link") return [];

    const optionIds = draftNearestLinkOptions?.[endpoint] || [];
    if (!optionIds.length) return [];
    const selectedIds = new Set(draftNearestLinkSelected?.[endpoint] || []);

    const draft = (links || []).find((l) => String(l?.link_id) === String(addLinkDraftInfo.linkId));
    const endpointPt = endpoint === "start" ? draft?.path?.[0] : draft?.path?.[draft?.path?.length - 1];
    if (!endpointPt) return [];

    return optionIds
      .map((id) => {
        const row = (links || []).find((l) => String(l?.link_id) === String(id));
        if (!row?.path?.length || row.path.length < 2) return null;
        const proj = closestPointOnPath(row.path, endpointPt);
        if (!proj) return null;
        const a = row.path[proj.segIdx];
        const b = row.path[proj.segIdx + 1];
        if (!a || !b) return null;
        const foot = proj.point;
        const dx = Number(b.lng) - Number(a.lng);
        const dy = Number(b.lat) - Number(a.lat);
        const mag = Math.sqrt(dx * dx + dy * dy) || 1;
        const scale = 0.0002;
        const tip = { lat: Number(foot.lat) + (dy / mag) * scale, lng: Number(foot.lng) + (dx / mag) * scale };
        return {
          id: String(id),
          selected: selectedIds.has(String(id)),
          path: [foot, tip],
        };
      })
      .filter(Boolean);
  }, [
    addLinkDraftMode,
    addLinkDraftInfo,
    draftTopologyStep,
    draftTopologyChoices,
    draftNearestLinkOptions,
    draftNearestLinkSelected,
    links,
  ]);


  // -------------------------
  // UI layout
  // -------------------------
  const resizeBuilderPanes = useCallback((clientX) => {
    const layout = builderLayoutRef.current;
    if (!layout) return;
    const bounds = layout.getBoundingClientRect();
    if (!bounds.width) return;
    const dividerWidth = 14;
    const minimumWorkingWidth = Math.min(420, bounds.width * 0.45);
    const minimumMapWidth = Math.min(520, bounds.width * 0.45);
    const minimumPercent = (minimumWorkingWidth / bounds.width) * 100;
    const maximumPercent = ((bounds.width - minimumMapWidth - dividerWidth) / bounds.width) * 100;
    const requestedPercent = ((clientX - bounds.left) / bounds.width) * 100;
    setWorkingPanePercent(Math.max(minimumPercent, Math.min(maximumPercent, requestedPercent)));
  }, []);

  const startDividerDrag = useCallback((event) => {
    dividerDragRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeBuilderPanes(event.clientX);
  }, [resizeBuilderPanes]);

  const moveDivider = useCallback((event) => {
    if (!dividerDragRef.current) return;
    resizeBuilderPanes(event.clientX);
  }, [resizeBuilderPanes]);

  const stopDividerDrag = useCallback((event) => {
    dividerDragRef.current = false;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handlePresentationActivate = useCallback((presentation, presentationRows) => {
    const binding = presentation?.entityBinding;
    const viewContext = presentation?.viewContext;
    const primaryLayer = Number(viewContext?.v) >= 2 ? viewContext?.primaryLayer : null;
    const type = String(binding?.type || primaryLayer?.type || "").toLowerCase();
    // A table bound to a stored result names no Builder layer, so register the run's own network as
    // a selectable one before resolving the id the map should switch to.
    const resultLayer = binding?.layerId ? null : resultLayerDoc(binding, presentation?.title);
    if (resultLayer) {
      setResultLayers((current) => current.some((layer) => layer.id === resultLayer.id)
        ? current
        : [...current, resultLayer]);
      // Selection below resolves through this ref, which is otherwise only refreshed on the next
      // render - by which time the layer switch would already have been rejected as unknown.
      if (!layerByIdRef.current.has(resultLayer.id)) {
        layerByIdRef.current = new Map(layerByIdRef.current).set(resultLayer.id, resultLayer);
      }
    }
    // The binding decides which entities the table is about, so it outranks whatever layer happened
    // to be on screen when the result was published. Falling back the other way pointed the map at a
    // similar-looking layer, which silently disabled the colour scale and the row selection.
    const layerId = String(bindingLayerKey(binding) || primaryLayer?.layerId || "");
    const idKey = String(binding?.idKey || "");
    if (!layerId || !["links", "nodes", "parcels"].includes(type)) {
      // A chart or board draws nothing on the map, so the previous table's entities must not be
      // left highlighted underneath it.
      clearSelections();
      return;
    }
    const rowIds = (presentationRows || [])
      .map((row) => String(idKey ? row?.[idKey] ?? "" : "").trim())
      .filter(Boolean);
    const ids = new Set(presentation?.kind === "table" ? rowIds : rowIds.slice(0, 500));
    const selectedIds = presentation?.kind === "table" ? new Set() : ids;
    if (activeLayerIdRef.current !== layerId) onChangeLayerSelection(layerId);
    setDestinationMapConfigName(type === "nodes" ? String(viewContext?.destinationConfig || "") : "");
    setAgentMapState((current) => ({ ...(current || {}), primaryLayer: { layerId, type } }));
    if (type === "links") setSelectedLinkIds(selectedIds);
    if (type === "nodes") setSelectedNodeIds(selectedIds);
    if (type === "parcels") setSelectedParcelIds(selectedIds);
    setMapFitSelection({
      type,
      ids: [...ids],
      wholeLayer: presentation?.kind === "map" || !ids.size,
      key: Date.now(),
    });

    if (viewContext && [1, 2].includes(Number(viewContext.v))) {
      const hazardLayerId = String(viewContext.hazardLayerId || "");
      const hazardSnapshotId = String(viewContext.hazardSnapshotId || "");
      const destinationConfig = String(viewContext.destinationConfig || "");
      setAgentMapState((current) => ({
        ...(current || {}),
        destinationLayer: destinationConfig
          ? { visible: true, configName: destinationConfig }
          : { visible: false },
        hazardLayer: hazardLayerId
          ? { visible: true, layerId: hazardLayerId, snapshotId: hazardSnapshotId || null }
          : { visible: false },
      }));
    }
  }, [clearSelections, onChangeLayerSelection]);

  const setBoundSelection = useCallback((binding, entityIds) => {
    const type = String(binding?.type || "");
    const layerId = bindingLayerKey(binding);
    if (!layerId || !["links", "nodes", "parcels"].includes(type)) return;
    if (String(activeLayerId || "") !== layerId) onChangeLayerSelection(layerId);
    const ids = new Set((entityIds || []).map(String));
    if (type === "links") setSelectedLinkIds(ids);
    if (type === "nodes") setSelectedNodeIds(ids);
    if (type === "parcels") setSelectedParcelIds(ids);
  }, [activeLayerId, onChangeLayerSelection]);

  const selectTemporalEntity = useCallback((presentation, segment) => {
    const type = segment?.entityType === "link" ? "links" : segment?.entityType === "node" ? "nodes" : "";
    const id = String(segment?.entityId || "");
    const resultId = String(presentation?.network?.resultId || "");
    if (!type || !id || !resultId) return;
    const binding = {
      type,
      resultId,
      idKey: type === "links" ? "link_id" : "node_id",
      fileName: type === "links" ? presentation.network.linksFileName : presentation.network.nodesFileName,
    };
    const layer = resultLayerDoc(binding, presentation.title);
    if (!layer) return;
    setResultLayers((current) => current.some((item) => item.id === layer.id) ? current : [...current, layer]);
    if (!layerByIdRef.current.has(layer.id)) layerByIdRef.current = new Map(layerByIdRef.current).set(layer.id, layer);
    if (activeLayerIdRef.current !== layer.id) onChangeLayerSelection(layer.id);
    setSelectedLinkIds(new Set(type === "links" ? [id] : []));
    setSelectedNodeIds(new Set(type === "nodes" ? [id] : []));
    setAgentMapState((current) => ({ ...(current || {}), primaryLayer: { layerId: layer.id, type } }));
    setDestinationMapConfigName("");
  }, [onChangeLayerSelection]);

  const toggleBoundEntity = useCallback((binding, entityId) => {
    const type = String(binding?.type || "");
    const layerId = bindingLayerKey(binding);
    const id = String(entityId || "");
    if (!id || !layerId || !["links", "nodes", "parcels"].includes(type)) return;
    if (String(activeLayerId || "") !== layerId) onChangeLayerSelection(layerId);
    const toggle = (current) => {
      const next = new Set(String(activeLayerId || "") === layerId ? current : []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    };
    if (type === "links") setSelectedLinkIds(toggle);
    if (type === "nodes") setSelectedNodeIds(toggle);
    if (type === "parcels") setSelectedParcelIds(toggle);
  }, [activeLayerId, onChangeLayerSelection]);

  const zoomBoundEntities = useCallback((binding, entityIds) => {
    const type = String(binding?.type || "");
    const layerId = bindingLayerKey(binding);
    const ids = (entityIds || []).map(String).filter(Boolean);
    if (!layerId || !ids.length || !["links", "nodes", "parcels"].includes(type)) return;
    if (String(activeLayerId || "") !== layerId) onChangeLayerSelection(layerId);
    setMapFitSelection({ type, ids, wholeLayer: false, key: Date.now() });
  }, [activeLayerId, onChangeLayerSelection]);

  const activatePresentationFromChat = useCallback((presentationId, messageId = "") => {
    trackBuilderEvent("builder_chat_analysis_opened", { builder_id: simulationIdSafe, tab: "chat", chat_id: activeAgentSessionId, analysis_type: "presentation" });
    setClosedAnalysisJobId("");
    setRequestedPresentationId(String(presentationId || ""));
    setFocusedPresentationMessageId(String(messageId || ""));
    setPresentationActivationKey((value) => value + 1);
  }, [activeAgentSessionId, simulationIdSafe]);

  const openAnalysisFromChat = useCallback((job, tab = "") => {
    trackBuilderEvent("builder_chat_analysis_opened", { builder_id: simulationIdSafe, tab: "chat", chat_id: activeAgentSessionId, analysis_type: "job" });
    setClosedAnalysisJobId("");
    setRequestedAnalysisJob(job);
    setRequestedAnalysisTab(tab);
    setRequestedAnalysisJobId(String(job?.id || ""));
    setRequestedAnalysisSessionId(activeAgentSessionId);
    setAnalysisActivationKey((value) => value + 1);
  }, [activeAgentSessionId, simulationIdSafe]);

  const handleAgentSessionChange = useCallback((sessionId) => {
    if (sessionId === activeAgentSessionIdRef.current) return;
    activeAgentSessionIdRef.current = sessionId;
    setActiveAgentSessionId(sessionId);
    setAnalysisJobSnapshot({ sessionId, jobs: [] });
    setRequestedAnalysisJobId("");
    setRequestedAnalysisJob(null);
    setClosedAnalysisJobId("");
    setAnalysisMapOverlay(null);
  }, []);

  const handleWorkspaceClose = useCallback((jobId) => {
    setRequestedPresentationId("");
    setRequestedAnalysisJobId("");
    setRequestedAnalysisJob(null);
    setClosedAnalysisJobId(jobId);
  }, []);

  const showAnalysisOnChatMap = useCallback(() => {
    if (rightTab !== "results") return;
    setReturnToResultsFromAnalysis(true);
    setRightTab("chat");
  }, [rightTab]);

  const returnToSimulationResults = useCallback(() => {
    setReturnToResultsFromAnalysis(false);
    setRequestedPresentationId("");
    setRequestedAnalysisJobId("");
    setRequestedAnalysisJob(null);
    setDismissAnalysisKey((value) => value + 1);
    setRightTab("results");
  }, []);

  const clearResultSelections = useCallback(() => {
    setResultSelectedLinkIds(new Set());
    setResultSelectedNodeIds(new Set());
  }, []);

  const toggleResultLink = useCallback((id) => {
    setResultSelectedLinkIds((current) => {
      const next = new Set(current);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleResultNode = useCallback((id) => {
    setResultSelectedNodeIds((current) => {
      const next = new Set(current);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!simulationIdSafe || !activeAgentSessionId || !userId) return undefined;

    const timer = setTimeout(() => {
      const polygonPoints = (isSelectingPolygon ? draftPolygon : savedPolygon) || [];
      const normalizedPoints = polygonPoints
        .map((point) => ({ lat: Number(point?.lat), lng: Number(point?.lng) }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
      const hasCustomPolygon = normalizedPoints.length > 0;
      const isResultsMap = rightTab === "results";
      const resultLayerId = String(resultMapContext?.layerId || "");
      const primaryLayer = isResultsMap
        ? resultLayerId ? {
            layerId: resultLayerId,
            type: String(resultMapContext?.entityType || "links"),
            name: String(resultMapContext?.runName || "Simulation results"),
            filename: "",
          } : null
        : mapLayerId
        ? {
            layerId: mapLayerId,
            type: String(mapLayer || ""),
            name: String(
              (isDestinationConfigVisible
                ? nodeLayerOptions.find((layer) => String(layer.id) === mapLayerId)
                : selectedLayer)?.name || ""
            ),
            filename: String(
              (isDestinationConfigVisible
                ? nodeLayerOptions.find((layer) => String(layer.id) === mapLayerId)
                : selectedLayer)?.filename || ""
            ),
          }
        : null;
      const selectionType = isResultsMap ? String(resultMapContext?.entityType || "links") : mapLayer;
      const selectedIds = isResultsMap
        ? selectionType === "nodes" ? [...resultSelectedNodeIds] : [...resultSelectedLinkIds]
        : mapLayer === "links"
        ? [...selectedLinkIds]
        : mapLayer === "nodes"
          ? [...mapSelectedNodeIds]
          : mapLayer === "parcels"
            ? [...selectedParcelIds]
            : mapLayer === "polygon"
              ? [...selectedPolygonIds]
              : [];
      const status = {
        userId,
        mode: isResultsMap ? "simulation_results" : hasCustomPolygon ? "custom_polygon" : isDestinationConfigVisible ? "destination" : "layer",
        source: isResultsMap ? "simulation_results" : "builder_map",
        primaryLayer,
        customPolygon: hasCustomPolygon
          ? {
              status: isSelectingPolygon ? "draft" : "saved",
              points: normalizedPoints,
            }
          : null,
        selection: primaryLayer
          ? {
              type: String(selectionType || ""),
              layerId: isResultsMap ? resultLayerId : mapLayerId,
              ids: selectedIds.slice(0, 500).map(String),
              count: selectedIds.length,
              truncated: selectedIds.length > 500,
            }
          : null,
        result: isResultsMap ? {
          runId: String(resultMapContext?.runId || ""),
          runName: String(resultMapContext?.runName || ""),
          timeSeconds: Number(resultMapContext?.timeSeconds || 0),
          selectedLinks: {
            layerId: String(resultMapContext?.linksLayerId || ""),
            ids: [...resultSelectedLinkIds].slice(0, 500).map(String),
            count: resultSelectedLinkIds.size,
            truncated: resultSelectedLinkIds.size > 500,
          },
          selectedNodes: {
            layerId: String(resultMapContext?.nodesLayerId || ""),
            ids: [...resultSelectedNodeIds].slice(0, 500).map(String),
            count: resultSelectedNodeIds.size,
            truncated: resultSelectedNodeIds.size > 500,
          },
        } : null,
        destinationLayer: isDestinationConfigVisible
          ? {
              visible: true,
              configName: destinationMapContext.name,
              nodeLayerId: destinationMapContext.nodeLayerId,
              visibilityLinksLayerId: destinationMapContext.visibilityLinksLayerId,
              destinationIds: [...destinationMapContext.destinationIds].slice(0, 500).map(String),
              destinationCount: destinationMapContext.destinationIds.size,
            }
          : null,
        hazardLayer: {
          visible: rightTab === "hazards" || isAgentHazardVisible,
          layerId: activeHazardLayerId || null,
          snapshotId: activeHazardSnapshotId || null,
        },
        revision: Date.now(),
        updatedAt: databaseServerTimestamp(),
      };
      const statusRef = databaseRef(
        database,
        `model_builders/${simulationIdSafe}/chat_sessions/${activeAgentSessionId}/ui/current_map_status/${userId}`
      );
      setDatabaseValue(statusRef, status).catch((error) => {
        console.warn("Failed to publish current map status:", error);
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [
    simulationIdSafe,
    activeAgentSessionId,
    userId,
    isSelectingPolygon,
    draftPolygon,
    savedPolygon,
    mapLayer,
    mapLayerId,
    selectedLayer,
    selectedLinkIds,
    mapSelectedNodeIds,
    selectedParcelIds,
    selectedPolygonIds,
    isDestinationConfigVisible,
    destinationMapContext,
    nodeLayerOptions,
    rightTab,
    isAgentHazardVisible,
    activeHazardLayerId,
    activeHazardSnapshotId,
    resultMapContext,
    resultSelectedLinkIds,
    resultSelectedNodeIds,
  ]);

  const builderTabs = (
    <nav className="mb-tabs mb-toolbarTabs" aria-label="Builder sections">
      {["chat", "edit", "config", ...(builderPermissions.hazardLayer ? ["hazards"] : []), "run", "results", ...(podcastRuns.length ? ["podcasts"] : []), "knowledge", "settings"].map((tab) => (
        <button
          key={tab}
          className={`mb-tabBtn ${rightTab === tab ? "isActive" : ""}`}
          type="button"
          onClick={() => changeRightTab(tab)}
        >
          {tab.charAt(0).toUpperCase() + tab.slice(1)}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="mb-shell">
      {toolbarPortal ? createPortal(builderTabs, toolbarPortal) : null}
      <div className="mb-layout" ref={builderLayoutRef} style={{ "--working-pane-width": `${workingPanePercent}%` }}>
        {/* Map column (displayed on the right via CSS) */}
        <div className="mb-left">
          <AgentResultsWorkspace
            key={activeAgentSessionId}
            showResultsDock={rightTab !== "edit" && rightTab !== "config"}
            onRequireMap={showAnalysisOnChatMap}
            dismissActivationKey={dismissAnalysisKey}
            returnToSimulationResults={rightTab === "chat" && returnToResultsFromAnalysis}
            onReturnToSimulationResults={returnToSimulationResults}
            presentations={agentPresentations}
            presentationsHydrated={agentPresentationsHydrated}
            presentationScopeKey={activeAgentSessionId}
            latestQuestionAt={latestAgentQuestion.sessionId === activeAgentSessionId ? latestAgentQuestion.at : null}
            requestedActiveId={requestedPresentationId}
            activationKey={presentationActivationKey}
            focusedMessageId={focusedPresentationMessageId}
            onFocusMessage={setFocusedPresentationMessageId}
            onActivate={handlePresentationActivate}
            selectedIdsByType={{
              links: selectedLinkIds,
              nodes: selectedNodeIds,
              parcels: selectedParcelIds,
            }}
            onSetBoundSelection={setBoundSelection}
            onToggleBoundEntity={toggleBoundEntity}
            onZoomBoundEntities={zoomBoundEntities}
            onMapOverlayChange={setAnalysisMapOverlay}
            onSelectTemporalEntity={selectTemporalEntity}
            analysisJobs={agentAnalysisJobs}
            requestedAnalysisJobId={requestedAnalysisSessionId === activeAgentSessionId ? requestedAnalysisJobId : ""}
            requestedAnalysisJob={requestedAnalysisSessionId === activeAgentSessionId ? requestedAnalysisJob : null}
            requestedAnalysisTab={requestedAnalysisSessionId === activeAgentSessionId ? requestedAnalysisTab : ""}
            analysisActivationKey={analysisActivationKey}
            closedAnalysisJobId={closedAnalysisJobId}
            onClose={handleWorkspaceClose}
          >
          {hasVisitedResults && <MBResultsMap
            isVisible={rightTab === "results"}
            simulationId={simulationIdSafe}
            isLoaded={isLoaded}
            loadError={loadError}
            selectedLinkIds={resultSelectedLinkIds}
            selectedNodeIds={resultSelectedNodeIds}
            onToggleLink={toggleResultLink}
            onToggleNode={toggleResultNode}
            onClearSelections={clearResultSelections}
            onContextChange={setResultMapContext}
          />}
          <MBMapComponent
            isVisible={rightTab !== "results"}
            isLoaded={isLoaded}
            loadError={loadError}
            links={mapLinks}
            overlayLinks={
              isDestinationConfigVisible ? destinationVisibilityLinks : []
            }
            nodes={mapNodes}
            overlayNodes={mapOverlayNodes}
            nodeLinkAssociation={isDestinationConfigVisible}
            overlayPolygons={mapOverlayPolygons}
            hazardPolygons={rightTab === "hazards" || isAgentHazardVisible ? activeHazardRows : []}
            selectedHazardFeatureIndex={selectedHazardFeatureIndex}
            parcels={parcels}
            polygons={polygonRows}
            activeLayer={mapLayer}
            activeLayerId={mapLayerId}
            defaultCenter={mapCenter}
            defaultZoom={mapZoom}
            onViewportChange={onMapViewportChange}
            searchValue={mapSearch}
            onChangeSearchValue={setMapSearch}
            onSubmitSearch={onSubmitMapSearch}
            isSelectingPolygon={isSelectingPolygon}
            draftPolygon={draftPolygon}
            savedPolygon={savedPolygon}
            onStartPolygon={onStartPolygon}
            onCancelPolygon={onCancelPolygon}
            onAddPolygonPoint={onAddPolygonPoint}
            onUndoPolygonPoint={onUndoPolygonPoint}
            onSavePolygon={onSavePolygon}
            selectedLinkIds={mapSelectedLinkIds}
            selectedNodeIds={mapSelectedNodeIds}
            selectedParcelIds={selectedParcelIds}
            selectedPolygonIds={selectedPolygonIds}
            selectedPolygonColor={selectedPolygonColor}
            onToggleLink={isBlockedRoadConfigVisible ? toggleBlockedRoadLink : isContraflowConfigVisible ? toggleContraflowLink : toggleLink}
            onToggleNode={mapNodeToggle}
            onToggleParcel={toggleParcel}
            onTogglePolygon={togglePolygon}
            onSelectHazardFeature={selectHazardFeature}
            focusTarget={mapFocusTarget}
            fitSelection={mapFitSelection}
            isDrawingDraftLink={rightTab === "edit" && addLinkDraftMode === "drawing"}
            draftLinkPath={addLinkDraftPath}
            onAddDraftLinkPoint={onAddLinkDraftPoint}
            draftLinkFocus={mapDraftLinkFocus}
            draftLinkCandidateArrows={mapDraftLinkCandidateArrows}
            contraflowMode={isContraflowConfigVisible}
            blockedRoadMode={isBlockedRoadConfigVisible}
            analysisOverlay={analysisMapOverlay}
          />
          </AgentResultsWorkspace>
        </div>

        <div
          className="mb-paneDivider"
          role="separator"
          aria-label="Resize working area and map"
          aria-orientation="vertical"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(workingPanePercent)}
          tabIndex={0}
          onPointerDown={startDividerDrag}
          onPointerMove={moveDivider}
          onPointerUp={stopDividerDrag}
          onPointerCancel={stopDividerDrag}
          onDoubleClick={() => setWorkingPanePercent(50)}
          onKeyDown={(event) => {
            if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
            event.preventDefault();
            const direction = event.key === "ArrowRight" ? 1 : -1;
            setWorkingPanePercent((current) => Math.max(30, Math.min(70, current + direction * 2)));
          }}
          title="Drag to resize; double-click to reset"
        ><span /></div>
  
        {/* Working column (displayed on the left via CSS) */}
        <div className="mb-right">
            {rightTab === "chat" && showBackToChat && <ScrollJumpButton className="mb-backToChat" direction="up" destination="chat" onClick={scrollBackToChat} />}
            <div className="mb-rightScroll" ref={workingScrollRef} onScroll={(event) => setShowBackToChat(event.currentTarget.scrollTop > 120)}>
                {rightTab === "chat" || rightTab === "results" ? (
                  <>
                    <WuiAgentChat
                      simulationId={simulationIdSafe}
                      userId={userId}
                      onSessionChange={handleAgentSessionChange}
                      presentations={agentPresentations}
                      onActivatePresentation={activatePresentationFromChat}
                      onFocusPresentationMessage={setFocusedPresentationMessageId}
                      onAnalysisJobsChange={setAnalysisJobSnapshot}
                      onLatestQuestionChange={setLatestAgentQuestion}
                      onOpenAnalysis={openAnalysisFromChat}
                    />
                    <div id="agent-session-workspace-root" className="mb-agentWorkspaceRoot" />
                    {builderPermissions.fetchData && <div className="mb-agentDataToolbar">
                      <button className="mb-btn mb-btnSecondary" type="button" onClick={runRoadNetworkFromPolygon} disabled={isRoadNetworkRunning}>
                        {isRoadNetworkRunning ? "Generating OSM network..." : "Generate OSM network"}
                      </button>
                      <button className="mb-btn mb-btnSecondary" type="button" onClick={runParcelDataFromPolygon} disabled={isParcelDataRunning}>
                        {isParcelDataRunning ? "Getting parcel data..." : "Get parcel data"}
                      </button>
                    </div>}
                    {/* Blocks should consume remaining space and scroll */}
                    {builderPermissions.fetchData && <div className="mb-blocks">
                        {visibleOperationBlocks.map((b) => (
                        <OperationBlock
                            key={b.blockId}
                            database={database}
                            blockId={b.blockId}
                            title={b.kind === "osm" ? "OSM network builder" : b.kind === "parcel" ? "Cloud Run: Parcel builder" : "Critical facilities import"}
                        />
                        ))}
                    </div>}
                  </>
                ) : rightTab === "edit" ? (
                  <MBLayerSelectionTab
                    activeLayer={activeLayer}
                    activeLayerId={activeLayerId}
                    activeLayerName={selectedLayer?.name || activeLayer}
                    activeLayerFileName={selectedLayer?.filename || ""}
                    gisLayers={gisLayers}
                    onChangeLayerSelection={onManualLayerSelection}
                    onDuplicateLayer={duplicateSelectedLayer}
                    onDeleteLayer={deleteSelectedLayer}
                    canDeleteLayer={canDeleteSelectedLayer}
                    onStartFilterLayer={startFilterLayer}
                    onCancelFilterLayer={cancelFilterLayer}
                    onSaveFilterLayer={saveFilterLayer}
                    onSaveLayerMeta={saveSelectedLayerMeta}
                    filterPolygonOptions={polygonLayerOptions}
                    filterSelectedPolygonLayerIds={selectedFilterPolygonLayerIds}
                    onToggleFilterPolygonLayer={toggleFilterPolygonLayer}
                    isFilteringLayer={isFilteringLayer}
                    links={links}
                    nodes={nodes}
                    parcels={parcels}
                    otherRows={otherRows}
                    polygonRows={polygonRows}
                    selectedLinkIds={selectedLinkIds}
                    selectedNodeIds={selectedNodeIds}
                    selectedParcelIds={selectedParcelIds}
                    selectedOtherIds={selectedOtherIds}
                    selectedPolygonIds={selectedPolygonIds}
                    selectedPolygonColor={selectedPolygonColor}
                    onChangeSelectedPolygonColor={setSelectedPolygonColor}
                    onToggleLink={toggleLink}
                    onToggleNode={toggleNode}
                    onToggleParcel={toggleParcel}
                    onToggleOther={toggleOther}
                    onTogglePolygon={togglePolygon}
                    onClearSelections={clearSelections}
                    onSaveLayerEdits={saveLayerEdits}
                    onDeleteLayerRow={deleteLayerRow}
                    onDeleteSelectedRows={deleteSelectedLayerRows}
                    onCreateLayerFromUpload={createLayerFromUpload}
                    onCreateLayerFromSavedPolygon={createLayerFromSavedPolygon}
                    hasSavedMapPolygon={!!(savedPolygon && savedPolygon.length >= 3)}
                    onDownloadLayerData={downloadSelectedLayerData}
                    onDownloadSelectedPolygons={downloadSelectedPolygonFeatures}
                    onSelectOppositeLink={selectOppositeLink}
                    onCenterSelection={centerSelectionOnMap}
                    isSaving={layerSaveState.isSaving}
                    saveError={layerSaveState.error}
                    saveSuccess={layerSaveState.success}
                    addLinkDraftMode={addLinkDraftMode}
                    addLinkDraftPath={addLinkDraftPath}
                    addLinkDraftInfo={addLinkDraftInfo}
                    draftTopologyStep={draftTopologyStep}
                    draftTopologyChoices={draftTopologyChoices}
                    draftNearestLinkOptions={draftNearestLinkOptions}
                    draftNearestLinkSelected={draftNearestLinkSelected}
                    addLinkNodeLayerOptions={nodeLayerOptions}
                    addLinkSelectedNodeLayerId={addLinkSelectedNodeLayerId}
                    addLinkNodeLayerLoading={addLinkNodeLayerLoading}
                    onStartAddLinkDraft={startAddLinkDraft}
                    onUndoAddLinkDraftPoint={undoAddLinkDraftPoint}
                    onCancelAddLinkDraft={cancelAddLinkDraft}
                    onFinishAddLinkDraft={finishAddLinkDraft}
                    onBackAddLinkNodeLayer={backAddLinkNodeLayer}
                    onAcceptAddLinkNodeLayer={acceptAddLinkNodeLayer}
                    onChangeAddLinkNodeLayer={setAddLinkSelectedNodeLayerId}
                    onSetDraftTopologyChoice={setDraftTopologyChoice}
                    onToggleDraftNearestLinkSelection={toggleDraftNearestLinkSelection}
                    onAcceptDraftTopologyStep={acceptDraftTopologyStep}
                    onBackDraftTopologyStep={backDraftTopologyStep}
                    onSaveAddLinkDraft={saveAddLinkDraft}
                    onDiscardAddLinkDraft={discardAddLinkDraft}
                    criticalFacilitiesPanel={builderPermissions.fetchData && activeLayerId ? (
                      <MBCriticalFacilitiesPanel
                        key={activeLayerId}
                        database={database}
                        simulationId={simulationIdSafe}
                        layerId={activeLayerId}
                        layerName={selectedLayer?.name || activeLayer}
                        onCompleted={refreshParcels}
                      />
                    ) : null}
                  />
                ) : rightTab === "config" ? (
                  <MBConfigTab
                    configVariables={availableConfigVars.map((row) => ({
                      name: row.name,
                      type: row.type,
                      nodeLayerId: row.nodeLayerId || "",
                      visibilityLinksLayerId: row.visibilityLinksLayerId || "",
                      destinations: toNumericDestinationIds(row.destinationIds),
                      minDepartureTime: row.minDepartureTime ?? "",
                      maxDepartureTime: row.maxDepartureTime ?? "",
                      granularSurvey: Boolean(row.granularSurvey),
                      startTimeRanges: Array.isArray(row.startTimeRanges) ? row.startTimeRanges : [],
                      hazardEntries: Array.isArray(row.hazardEntries) ? row.hazardEntries : [],
                      linksLayerId: row.linksLayerId || "",
                      destinationConfigName: row.destinationConfigName || "",
                      blockedRoadEntries: normalizeBlockedRoadEntries(row.blockedRoadEntries),
                      contraflowEntries: normalizeContraflowEntries(row.contraflowEntries || row.linkIds).map((entry) => {
                        const main = contraflowLinks.find((link) => String(link?.link_id) === entry.linkId);
                        const reverse = contraflowLinks.find((link) => String(link?.link_id) === entry.reverseLinkId);
                        return {
                          ...entry,
                          startNodeId: getLinkStartNodeId(main),
                          endNodeId: getLinkEndNodeId(main),
                          originalMainLanes: Number.isInteger(Number(main?.lanes)) ? Number(main.lanes) : null,
                          originalReverseLanes: Number.isInteger(Number(reverse?.lanes)) ? Number(reverse.lanes) : null,
                        };
                      }),
                    }))}
                    activeVariableName={effectiveConfigContext.name}
                    activeNodeLayerId={effectiveConfigContext.nodeLayerId}
                    activeVisibilityLinksLayerId={effectiveConfigContext.visibilityLinksLayerId}
                    nodeLayerOptions={nodeLayerOptions.map((row) => ({
                      id: String(row.id),
                      name: String(row.name || row.id),
                      filename: String(row.filename || ""),
                    }))}
                    linksLayerOptions={linksLayerOptions.map((row) => ({
                      id: String(row.id),
                      name: String(row.name || row.id),
                      filename: String(row.filename || ""),
                    }))}
                    hazardLayerOptions={hazardLayers.map((row) => ({
                      id: String(row.id),
                      name: String(row.name || row.id),
                    }))}
                    isEditingDestinations={isEditingDestinations}
                    isEditingStartTimes={isEditingStartTimes}
                    isEditingHazardConfig={isEditingHazardConfig}
                    allowHazardConfig={builderPermissions.hazardLayer}
                    isEditingContraflow={isEditingContraflow}
                    isEditingBlockedRoad={isEditingBlockedRoad}
                    blockedRoadTwoSided={blockedRoadTwoSided}
                    hazardConfigName={hazardConfigNameDraft}
                    isVariableSelectionLocked={isConfigEditActive}
                    isSaving={configSaveState.isSaving}
                    saveError={configSaveState.error}
                    saveSuccess={configSaveState.success}
                    onSelectVariable={selectConfigVariable}
                    onCreateVariable={createConfigVariable}
                    onDeleteVariable={deleteConfigVariable}
                    onChangeNodeLayer={setActiveDestinationNodeLayer}
                    onChangeVisibilityLinksLayer={setActiveDestinationVisibilityLinksLayer}
                    onStartEditDestinations={startEditDestinations}
                    onStartEditStartTimes={startEditStartTimes}
                    onStartEditHazardConfig={startEditHazardConfig}
                    onStartEditContraflow={startEditContraflow}
                    onStartEditBlockedRoad={startEditBlockedRoad}
                    onChangeBlockedRoadLinksLayer={setActiveBlockedRoadLinksLayer}
                    onChangeBlockedRoadTwoSided={setBlockedRoadTwoSided}
                    onClearBlockedRoad={clearBlockedRoad}
                    onRemoveBlockedRoadEntry={removeBlockedRoadEntry}
                    onAddBlockedRoadLinkIds={(ids) => addConfigLinkIdsByInput(ids, "blocked_road")}
                    onFocusBlockedRoadLink={(linkId) => centerSelectionOnMap("links", linkId)}
                    onChangeContraflowLinksLayer={setActiveContraflowLinksLayer}
                    onChangeContraflowDestinationConfig={setActiveContraflowDestinationConfig}
                    onSwitchContraflowLink={switchContraflowLink}
                    onClearContraflow={clearContraflow}
                    onRemoveContraflowEntry={removeContraflowEntry}
                    onAddContraflowLinkIds={(ids) => addConfigLinkIdsByInput(ids, "contraflow")}
                    onChangeContraflowLaneAllocation={setContraflowLaneAllocation}
                    onFocusContraflowLink={(linkId) => centerSelectionOnMap("links", linkId)}
                    onChangeHazardConfigName={(name) => {
                      setHazardConfigNameDraft(name);
                      setConfigSaveState((prev) => ({ ...prev, error: "", success: "" }));
                    }}
                    onCancelEdit={cancelConfigEdit}
                    onClearDestinations={clearDestinations}
                    onRemoveDestination={removeDestination}
                    onFocusDestinationNode={(nodeId) => centerSelectionOnMap("nodes", nodeId)}
                    onAddDestinationIds={addDestinationIdsByInput}
                    onChangeDepartureMin={setActiveDepartureMin}
                    onChangeDepartureMax={setActiveDepartureMax}
                    onChangeGranularSurvey={setActiveGranularSurvey}
                    onAddStartTimeRange={addActiveStartTimeRange}
                    onUpdateStartTimeRange={updateActiveStartTimeRange}
                    onRemoveStartTimeRange={removeActiveStartTimeRange}
                    onChangeHazardLayer={setActiveHazardConfigLayer}
                    onChangeHazardRerouting={setActiveHazardRerouting}
                    onAddHazardEntry={addActiveHazardEntry}
                    onRemoveHazardEntry={removeActiveHazardEntry}
                    onSaveConfig={saveConfig}
                  />
                ) : rightTab === "hazards" ? (
                  <MBHazardsTab
                    hazardLayers={hazardLayers}
                    activeHazardLayerId={activeHazardLayerId}
                    snapshots={hazardSnapshots}
                    activeSnapshotId={activeHazardSnapshotId}
                    selectedSnapshotRows={activeHazardRows}
                    selectedGeoJson={activeHazardGeoJson}
                    selectedFeatureIndex={selectedHazardFeatureIndex}
                    isBusy={hazardState.isBusy}
                    error={hazardState.error}
                    success={hazardState.success}
                    onSelectHazardLayer={(layerId) => {
                      setActiveHazardLayerId(String(layerId || ""));
                      setHazardState((prev) => ({ ...prev, error: "", success: "" }));
                    }}
                    onCreateHazardLayer={createHazardLayer}
                    onDeleteHazardLayer={deleteHazardLayer}
                    onUploadSnapshots={uploadHazardSnapshots}
                    onSelectSnapshot={(snapshotId) => {
                      setActiveHazardSnapshotId(String(snapshotId || ""));
                      setHazardState((prev) => ({ ...prev, error: "", success: "" }));
                    }}
                    onDeleteSnapshot={deleteHazardSnapshot}
                    onSelectFeature={selectHazardFeature}
                  />
                ) : rightTab === "knowledge" ? (
                  <MBKnowledgeTab simulationId={simulationIdSafe} />
                ) : rightTab === "podcasts" ? (
                  <MBPodcastsTab builderId={simulationIdSafe} runs={podcastRuns} />
                ) : rightTab === "settings" ? (
                  <MBSettingsTab permissions={builderPermissions} onChange={changeBuilderPermission} savingKey={settingsSavingKey} error={settingsError} />
                ) : (
                  <MBRunTab
                    simulationId={SIMULATION_ID}
                    database={database}
                    showHazards={builderPermissions.hazardLayer}
                  />
                )}
            </div>
        </div>
      </div>
    </div>
  );
  
}
